import { PROGRAM_GRID_MEDIA_KIT_PROPOSALS } from './programGridMediaKitProposals';

/**
 * Propuestas estáticas: siempre disponibles en el Media Kit aunque el API no responda.
 * Las que existan también en el API se pueden mostrar desde el API (prioridad API si está disponible).
 * Formato igual al del API: type, slug, name, metaTitle, metaDescription, content, isActive, order.
 */
const STATIC_PROPOSALS_RAW = [
  {
    type: 'client',
    slug: 'gpm-eventos',
    name: 'GPM Eventos',
    metaTitle: 'GPM Eventos | Media Kit · TNO Network',
    metaDescription:
      'Media kit de GPM Eventos en TNO Network: micros en rotativa diaria, lower thirds en programas clave, entrevista mensual en estudio, artículos SEO en TNO Digest y logo fijo 24/7 en el portal.',
    content: {
      hero: {
        overline: 'Media Kit · Propuesta de alianza',
        title: 'GPM Eventos en TNO Network',
        subtitle:
          'Presencia de marca en streaming, pantalla en vivo, estudio, editorial y web — un ecosistema 360 para posicionar eventos, experiencias y aliados comerciales.',
        analysisTitle: 'Resumen ejecutivo',
        analysisText:
          'GPM Eventos puede convertirse en una marca visible dentro del ecosistema TNO Network: rotación diaria de micros en la grilla regular, gráfica en vivo en programas clave, un bloque mensual de entrevista en estudio principal, artículos optimizados en TNO Digest y presencia fija en el portal web. La combinación cubre recordación (rotativa), credibilidad en antena (lower thirds y entrevista), autoridad digital (SEO editorial) y visibilidad permanente (espacio de aliados 24/7).',
      },
      tnoEcosystem: {
        title: 'El ecosistema TNO',
        subtitle: 'Donde vive la propuesta de GPM Eventos',
        description:
          'TNO Network no es un solo canal: es un ecosistema de comunicación que une la inmediatez del streaming con la permanencia del On-Demand y la profundidad editorial de TNO Digest. Para GPM Eventos, eso significa que cada acción de marca puede vivir en vivo, quedar archivada y ampliarse en web — maximizando el retorno de cada pieza producida.',
      },
      productsExplained: {
        overline: 'Inventario mensual',
        title: 'Servicios, formato/ubicación y frecuencia',
        description:
          'A continuación, el detalle de cada formato disponible: qué es, dónde se ubica dentro del ecosistema TNO y con qué frecuencia se ejecuta. Los servicios pueden activarse de forma individual o combinarse en un plan integral según calendario de eventos, lanzamientos o campañas de temporada.',
        itemsLayout: 'fullRow',
        items: [
          {
            id: 'micros-rotativa',
            icon: 'broadcast',
            badge: 'Alta rotación diaria',
            title: 'Micros en Rotativa',
            summary:
              'Piezas de video corto integradas en la grilla regular de TNO Network. Son el formato ideal para mantener la marca en el radar del público: mensajes ágiles, recordación constante y presencia en la señal que acompaña al espectador durante horas de programación.',
            topics: [
              'Formato / ubicación: video en la grilla regular de TNO (señal multiplataforma de Radio Visual).',
              'Frecuencia mensual: alta rotación diaria — la pieza circula de forma recurrente a lo largo de la programación.',
              'Ideal para: promoción de fechas, preventa, recordatorio de eventos próximos o mensajes de marca de corta duración.',
              'Ventaja: top of mind sostenido sin depender de un solo programa o un solo horario de emisión.',
              'Las piezas pueden reutilizarse en campañas cruzadas con otros formatos del plan (web, Digest, entrevista).',
            ],
          },
          {
            id: 'banners-pantalla',
            icon: 'web',
            badge: 'Programas clave',
            title: 'Banners en Pantalla',
            summary:
              'Gráfica en vivo tipo Lower Thirds: la marca aparece en pantalla durante la transmisión de programas seleccionados del ecosistema TNO. Es presencia visual directa, en el momento de mayor atención del espectador, sin interrumpir el flujo del programa.',
            topics: [
              'Formato / ubicación: gráfica en vivo (Lower Thirds) superpuesta en la señal de streaming.',
              'Frecuencia mensual: presencia en programas clave — los espacios de mayor audiencia y afinidad temática.',
              'Ideal para: reforzar patrocinio, anunciar eventos en curso o vincular la marca con contenidos de autoridad.',
              'Ventaja: visibilidad premium en contexto editorial; la marca se asocia con el programa y su audiencia.',
              'Los cintillos pueden adaptarse por campaña: logo, claim, fecha del evento o call to action.',
            ],
          },
          {
            id: 'entrevista',
            icon: 'mic',
            badge: '1 al mes',
            title: 'Entrevista',
            summary:
              'Bloque en vivo desde el estudio principal de TNO Network. Es el formato de mayor profundidad narrativa: permite contar la historia de GPM Eventos, presentar producciones, voceros o aliados, y generar contenido de valor que trasciende la pauta tradicional.',
            topics: [
              'Formato / ubicación: bloque en vivo en estudio principal, con producción multicámara y difusión en señal TNO.',
              'Frecuencia mensual: 1 entrevista al mes — espacio dedicado para desarrollar un mensaje completo.',
              'Ideal para: lanzamientos, behind the scenes de eventos, presentación de nuevas líneas de negocio o casos de éxito.',
              'Ventaja: credibilidad y cercanía; el público conoce a las personas detrás de la marca.',
              'El contenido puede recortarse en clips para redes y ampliarse en artículo TNO Digest.',
            ],
          },
          {
            id: 'tno-digest',
            icon: 'seo',
            badge: 'Publicaciones pautadas',
            title: 'TNO Digest',
            summary:
              'Artículos web optimizados para buscadores, publicados en TNO Digest (la plataforma editorial de tnonetwork.com). A diferencia de la rotativa — que impacta en el momento — el Digest construye presencia digital de largo plazo: cada pieza sigue generando alcance orgánico semanas y meses después de publicarse.',
            topics: [
              'Formato / ubicación: artículos web optimizados (SEO) en TNO Digest, dentro del portal tnonetwork.com.',
              'Frecuencia mensual: publicaciones pautadas según calendario editorial acordado.',
              'Ideal para: notas de prensa ampliadas, crónicas de eventos, entrevistas escritas, guías y contenido evergreen.',
              'Ventaja: autoridad digital y posicionamiento en Google para búsquedas relacionadas con eventos y entretenimiento.',
              'Cada artículo puede incluir imágenes, enlaces y CTAs hacia landing de GPM Eventos o venta de entradas.',
            ],
          },
          {
            id: 'logo-web',
            icon: 'store',
            badge: 'Fijo 24/7',
            title: 'Logo en Web',
            summary:
              'Espacio permanente en el portal tnonetwork.com dentro del bloque de aliados comerciales. Es la base de la presencia digital: la marca está visible las 24 horas del día, los 7 días de la semana, para quien navega el ecosistema TNO desde cualquier dispositivo.',
            topics: [
              'Formato / ubicación: banner / espacio de aliados en el portal web de TNO Network.',
              'Frecuencia mensual: fijo 24/7 — presencia continua sin depender de emisiones en vivo.',
              'Ideal para: recordación de marca, enlace directo al sitio de GPM Eventos y refuerzo de credibilidad por asociación con TNO.',
              'Ventaja: complementa la rotativa y el Digest; quien llega por contenido editorial también encuentra la marca.',
              'El espacio puede actualizarse por campaña: logo, arte de evento activo o mensaje promocional.',
            ],
          },
        ],
      },
      metricsSection: {
        overline: 'Cobertura',
        title: 'Un plan que cubre todo el embudo',
        description:
          'La propuesta de GPM Eventos no apuesta a un solo punto de contacto: combina recordación diaria (rotativa), impacto en antena (lower thirds), profundidad narrativa (entrevista), autoridad digital (Digest) y presencia permanente (web). Cada formato cumple una función distinta dentro del recorrido del público.',
        footerLine:
          'Streaming + estudio + editorial + web = visibilidad sostenida para marcas de eventos y experiencias.',
      },
      metrics: [
        {
          value: '5',
          label: 'Formatos integrados',
          subtitle: 'Rotativa, lower thirds, entrevista, Digest y web — un inventario completo.',
        },
        {
          value: '24',
          label: 'Horas de presencia web',
          subtitle: 'Logo y espacio de aliados visible todo el día en tnonetwork.com.',
        },
        {
          value: '1',
          label: 'Entrevista mensual en estudio',
          subtitle: 'Bloque en vivo con producción profesional desde el estudio principal TNO.',
        },
        {
          value: '360',
          label: 'Grados de cobertura',
          subtitle: 'Vivo, On-Demand, editorial SEO y portal — el ecosistema completo TNO.',
        },
      ],
      valueAdd: {
        layout: 'features',
        overline: 'Por qué TNO',
        title: 'Valor diferencial para GPM Eventos',
        items: [
          {
            title: 'Recordación sin depender de un solo horario',
            description:
              'La rotativa diaria mantiene la marca presente a lo largo de la grilla, mientras los lower thirds refuerzan el mensaje en los programas de mayor impacto. No se trata de un spot aislado: es presencia sostenida.',
            icon: 'chart',
          },
          {
            title: 'Narrativa con profundidad',
            description:
              'La entrevista mensual en estudio permite contar historias completas: el equipo, la producción, el evento. Eso genera confianza y diferencia a GPM Eventos frente a la publicidad genérica.',
            icon: 'handshake',
          },
          {
            title: 'Activo digital permanente',
            description:
              'TNO Digest y el espacio web 24/7 construyen una huella digital que sigue trabajando después del evento: artículos indexables, enlaces directos y visibilidad constante en el portal.',
            icon: 'network',
          },
          {
            title: 'Flexibilidad por campaña',
            description:
              'Cada formato se adapta al calendario de GPM Eventos: preventa, día del evento, post-evento o posicionamiento de marca. El inventario se planifica mes a mes según prioridades comerciales.',
            icon: 'shield',
          },
        ],
      },
      areYouReady: {
        title: '¿Listos para activar el plan?',
        description:
          'Escríbenos y armamos una propuesta personalizada: combinamos los formatos del inventario según tus objetivos, fechas de eventos y presupuesto. Podemos activar el plan completo o iniciar por los formatos de mayor impacto inmediato.',
        ctaText: 'Hablar por WhatsApp',
        footerText: '© TNO Network 2026 | GPM Eventos — Media Kit.',
        whatsappNumber: '584241234567',
      },
    },
    isActive: true,
    order: 0.9,
  },
  {
    type: 'program',
    slug: 'expedientesdeldelito',
    name: 'Expedientes del Delito',
    metaTitle: 'Expedientes del Delito | Anuncia con nosotros',
    metaDescription:
      'Expedientes del Delito: análisis sobre criminalidad, justicia y seguridad en Venezuela. Con Santiago Gutiérrez y Angélica Lugo. Patrocinios y opciones de comercialización para marcas que buscan credibilidad y autoridad periodística.',
    content: {
      who: {
        title: 'Expedientes del Delito: La narrativa de quienes conocen el terreno',
        description:
          'El análisis definitivo sobre criminalidad, justicia y seguridad en Venezuela. Investigación profunda y experiencia de campo con máxima credibilidad periodística.',
        sectionTitle: '¿Qué es Expedientes del Delito?',
        sectionBody:
          'Un espacio donde Santiago Gutiérrez y Angélica Lugo diseccionan los casos que marcan la pauta nacional en TNO Network.',
        anchorName: 'Santiago Gutiérrez y Angélica Lugo',
        anchorSubtitle: 'Conductores de Expedientes del Delito.',
      },
      videoUrl: 'https://www.youtube.com/embed/3cL0ijWChrE',
      benefitsSection: {},
      benefits: [],
      metrics: [],
      values: {},
      pricingOptions: [],
      areYouReady: {
        title: '¿Listo para patrocinar?',
        description:
          'Conecta tu marca con un entorno de máxima credibilidad, seriedad y prestigio periodístico.',
        ctaText: 'Patrocinar Expedientes del Delito',
        footerText: '© TNO Network 2026 | Expedientes del Delito.',
      },
    },
    isActive: true,
    order: 0.82,
    ogImage: '/images/LOGOEXPEDIENTES.png',
  },
  {
    type: 'program',
    slug: 'noticias',
    name: 'Noticias TNO',
    metaTitle: 'Noticias TNO | Anuncia con nosotros',
    metaDescription:
      'Noticias TNO: información en vivo con la autoridad de Sol Vargas. Patrocinios, rotativas y opciones de comercialización para marcas que buscan prestigio periodístico.',
    content: {
      who: {
        title: 'Noticias TNO: periodismo robusto en el ecosistema digital',
        description:
          'Resumen informativo semanal en vivo con rigor, objetividad y equilibrio. La voz de Sol Vargas en el ecosistema global de TNO Network.',
        sectionTitle: '¿Qué es Noticias TNO?',
        sectionBody:
          'Propuesta informativa de vanguardia que combina streaming, redes y autoridad editorial para una audiencia que exige inmediatez y veracidad.',
        anchorName: 'Sol Vargas',
        anchorSubtitle: 'Conductora de Noticias TNO.',
      },
      videoUrl: 'https://www.youtube.com/embed/qUt5z7CdIMI',
      benefitsSection: {},
      benefits: [],
      metrics: [],
      values: {},
      pricingOptions: [],
      areYouReady: {
        title: '¿Listo para patrocinar?',
        description:
          'Asocia tu marca con el prestigio periodístico de Noticias TNO y el ecosistema multiplataforma de TNO Network.',
        ctaText: 'Patrocinar Noticias TNO',
        footerText: '© TNO Network 2026 | Noticias TNO.',
      },
    },
    isActive: true,
    order: 0.84,
    ogImage: '/images/logos/logo_tnonetwork_black.png',
  },
  {
    type: 'program',
    slug: 'tnobusiness',
    name: 'TNO Business',
    metaTitle: 'TNO Business | Media Kit',
    metaDescription:
      'TNO Business: autoridad corporativa, entrevistas a líderes y patrocinio B2B en TNO Network. Streaming, On-Demand y TNO Digest para marcas que buscan credibilidad empresarial.',
    content: {
      who: {
        title: 'TNO Business: la vitrina corporativa de TNO Network',
        description:
          'Plataforma de referencia para directivos y emprendedores: coyuntura, innovación y casos de éxito con producción TNO Media Lab.',
        sectionTitle: '¿Qué es TNO Business?',
        sectionLinkUrl: 'https://ads.tnonetwork.com/patrocinio-tno-business',
        sectionLinkLabel: 'ads.tnonetwork.com/patrocinio-tno-business',
        sectionBody:
          'Programa de entrevistas y análisis empresarial en vivo y On-Demand. Media kit de patrocinio y opciones comerciales en el enlace anterior.',
        anchorName: 'TNO Business',
        anchorSubtitle: 'Programa corporativo · TNO Network.',
      },
      videoUrl: '',
      benefitsSection: {},
      benefits: [],
      metrics: [],
      values: {},
      pricingOptions: [],
      areYouReady: {
        title: '¿Listo para patrocinar TNO Business?',
        description:
          'Consulta la propuesta genérica de patrocinio: paquetes, métricas y opciones de comercialización B2B en TNO Network.',
        ctaText: 'Ver media kit de patrocinio',
        footerText: '© TNO Network 2026 | TNO Business · Guest experience en /tnobusiness.',
      },
    },
    isActive: true,
    order: 0.835,
    ogImage: 'https://files.tnonetwork.com/shows/logo/tnobusiness.png',
  },
  {
    type: 'program',
    slug: 'ladosispositiva',
    name: 'La Dosis Positiva',
    metaTitle: 'La Dosis Positiva | Anuncia con nosotros',
    metaDescription: 'Anuncia con nosotros en La Dosis Positiva. Bienestar, productividad y estilo de vida.',
    content: {
      who: {
        title: 'La Dosis Positiva: Inspiración diaria para una vida en equilibrio',
        description: 'El espacio dedicado a optimizar el bienestar físico, la claridad mental y la calidad de nuestras relaciones.',
        sectionTitle: '¿Qué es La Dosis Positiva?',
        sectionBody: 'Es un ecosistema de contenido diseñado para quienes buscan crecer personal y profesionalmente. Transmisión en vivo los jueves a las 2:00 PM por TNO Network.',
        anchorName: 'Yanira Bocaranda',
        anchorSubtitle: 'Ancla de La Dosis Positiva.',
        anchorBio: 'Yanira Bocaranda lidera La Dosis Positiva, un espacio que combina bienestar físico, claridad mental y relaciones de calidad. Su estilo cercano y profesional convierte cada programa en una experiencia inspiradora.',
      },
      videoUrl: 'https://www.youtube.com/embed/K6NfYmbfGhk',
      benefitsSection: {},
      benefits: [],
      metrics: [],
      values: {},
      pricingOptions: [],
      areYouReady: {
        title: '¿Listo para patrocinar?',
        description: 'Forma parte de La Dosis Positiva y conecta con una comunidad que valora la practicidad, el crecimiento y el bienestar real.',
        ctaText: 'Patrocinar La Dosis Positiva',
        footerText: '© TNO Network 2026 | La Dosis Positiva: Contenido que Transforma.',
      },
    },
    isActive: true,
    order: 1,
    ogImage: 'https://files.tnonetwork.com/shows/logo/ladosispositiva.png',
  },
  {
    type: 'program',
    slug: 'puntodefe',
    name: 'Punto de Fe',
    metaTitle: 'Punto de Fe | Anuncia con nosotros',
    metaDescription:
      'Anuncia con nosotros en Punto de Fe con Margarita Toledo. Fe, valores familiares y bienestar integral en TNO Network.',
    content: {
      who: {
        title: 'Punto de Fe: fe, valores y propósito en tu día a día',
        description:
          'El programa ancla de TNO Network donde la espiritualidad, la paz mental y los valores familiares se conversan con naturalidad.',
        sectionTitle: '¿Qué es Punto de Fe?',
        sectionBody:
          'Conducción de Margarita Toledo: entrevistas, reflexión y comunidad en vivo y On-Demand por TNO Network.',
        anchorName: 'Margarita Toledo',
        anchorSubtitle: 'Ancla de Punto de Fe.',
        anchorBio:
          'Periodista con más de 25 años de trayectoria que conecta con su audiencia desde la fe, la credibilidad y el propósito.',
      },
      videoUrl: 'https://www.youtube-nocookie.com/embed/0y1kYrSkGBA',
      benefitsSection: {},
      benefits: [],
      metrics: [],
      values: {},
      pricingOptions: [],
      areYouReady: {
        title: '¿Listo para patrocinar?',
        description:
          'Forma parte de Punto de Fe y conecta con una comunidad que valora la esperanza, los valores y el propósito.',
        ctaText: 'Patrocinar Punto de Fe',
        footerText: '© TNO Network 2026 | Punto de Fe: Fe que inspira.',
      },
    },
    isActive: true,
    order: 1.02,
    ogImage: 'https://files.tnonetwork.com/shows/logo/puntodefe.png',
  },
  {
    type: 'program',
    slug: 'somospadres',
    name: 'Somos Padres',
    metaTitle: 'Somos Padres | Anuncia con nosotros',
    metaDescription:
      'Anuncia en Somos Padres con Yelitza Yánez. Crianza, salud y bienestar familiar en TNO Network y SomosPadres.info.',
    content: {
      who: {
        title: 'Somos Padres: crianza, salud y bienestar para toda la familia',
        description:
          'Magazine de TNO Network para padres y madres que buscan información útil, entrevistas con expertos y estrategias reales para criar con confianza.',
        sectionTitle: '¿Qué es Somos Padres?',
        sectionBody:
          'Programa en formato entrevista los martes a la 1:00 PM (Venezuela). Revista digital en somospadres.info.',
        anchorName: 'Yelitza Yánez',
        anchorSubtitle: 'Ancla de Somos Padres.',
        anchorBio:
          'Periodista, creadora de SomosPadres.info y especialista en branded content con 28 años de experiencia.',
      },
      videoUrl: 'https://www.youtube.com/embed/uh9e6I4Hb0o?start=1',
      benefitsSection: {},
      benefits: [],
      metrics: [],
      values: {},
      pricingOptions: [],
      areYouReady: {
        title: '¿Listo para patrocinar?',
        description:
          'Forma parte de Somos Padres y conecta con padres y madres que investigan en salud, educación y bienestar de sus hijos.',
        ctaText: 'Patrocinar Somos Padres',
        footerText: '© TNO Network 2026 | Somos Padres: Crianza con información de verdad.',
      },
    },
    isActive: true,
    order: 1.04,
    ogImage: 'https://files.tnonetwork.com/shows/logo/somospadres.png',
  },
  {
    type: 'program',
    slug: 'vivirenequilibrio',
    name: 'Vivir en Equilibrio',
    metaTitle: 'Vivir en Equilibrio | Anuncia con nosotros',
    metaDescription:
      'Anuncia en Vivir en Equilibrio con Liliana Fernandes. Bienestar integral, fitness y equilibrio mente-cuerpo en TNO Network.',
    content: {
      who: {
        title: 'Vivir en Equilibrio: bienestar integral para mente, cuerpo y alma',
        description:
          'Herramientas prácticas para una vida plena: equilibrio físico, foco mental y hábitos saludables.',
        sectionTitle: '¿Qué es Vivir en Equilibrio?',
        sectionBody:
          'Programa de bienestar 360° con Liliana Fernandes en vivo y On-Demand por TNO Network.',
        anchorName: 'Liliana Fernandes',
        anchorSubtitle: 'Ancla de Vivir en Equilibrio.',
        anchorBio:
          'Conecta con audiencias activas que priorizan salud, constancia y transformación personal.',
      },
      videoUrl: 'https://www.youtube.com/embed/c6S2eKbOCOM?start=1',
      benefitsSection: {},
      benefits: [],
      metrics: [],
      values: {},
      pricingOptions: [],
      areYouReady: {
        title: '¿Listo para patrocinar?',
        description:
          'Forma parte de Vivir en Equilibrio y conecta con quienes buscan equilibrio integral.',
        ctaText: 'Patrocinar Vivir en Equilibrio',
        footerText: '© TNO Network 2026 | Vivir en Equilibrio: Bienestar que transforma.',
      },
    },
    isActive: true,
    order: 1.06,
    ogImage: 'https://files.tnonetwork.com/shows/logo/vivirenequilibrio.png',
  },
  {
    type: 'program',
    slug: 'tnosports',
    name: 'TNO Sports',
    metaTitle: 'TNO Sports | Anuncia con nosotros',
    metaDescription:
      'Anuncia en TNO Sports con Yeraldin Marrero (+100K en Instagram). Agenda deportiva, fútbol, béisbol y Vinotinto en TNO Network.',
    content: {
      who: {
        logoUrl: 'https://files.tnonetwork.com/shows/logo/tnosports.png',
        title: 'TNO Sports: la agenda deportiva de TNO Network con pasión y análisis',
        description:
          'Fútbol, béisbol, agenda nacional e internacional y cobertura con el sello TNO.',
        sectionTitle: '¿Qué es TNO Sports?',
        sectionBody:
          'Programa deportivo ganador del TNO Radio Award en vivo y On-Demand por TNO Network.',
        anchorName: 'Yeraldin Marrero',
        anchorSubtitle: 'Ancla de TNO Sports.',
        anchorImageUrl: '/images/talent/yeraldinmarrero.jpg',
        anchorBio:
          'Periodista y comunicadora deportiva con más de 100 mil seguidores en Instagram. Conduce TNO Sports en TNO Network: agenda en antena, análisis y cobertura en terreno para marcas y eventos.',
      },
      videoUrl: 'https://www.youtube.com/embed/Etof3gg2vAA?start=1',
      benefitsSection: {},
      benefits: [],
      metrics: [
        { value: '100K+', label: 'Seguidores de Yeraldin Marrero en Instagram' },
        { value: '90K+', label: 'Seguidores del ecosistema social TNO Network' },
        { value: '30K+', label: 'Visitas mensuales web y plataforma TNO' },
        { value: '1', label: 'Emisión en vivo semanal · TNO Sports' },
      ],
      values: {},
      pricingOptions: [],
      areYouReady: {
        title: '¿Listo para patrocinar?',
        description:
          'Forma parte de TNO Sports y conecta con fanáticos del deporte venezolano.',
        ctaText: 'Patrocinar TNO Sports',
        footerText: '© TNO Network 2026 | TNO Sports: Pasión que convierte.',
      },
    },
    isActive: true,
    order: 1.08,
    ogImage: 'https://files.tnonetwork.com/shows/logo/tnosports.png',
  },
  {
    type: 'program',
    slug: 'comerciocorporativo',
    name: 'Comercio Corporativo',
    metaTitle: 'Comercio Corporativo | Anuncia con nosotros',
    metaDescription:
      'Anuncia en Comercio Corporativo con Darío Ascanio en TNO Network. Economía, gestión, finanzas y éxito empresarial. Patrocinio, segmentos, entrevistas, TNO Digest, cápsulas y UGC.',
    content: {
      who: {
        logoUrl: 'https://files.tnonetwork.com/shows/logo/comerciocorporativo.png',
        title: 'Comercio Corporativo: economía, gestión y éxito empresarial',
        description:
          'Coyuntura económica, liderazgo consciente y casos de éxito con rigor periodístico.',
        sectionTitle: '¿Qué es Comercio Corporativo?',
        sectionBody:
          'Con Darío Ascanio: lunes 10:00 AM (Venezuela) en vivo y On-Demand por TNO Network.',
        anchorName: 'Darío Ascanio',
        anchorSubtitle: 'Ancla de Comercio Corporativo.',
        anchorImageUrl: 'https://files.tnonetwork.com/users/profile/darioascanio.jpg',
        anchorBio:
          'Periodista que conduce entrevistas con líderes, gremios y emprendedores; ideal para marcas B2B y patrocinio corporativo.',
      },
      videoUrl: 'https://www.youtube.com/embed/1ZTlJJYA878?start=3',
      benefitsSection: {},
      benefits: [],
      metrics: [
        { value: '4+', label: 'Episodios On-Demand disponibles' },
        { value: '90K+', label: 'Seguidores del ecosistema social TNO Network' },
        { value: '30K+', label: 'Visitas mensuales web y plataforma TNO' },
        { value: '1', label: 'Emisión en vivo semanal · Lunes 10:00 AM' },
      ],
      values: {},
      pricingOptions: [],
      areYouReady: {
        title: '¿Listo para patrocinar?',
        description:
          'Forma parte de Comercio Corporativo y conecta con quienes mueven el sector empresarial.',
        ctaText: 'Patrocinar Comercio Corporativo',
        footerText: '© TNO Network 2026 | Comercio Corporativo: Gestión que transforma.',
      },
    },
    isActive: true,
    order: 1.065,
    ogImage: 'https://files.tnonetwork.com/shows/logo/comerciocorporativo.png',
  },
  {
    type: 'program',
    slug: 'despuesdelevento',
    name: 'Después del evento',
    metaTitle: 'Después del evento | Anuncia con nosotros',
    metaDescription:
      'Anuncia en Después del evento con Angélica Lugo y Yelitza Yánez en TNO Network. Resiliencia, historias humanas y periodismo de soluciones. Patrocinio, segmentos, entrevistas, TNO Digest, cápsulas y UGC.',
    content: {
      who: {
        logoUrl: 'https://files.tnonetwork.com/shows/logo/despuesdelevento.png',
        title: 'Después del evento: historias humanas que permanecen',
        description:
          'Periodismo de soluciones y resiliencia: entrevistas que trascienden la noticia.',
        sectionTitle: '¿Qué es Después del evento?',
        sectionBody:
          'Con Angélica Lugo y Yelitza Yánez: martes 7:00 PM (Venezuela) en vivo y On-Demand por TNO Network.',
        anchorName: 'Angélica Lugo y Yelitza Yánez',
        anchorSubtitle: 'Anclas de Después del evento.',
        anchorImageUrl: 'https://files.tnonetwork.com/shows/cover/despuesdelevento.jpg',
        anchorBio:
          'Conducen un espacio íntimo para explorar cómo el trauma se transforma en superación; ideal para marcas con propósito social.',
      },
      videoUrl: 'https://www.youtube.com/embed/t52SgiuzxRk?start=1',
      benefitsSection: {},
      benefits: [],
      metrics: [
        { value: '14+', label: 'Episodios On-Demand disponibles' },
        { value: '90K+', label: 'Seguidores del ecosistema social TNO Network' },
        { value: '30K+', label: 'Visitas mensuales web y plataforma TNO' },
        { value: '1', label: 'Emisión en vivo semanal · Martes 7:00 PM' },
      ],
      values: {},
      pricingOptions: [],
      areYouReady: {
        title: '¿Listo para patrocinar?',
        description:
          'Forma parte de Después del evento y conecta con quienes buscan historias que inspiran.',
        ctaText: 'Patrocinar Después del evento',
        footerText: '© TNO Network 2026 | Después del evento: Historias humanas que permanecen.',
      },
    },
    isActive: true,
    order: 1.068,
    ogImage: 'https://files.tnonetwork.com/shows/logo/despuesdelevento.png',
  },
  {
    type: 'program',
    slug: 'ensintoniacontusalud',
    name: 'En Sintonía con tu Salud',
    metaTitle: 'En Sintonía con tu Salud | Anuncia con nosotros',
    metaDescription:
      'Anuncia en En Sintonía con tu Salud con la Dra. Grecia Andreina en TNO Network. Bienestar integral, prevención y salud. Patrocinio, segmentos, entrevistas, TNO Digest, cápsulas y UGC.',
    content: {
      who: {
        logoUrl: 'https://files.tnonetwork.com/shows/logo/ensintoniacontusalud.png',
        title: 'En Sintonía con tu Salud: bienestar integral y prevención',
        description:
          'Ciencia y cotidianidad para una vida plena: salud física, mental y hábitos preventivos.',
        sectionTitle: '¿Qué es En Sintonía con tu Salud?',
        sectionBody:
          'Con la Dra. Grecia Andreina en vivo y On-Demand por TNO Network.',
        anchorName: 'Dra. Grecia Andreina',
        anchorSubtitle: 'Ancla de En Sintonía con tu Salud.',
        anchorImageUrl: 'https://files.tnonetwork.com/users/profile/greciagonzalez.jpg',
        anchorBio:
          'Médica y comunicadora que orienta a la audiencia en prevención y bienestar integral; ideal para marcas de salud y wellness.',
      },
      videoUrl: '',
      benefitsSection: {},
      benefits: [],
      metrics: [
        { value: '18+', label: 'Episodios On-Demand disponibles' },
        { value: '90K+', label: 'Seguidores del ecosistema social TNO Network' },
        { value: '30K+', label: 'Visitas mensuales web y plataforma TNO' },
        { value: '1', label: 'Encuentro semanal en antena' },
      ],
      values: {},
      pricingOptions: [],
      areYouReady: {
        title: '¿Listo para patrocinar?',
        description:
          'Forma parte de En Sintonía con tu Salud y conecta con quienes priorizan el autocuidado.',
        ctaText: 'Patrocinar En Sintonía con tu Salud',
        footerText: '© TNO Network 2026 | En Sintonía con tu Salud: Bienestar integral que informa.',
      },
    },
    isActive: true,
    order: 1.069,
    ogImage: 'https://files.tnonetwork.com/shows/logo/ensintoniacontusalud.png',
  },
  {
    type: 'program',
    slug: '2doescalon',
    name: '2do Escalón',
    metaTitle: '2do Escalón | Anuncia con nosotros',
    metaDescription:
      'Anuncia en 2do Escalón en TNO Network. Cultura pop, juventud y entretenimiento universitario. Patrocinio, segmentos, entrevistas, TNO Digest, cápsulas y UGC.',
    content: {
      who: {
        logoUrl: 'https://files.tnonetwork.com/shows/logo/2doescalon.png',
        title: '2do Escalón: cultura pop, juventud y entretenimiento',
        description:
          'Cuatro comunicadores en potencia descifran el mundo real con humor, tendencias y conversación sin filtro.',
        sectionTitle: '¿Qué es 2do Escalón?',
        sectionBody:
          'Martes 1:00 PM y Viernes 2:00 PM (Venezuela) en vivo y On-Demand por TNO Network.',
        anchorName: 'Equipo 2do Escalón',
        anchorSubtitle: 'Comunicadores juveniles de TNO Network.',
        anchorImageUrl: 'https://files.tnonetwork.com/shows/ondemand_main/2doescalon.jpg',
        anchorBio:
          'Podcast y programa en antena para universitarios y jóvenes profesionales; ideal para marcas de educación, tecnología, moda y entretenimiento.',
      },
      videoUrl: 'https://www.youtube.com/embed/uZeFbL-Aiu4',
      benefitsSection: {},
      benefits: [],
      metrics: [
        { value: '11+', label: 'Episodios On-Demand disponibles' },
        { value: '90K+', label: 'Seguidores del ecosistema social TNO Network' },
        { value: '30K+', label: 'Visitas mensuales web y plataforma TNO' },
        { value: '2', label: 'Emisiones en vivo semanales' },
      ],
      values: {},
      pricingOptions: [],
      areYouReady: {
        title: '¿Listo para patrocinar?',
        description:
          'Forma parte de 2do Escalón y conecta con la audiencia joven de TNO Network.',
        ctaText: 'Patrocinar 2do Escalón',
        footerText: '© TNO Network 2026 | 2do Escalón: Cultura pop, juventud y entretenimiento.',
      },
    },
    isActive: true,
    order: 1.0695,
    ogImage: 'https://files.tnonetwork.com/shows/logo/2doescalon.png',
  },
  {
    type: 'program',
    slug: 'hablemosdetodo',
    name: 'Hablemos de Todo',
    metaTitle: 'Hablemos de Todo | Anuncia con nosotros',
    metaDescription:
      'Anuncia en Hablemos de Todo con Janeth Cano en TNO Network. Relaciones, liderazgo, bienestar y desarrollo personal. Patrocinio, segmentos, entrevistas, TNO Digest, cápsulas y UGC.',
    content: {
      who: {
        logoUrl: 'https://files.tnonetwork.com/shows/logo/hablemosdetodo.png',
        title: 'Hablemos de Todo: conversaciones para crecer en lo personal y profesional',
        description:
          'Un espacio de entrevistas y conversación honesta sobre relaciones, liderazgo, bienestar y decisiones que impactan la vida diaria.',
        sectionTitle: '¿Qué es Hablemos de Todo?',
        sectionBody:
          'Con Janeth Cano en TNO Network: contenido On-Demand, clips para redes y presencia en YouTube.',
        anchorName: 'Janeth Cano',
        anchorSubtitle: 'Ancla de Hablemos de Todo.',
        anchorImageUrl: 'https://files.tnonetwork.com/shows/logo/hablemosdetodo.png',
        anchorBio:
          'Comunicadora y conductora que conecta temas humanos y profesionales con una narrativa cercana, ideal para marcas que buscan confianza y afinidad.',
      },
      videoUrl: 'https://www.youtube.com/embed/DNYNvZsZMLM',
      benefitsSection: {},
      benefits: [],
      metrics: [
        { value: '16+', label: 'Episodios On-Demand disponibles' },
        { value: '90K+', label: 'Seguidores del ecosistema social TNO Network' },
        { value: '30K+', label: 'Visitas mensuales web y plataforma TNO' },
        { value: '1', label: 'Programa activo en formato podcast' },
      ],
      values: {},
      pricingOptions: [],
      areYouReady: {
        title: '¿Listo para patrocinar?',
        description:
          'Forma parte de Hablemos de Todo y conecta con una audiencia que busca contenido con propósito.',
        ctaText: 'Patrocinar Hablemos de Todo',
        footerText: '© TNO Network 2026 | Hablemos de Todo: Conversaciones que generan valor.',
      },
    },
    isActive: true,
    order: 1.0696,
    ogImage: 'https://files.tnonetwork.com/shows/logo/hablemosdetodo.png',
  },
  {
    type: 'program',
    slug: 'siempremasmusica',
    name: 'Siempre más Música',
    metaTitle: 'Siempre más Música | Anuncia con nosotros',
    metaDescription:
      'Anuncia en Siempre más Música con Arlene Flores en TNO Network. Música, cultura, entrevistas y eventos. Patrocinio, segmentos, entrevistas, TNO Digest, cápsulas y UGC.',
    content: {
      who: {
        logoUrl: 'https://files.tnonetwork.com/shows/logo/siempremasmusica.png',
        title: 'Siempre más Música: la vitrina musical de TNO Network',
        description:
          'Difusión de la escena musical nacional e internacional, entrevistas y cobertura de eventos culturales.',
        sectionTitle: '¿Qué es Siempre más Música?',
        sectionBody:
          'Con Arlene Flores: viernes 5:00 PM (Venezuela) en vivo y On-Demand por TNO Network.',
        anchorName: 'Arlene Flores',
        anchorSubtitle: 'Host de Siempre más Música.',
        anchorImageUrl: 'https://files.tnonetwork.com/users/profile/arleflores.jpg',
        anchorBio:
          'Conduce el espacio musical de TNO Network: artistas, tendencias, festivales y gestión cultural para marcas de entretenimiento.',
      },
      videoUrl: 'https://www.youtube.com/embed/cYAAJBkfskM?start=1',
      benefitsSection: {},
      benefits: [],
      metrics: [
        { value: '19+', label: 'Episodios On-Demand (temporada actual)' },
        { value: '90K+', label: 'Seguidores del ecosistema social TNO Network' },
        { value: '30K+', label: 'Visitas mensuales web y plataforma TNO' },
        { value: '1', label: 'Emisión en vivo semanal · Viernes 5:00 PM' },
      ],
      values: {},
      pricingOptions: [],
      areYouReady: {
        title: '¿Listo para patrocinar?',
        description:
          'Forma parte de Siempre más Música y conecta con audiencias que viven la cultura y el entretenimiento.',
        ctaText: 'Patrocinar Siempre más Música',
        footerText: '© TNO Network 2026 | Siempre más Música: La vitrina musical de TNO Network.',
      },
    },
    isActive: true,
    order: 1.07,
    ogImage: 'https://files.tnonetwork.com/shows/logo/siempremasmusica.png',
  },
  {
    type: 'program',
    slug: 'vidainmobiliaria',
    name: 'Vida Inmobiliaria',
    metaTitle: 'Vida Inmobiliaria | Anuncia con nosotros',
    metaDescription:
      'Anuncia en Vida Inmobiliaria con Carolina Hernández. Mercado inmobiliario, inversión y patrimonio en TNO Network.',
    content: {
      who: {
        title: 'Vida Inmobiliaria: mercado, tendencias y decisiones inteligentes sobre tu patrimonio',
        description:
          'Información clara sobre bienes raíces para compradores, inversionistas y familias.',
        sectionTitle: '¿Qué es Vida Inmobiliaria?',
        sectionBody:
          'Programa del nicho inmobiliario con Carolina Hernández en vivo y On-Demand por TNO Network.',
        anchorName: 'Carolina Hernández',
        anchorSubtitle: 'Ancla de Vida Inmobiliaria.',
        anchorBio:
          'Conecta con audiencias que investigan, compran y administran bienes raíces.',
      },
      videoUrl: 'https://www.youtube.com/embed/Efe0ayB2Afo?start=3',
      benefitsSection: {},
      benefits: [],
      metrics: [],
      values: {},
      pricingOptions: [],
      areYouReady: {
        title: '¿Listo para patrocinar?',
        description:
          'Forma parte de Vida Inmobiliaria y conecta con quienes mueven el sector.',
        ctaText: 'Patrocinar Vida Inmobiliaria',
        footerText: '© TNO Network 2026 | Vida Inmobiliaria: Tu patrimonio, nuestra conversación.',
      },
    },
    isActive: true,
    order: 1.1,
    ogImage: 'https://files.tnonetwork.com/shows/logo/vidainmobiliaria.png',
  },
  {
    type: 'program',
    slug: 'marketingenmovimiento',
    name: 'Marketing en Movimiento',
    metaTitle: 'Marketing en Movimiento | Anuncia con nosotros',
    metaDescription:
      'Anuncia en Marketing en Movimiento. Marketing real, emprendimiento, digitalización y talento con Loyola Rosales y Luzmey Hernández en TNO Network.',
    content: {
      who: {
        title: 'Marketing en Movimiento: estrategia, negocios y transformación digital',
        description:
          'El espacio donde marcas y emprendedores encuentran tendencias, casos reales y herramientas para crecer en la era digital.',
        sectionTitle: '¿Qué es Marketing en Movimiento?',
        sectionBody:
          'Programa de TNO Network con entrevistas a expertas en marketing, economía, emprendimiento y talento. Transmisión en vivo los martes a las 11:00 AM (Venezuela).',
        anchorName: 'Loyola Rosales y Luzmey Hernández',
        anchorSubtitle: 'Conductoras de Marketing en Movimiento.',
        anchorBio:
          'Loyola Rosales (CME Marketing Solutions) y Luzmey Hernández (UPA B2B) combinan estrategia de negocios y marketing digital en un formato dinámico para audiencias que toman decisiones de inversión.',
      },
      videoUrl: 'https://www.youtube.com/embed/DcBqH6g79wM',
      benefitsSection: {},
      benefits: [],
      metrics: [],
      values: {},
      pricingOptions: [],
      areYouReady: {
        title: '¿Listo para patrocinar?',
        description:
          'Forma parte de Marketing en Movimiento y conecta con profesionales del marketing, emprendedores y tomadores de decisión.',
        ctaText: 'Patrocinar Marketing en Movimiento',
        footerText: '© TNO Network 2026 | Marketing en Movimiento: Estrategia que no se detiene.',
      },
    },
    isActive: true,
    order: 1.25,
    ogImage: 'https://files.tnonetwork.com/shows/logo/marketingenmovimiento.png',
  },
  {
    type: 'program',
    slug: '3decopas',
    name: '3 de Copas con Zidney',
    metaTitle: '3 de Copas con Zidney | Anuncia con nosotros',
    metaDescription:
      'Anuncia en 3 de Copas con Zidney: conversación, lifestyle y transformación en TNO Network. Patrocinio, segmentos y UGC.',
    content: {
      who: {
        title: '3 de Copas: conversaciones donde cada transformación es un brindis',
        description:
          'Un espacio de TNO Network para hablar de cambio real: emociones, estilo de vida, autoconocimiento y cultura con un tono cercano y celebratorio.',
        sectionTitle: '¿Qué es 3 de Copas?',
        sectionBody:
          '3 de Copas es un programa en el que la conversación importa tanto como el mensaje. Presencia en app y web de TNO, redes del ecosistema y On-Demand. Promoción en redes: jueves 3:00 PM (Venezuela) en TNO Network; confirma grilla en cartelera y redes.',
        anchorName: 'Zidney Alzauro',
        anchorSubtitle: 'Ancla de 3 de Copas.',
        anchorBio:
          'Zidney Alzauro conduce 3 de Copas con energía auténtica y un enfoque en la transformación personal, el estilo de vida y los vínculos.',
      },
      videoUrl: 'https://www.youtube.com/embed/a9Qzl7MmHBg?start=1',
      benefitsSection: {},
      benefits: [],
      metrics: [],
      values: {},
      pricingOptions: [],
      areYouReady: {
        title: '¿Listo para patrocinar?',
        description:
          'Forma parte de 3 de Copas y conecta con quienes buscan conversación, estilo y transformación.',
        ctaText: 'Patrocinar 3 de Copas',
        footerText: '© TNO Network 2026 | 3 de Copas: conversación que conecta.',
      },
    },
    isActive: true,
    order: 1.5,
    ogImage: 'https://files.tnonetwork.com/shows/logo/3decopas.png',
  },
  {
    type: 'program',
    slug: 'doctorcondominioradio',
    name: 'Dr. Condominio Radio',
    metaTitle: 'Dr. Condominio Radio | La voz de la convivencia y el bienestar inmobiliario',
    metaDescription: 'Dr. Condominio Radio: asesoría, gestión y soluciones para la vida en comunidad. Conecta tu marca con copropietarios y juntas de condominio con el respaldo del Dr. Julio César López Galea.',
    content: {
      who: {
        title: 'Dr. Condominio Radio: La voz de la convivencia y el bienestar inmobiliario',
        description: 'El espacio líder dedicado a la asesoría, gestión y soluciones para la vida en comunidad. Transformamos los retos de la propiedad horizontal en herramientas de sana convivencia, creando una plataforma de confianza donde las marcas se integran como soluciones directas a las necesidades del hogar y el patrimonio.',
        sectionTitle: '¿Qué es Dr. Condominio?',
        sectionBody: 'Es un ecosistema de contenido diseñado para copropietarios, juntas de condominio y administradores que buscan proteger su inversión y mejorar su calidad de vida. Nos enfocamos en soluciones legales, técnicas y de mantenimiento con un lenguaje claro, dinámico y, sobre todo, resolutivo. En Dr. Condominio, convertimos la gestión inmobiliaria en un proceso eficiente y transparente. Transmisión en vivo: [Insertar Día] a las [Insertar Hora] por TNO Network.',
        anchorName: 'Dr. Julio César López Galea',
        anchorSubtitle: 'La autoridad jurídica y técnica que el mercado inmobiliario respeta.',
        anchorBio: 'El Dr. Julio César López Galea lidera este espacio con una trayectoria impecable en el derecho y la gestión de condominios. Su profundo conocimiento de la normativa venezolana y su enfoque práctico para resolver conflictos lo convierten en la figura de mayor credibilidad en el sector. Su liderazgo asegura que cada marca patrocinante cuente con el respaldo de una autoridad reconocida, garantizando que el mensaje comercial sea recibido con confianza y seriedad por quienes toman las decisiones.',
      },
      videoUrl: 'https://www.youtube.com/embed/UcoArfFDvNk',
      benefitsSection: {
        title: 'Temas: Un panorama integral',
        description: 'Dr. Condominio Radio se divide en ejes estratégicos para abordar cada arista de la vida en edificios. Cada tema conecta con una necesidad real de compra:',
      },
      benefits: [
        { title: 'Asesoría Jurídica', text: 'Interpretación experta de la Ley de Propiedad Horizontal y justicia administrativa.', icon: 'Gavel' },
        { title: 'Ingeniería y Mantenimiento', text: 'Soluciones para fachadas, impermeabilización, ascensores y sistemas hidráulicos.', icon: 'Build' },
        { title: 'Administración y Cobranza', text: 'Estrategias contra la morosidad y optimización de fondos de reserva.', icon: 'AccountBalance' },
        { title: 'Innovación en Seguridad', text: 'Tecnología aplicada al resguardo de edificios y control de acceso.', icon: 'Security' },
        { title: 'Convivencia Vecinal', text: 'Mediación de conflictos y fomento de la armonía en comunidad.', icon: 'People' },
      ],
      metrics: [
        { value: '20,000+', label: 'Seguidores en todas sus redes sumadas' },
        { value: '30,000+', label: 'Visitas mensuales del ecosistema TNO (alcance editorial multiplataforma)' },
        { value: '20+', label: 'Episodios On-Demand disponibles (biblioteca de consulta permanente)' },
        { value: '1', label: 'Emisión en vivo semanal con distribución global por TNO Network' },
      ],
      values: {
        title: 'Ventajas Competitivas: ¿Por qué Dr. Condominio?',
        items: [
          'Autoridad del Ancla: El respaldo del Dr. Julio César López Galea otorga un sello de garantía a sus aliados.',
          'Mercado B2B y B2C: Conexión simultánea con propietarios individuales y juntas que firman contratos corporativos.',
          'Innovación Mediática: Somos un Media Lab; tu marca se ve en alta definición y se escucha con la mejor tecnología de streaming.',
          'UGC y Validación Real: Generamos contenido donde mostramos tu producto resolviendo un problema real en un edificio, eliminando la barrera de la "publicidad tradicional".',
        ],
      },
      pricingOptions: [
        {
          title: 'UGC Service Experience',
          priceLabel: '100 USD mensual',
          features: [
            'Un video semanal.',
            'Video dinámico tipo "Solución en sitio" (mantenimiento, remodelación, servicios).',
            'Demostración real del producto/servicio en un entorno de edificio o condominio.',
            'Narrativa enfocada en la resolución de problemas comunes.',
            'Archivo optimizado para las redes sociales de la marca.',
          ],
          idealFor: 'Empresas de impermeabilización, seguridad, ascensores, pintura, ferretería y servicios legales.',
          buttonText: 'Activar Campaña de Confianza',
          highlightedTitle: 'Validación de Comunidad',
          highlightedContent: [
            'El contenido auténtico en condominios genera una confianza del 75% más que un folleto tradicional.',
            'Tu marca deja de ser un gasto y se convierte en una inversión para la comunidad.',
          ],
        },
        {
          title: 'Sponsorship: El ABC del Condominio',
          priceLabel: '100',
          features: [
            'Integración de marca en la sección de asesoría legal o técnica.',
            'Mención de beneficios del producto como facilitador de la convivencia.',
            'Presencia visual del logo y producto en el set (Radio Visual).',
          ],
          idealFor: 'Bancos (cuentas de condominio), seguros, empresas de limpieza y software de administración.',
          buttonText: 'Patrocinar Segmento',
        },
        {
          title: 'Interactive Community Push',
          priceLabel: '60',
          features: [
            'Encuestas y sesiones de preguntas y respuestas en Instagram Stories.',
            'Fomento del debate sobre mejoras en edificios vinculadas a la marca.',
            'Call to action con link directo a cotizaciones o catálogos.',
          ],
          idealFor: 'Proveedores que buscan captar leads directos de Juntas de Condominio.',
          buttonText: 'Generar Leads',
        },
        {
          title: 'Socio Estratégico del Condominio',
          priceLabel: '250 por mes',
          features: [
            'Mención de apertura y cierre: "Este programa llega a ustedes gracias a..."',
            'Banner fijo (Lower Third) con logo de la marca durante toda la transmisión en streaming.',
            'Rotación de cuñas comerciales de 30 seg en cada bloque del programa.',
            'Exclusividad de rubro (Solo una marca por categoría en el programa).',
          ],
          idealFor: 'Empresas líderes en seguros, banca corporativa o servicios de ingeniería a gran escala.',
          buttonText: 'Ser Patrocinante Principal',
        },
        {
          title: 'Expert Talk: Soluciones en Vivo',
          priceLabel: '180 por entrevista',
          features: [
            'Segmento de 15 minutos de entrevista central con el Dr. Julio César López Galea.',
            'Enfoque educativo-comercial: Cómo tu producto resuelve un vacío legal o técnico.',
            'Corte del video de la entrevista optimizado para YouTube y Redes Sociales.',
            'Derecho a usar el sello "Visto en Dr. Condominio Radio" en tus comunicaciones.',
          ],
          idealFor: 'CEOs de administradoras, peritos, especialistas en seguridad o nuevos productos tecnológicos.',
          buttonText: 'Agendar Entrevista',
        },
        {
          title: 'Branded Content Editorial',
          priceLabel: '40 por post',
          features: [
            'Artículo especializado en tnonetwork.com (Sección TNO Digest).',
            'Redacción profesional enfocada en soluciones para la comunidad.',
            'Incluye enlaces (Backlinks) directos a tu página web o WhatsApp de ventas.',
            'Permanencia garantizada: Tu marca aparecerá en las búsquedas de Google sobre condominios.',
          ],
          idealFor: 'Proveedores que necesitan explicar a fondo sus servicios (ej. nuevos sistemas de impermeabilización).',
          buttonText: 'Publicar Artículo',
        },
      ],
      areYouReady: {
        title: '¿Listo para ser la solución que los condominios están buscando?',
        description: 'Forma parte de Dr. Condominio Radio y conecta con los tomadores de decisiones que invierten diariamente en el mantenimiento y mejora de su hábitat. Hagamos que tu empresa sea el próximo gran aliado de nuestras comunidades bajo la guía del Dr. Julio César López Galea.',
        ctaText: 'Contactar para Dr. Condominio Radio',
        footerText: '© TNO Network 2026 | Dr. Condominio: Tu patrimonio, nuestra prioridad. | The Content Evolution.',
      },
    },
    isActive: true,
    order: 2,
  },
  {
    type: 'program',
    slug: 'franquiciasenvenezuela',
    name: 'Franquicias en Venezuela',
    metaTitle: 'Franquicias en Venezuela | Anuncia con nosotros',
    metaDescription: 'Franquicias en Venezuela: el ecosistema de referencia en TNO Network para analizar, promover y expandir modelos de negocio escalables. Con Argenis Carpio y una audiencia B2B de inversión, gerencia y emprendimiento.',
    content: {
      who: {
        contactEmail: 'mercadeoyventas@tnonetwork.com',
        logoUrl: 'https://files.tnonetwork.com/shows/logo/franquiciasenvenezuela.png',
        title: 'Franquicias en Venezuela: El poder de la escalabilidad',
        description: 'El ecosistema de referencia en TNO Network para el análisis, la promoción y la expansión de los modelos de negocio más exitosos del país. Una plataforma de autoridad donde la inversión encuentra su hoja de ruta.',
        sectionTitle: '¿Qué es Franquicias en Venezuela?',
        sectionLinkUrl: 'https://tnonetwork.com/show/franquiciasenvenezuela',
        sectionLinkLabel: 'tnonetwork.com/show/franquiciasenvenezuela',
        sectionBody: 'Es el espacio estratégico diseñado para decodificar el ADN de las empresas que logran trascender. Más que un programa, es una cátedra de visión empresarial que conecta a dueños de marcas, inversionistas y emprendedores con las herramientas necesarias para transformar un negocio en una red de éxito replicable. Conéctate en vivo los lunes de 1:00 PM a 3:00 PM por TNO Network.',
        anchorName: 'Argenis Carpio',
        anchorSubtitle: 'Experto en Franquicias y Visión de Negocios.',
        anchorBio: 'Argenis Carpio lidera este espacio con la autoridad que le otorgan años de consultoría y conocimiento profundo del mercado nacional. Su capacidad para diseccionar modelos de negocio y entrevistar a las mentes maestras detrás de las marcas más emblemáticas convierte cada episodio en una herramienta de consulta obligatoria para el sector empresarial.',
        anchorImageUrl: 'https://files.tnonetwork.com/users/profile/argenismcarpio.jpg',
      },
      benefitsSection: {
        title: 'Ejes Temáticos',
        description: 'Un panorama de inversión estratégica. "Franquicias en Venezuela" se estructura en pilares que garantizan contenido de alto valor, ideal para marcas que buscan posicionarse ante tomadores de decisiones.',
      },
      benefits: [
        {
          title: 'Casos de Éxito',
          text: 'Análisis de las marcas que han logrado estandarizar la excelencia y expandirse con solidez.',
          icon: 'TrendingUp',
        },
        {
          title: 'ADN del Franquiciante',
          text: 'Entrevistas de autoridad con los fundadores y directivos que están transformando la economía.',
          icon: 'People',
        },
        {
          title: 'Ruta del Inversionista',
          text: 'Consejos técnicos, legales y financieros para quienes buscan oportunidades de negocio seguras.',
          icon: 'AccountBalance',
        },
        {
          title: 'Talento de Exportación',
          text: 'Historias de marcas venezolanas que cruzan fronteras con modelos replicables.',
          icon: 'TrendingUp',
        },
        {
          title: 'Innovación en Retail',
          text: 'Tendencias, tecnología y nuevas dinámicas del consumo masivo.',
          icon: 'Build',
        },
        {
          title: 'TNO Digest (Editorial)',
          text: 'Crónicas especializadas y reseñas profundas sobre el impacto de las franquicias en el PIB nacional.',
          icon: 'Description',
        },
      ],
      metricsSection: {
        overline: 'Números',
        title: 'El impacto de Franquicias en Venezuela',
        description: 'Tu marca se conecta con una audiencia calificada: inversionistas, gerentes y emprendedores con capacidad de ejecución.',
      },
      metrics: [
        {
          value: '8000+',
          label: 'Seguidores en Instagram',
          subtitle: 'Comunidad enfocada en negocios, inversión y expansión.',
          icon: 'instagram',
        },
        {
          value: '30000+',
          label: 'Visitas mensuales al ecosistema TNO',
          subtitle: 'Tráfico mensual en TNO Network.',
          icon: 'web',
        },
        {
          value: '90000+',
          label: 'Seguidores del ecosistema TNO',
          subtitle: 'Audiencia acumulada en todas sus redes.',
          icon: 'audience',
        },
        {
          value: '1000+',
          label: 'Conectados a la transmisión en vivo',
          subtitle: 'Lunes 1:00 PM a 3:00 PM por TNO Network.',
          icon: 'live',
        },
      ],
      transmissionFlow: {
        overline: 'Ciclo de transmisión',
        title: '1 en vivo, 2 reposiciones y on-demand',
        description: 'Cada programa se emite en vivo los lunes, tiene una reposición en la noche, otra el domingo y luego queda disponible en la plataforma on-demand de TNO Network.',
        steps: [
          {
            label: '1 en vivo',
            sublabel: 'Lunes 1:00 PM a 3:00 PM',
            icon: 'live',
          },
          {
            label: 'Reposición 1',
            sublabel: 'Lunes en la noche',
            icon: 'replay',
          },
          {
            label: 'Reposición 2',
            sublabel: 'Domingo',
            icon: 'replay',
          },
          {
            label: 'On-demand',
            sublabel: 'Plataforma TNO',
            icon: 'ondemand',
          },
        ],
      },
      values: {
        title: 'Ventajas Competitivas: ¿Por qué Franquicias en Venezuela?',
        items: [
          'Validación de Marca: Estar en el set de Argenis Carpio otorga un sello de seriedad y éxito a cualquier modelo de negocio.',
          'Producción Premium: Calidad cinematográfica desde el TNO Media Lab, proyectando la solidez corporativa que los inversionistas buscan.',
          'Nichos de Alto Valor: El entorno ideal para banca, seguros, consultoras legales, software de gestión y empresas de suministros.',
          'Huella Digital Permanente: Cada entrevista se convierte en un activo de ventas para la franquicia, disponible siempre en nuestro formato On-Demand y en TNO Digest.',
        ],
      },
      pricingSection: {
        overline: 'Opciones de Conexión',
        title: 'Franquicias en Venezuela',
        description: 'Desliza para ver más opciones.',
      },
      pricingOptions: [
        {
          title: 'Mención de Autoridad',
          features: [
            'Integración de marca en la apertura o cierre del programa.',
            'Asociación directa con un entorno de negocios, inversión y escalabilidad.',
            'Presencia ideal para marcas que buscan validación institucional.',
          ],
          idealFor: 'Banca, seguros, firmas legales y software empresarial.',
          duration: 'Mensual',
          highlightedTitle: 'Ideal para',
          highlightedContent: [
            'Banca, seguros, firmas legales y software empresarial.',
            'Marcas que necesitan validación institucional frente a inversionistas y tomadores de decisiones.',
          ],
          highlightedButtonText: 'Conectar mi marca',
          buttonText: 'Conectar mi marca',
        },
        {
          title: 'Segmento Ruta del Inversionista',
          features: [
            'Presencia de marca en un bloque editorial enfocado en oportunidades seguras de negocio.',
            'Mensajes alineados con educación financiera, expansión y toma de decisiones.',
            'Formato ideal para construir confianza entre potenciales inversionistas.',
          ],
          idealFor: 'Consultoras, fondos, aseguradoras y servicios financieros.',
          duration: 'Mensual',
          highlightedTitle: 'Ideal para',
          highlightedContent: [
            'Consultoras, fondos, aseguradoras y servicios financieros.',
            'Empresas que desean conectar con audiencias enfocadas en expansión, seguridad y rentabilidad.',
          ],
          highlightedButtonText: 'Activar segmento',
          buttonText: 'Activar segmento',
        },
        {
          title: 'Entrevista ADN del Franquiciante',
          features: [
            'Conversación de alto valor con el ancla sobre el modelo de negocio y su potencial de expansión.',
            'Contenido reusable como pieza comercial y activo de autoridad.',
            'Distribución permanente en formato On-Demand.',
          ],
          idealFor: 'Franquicias en expansión, marcas consolidadas y directivos que buscan posicionamiento.',
          duration: 'Por activación',
          highlightedTitle: 'Ideal para',
          highlightedContent: [
            'Franquicias en expansión, marcas consolidadas y directivos que buscan posicionamiento.',
            'Voceros que necesitan una pieza de autoridad comercial reutilizable ante aliados e inversionistas.',
          ],
          highlightedButtonText: 'Solicitar entrevista',
          buttonText: 'Solicitar entrevista',
        },
        {
          title: 'TNO Digest Editorial',
          features: [
            'Artículo o reseña especializada con enfoque en expansión y reputación de marca.',
            'Contenido pensado para visibilidad orgánica y consulta permanente.',
            'Refuerzo del mensaje comercial desde el ángulo editorial.',
          ],
          idealFor: 'Marcas que necesitan profundizar propuesta de valor y respaldo digital.',
          duration: 'Por publicacion',
          highlightedTitle: 'Ideal para',
          highlightedContent: [
            'Marcas que necesitan profundizar propuesta de valor y respaldo digital.',
            'Empresas que buscan visibilidad orgánica y una huella editorial permanente.',
          ],
          highlightedButtonText: 'Publicar en TNO Digest',
          buttonText: 'Publicar en TNO Digest',
        },
        {
          title: 'Insert para Redes',
          features: [
            'Presencia de marca en los clips promocionales del programa.',
            'Integración visual orientada a recordación y asociación con el contenido.',
            'Formato ideal para reforzar visibilidad en piezas cortas para redes sociales.',
          ],
          idealFor: 'Marcas que buscan presencia continua en Instagram, reels, shorts y clips de difusión.',
          duration: 'Mensual',
          highlightedTitle: 'Ideal para',
          highlightedContent: [
            'Marcas que buscan presencia continua en Instagram, reels, shorts y clips de difusión.',
            'Empresas que desean acompañar la promoción del programa con una huella visual constante.',
          ],
          highlightedButtonText: 'Activar insert en redes',
          buttonText: 'Activar insert en redes',
        },
      ],
      areYouReady: {
        title: '¿Listo para escalar tu mensaje?',
        description: 'Forma parte de "Franquicias en Venezuela" y posiciona tu marca ante la comunidad de negocios más influyente del país. Hagamos que tu propuesta sea el próximo gran movimiento en el tablero empresarial.',
        ctaText: 'Contactar a Franquicias en Venezuela',
        contactEmail: 'mercadeoyventas@tnonetwork.com',
        footerText: '© TNO Network 2026 | Franquicias en Venezuela: El Poder de la Escalabilidad.',
        footerSecondaryText: '© TNO Network 2026 | The Content Evolution.',
      },
    },
    isActive: true,
    order: 3,
  },
  {
    type: 'client',
    slug: 'bancaribe',
    name: 'Bancaribe',
    metaTitle: 'Propuesta Comercial | Bancaribe & TNO Network',
    metaDescription: 'Propuesta Bancaribe y TNO Network: Punto de Fe con Margarita Toledo, UGC Lab, Glosario del Progreso (SEO), Flash Mentions y Plan Rotativa 24/7. Inversión total $480/mes.',
    content: {},
    isActive: true,
    order: 4,
  },
  {
    type: 'client',
    slug: 'papaupa',
    name: 'Papaupa',
    metaTitle: 'Propuesta Multi-Programa | Papaupa × TNO Network',
    metaDescription:
      'Papaupa × TNO Network: Plan de Dominio Multi-Pantalla $300/mes. Rotativa/micros 24/7, menciones en Punto de Fe, Somos Padres y 2do Escalón, inserts, banners y redes. «Un pedacito de cielo en cada etapa de tu vida».',
    content: {},
    isActive: true,
    order: 4.5,
  },
  {
    type: 'client',
    slug: 'movistar',
    name: 'Movistar',
    metaTitle: 'Propuesta Comercial | Movistar & TNO Network',
    metaDescription: 'Alianza Movistar y TNO Network para Punto de Fe. Conectividad con Propósito: el Aliado Tecnológico de la Esperanza. Cintillo de autoridad, Tecnología al Servicio del Mensaje, Historias de Conexión.',
    content: {},
    isActive: true,
    order: 5,
  },
  {
    type: 'client',
    slug: 'artesco',
    name: 'Artesco',
    metaTitle: 'Propuesta Comercial | Artesco & TNO Network',
    metaDescription: 'El Arte de Plasmar tu Fe: Artesco en Punto de Fe con Margarita Toledo. Escritura terapéutica, arte como oración, creación de memorias. Aliado de Bienestar. Inversión total $500/mes.',
    content: {},
    isActive: true,
    order: 6,
  },
  {
    type: 'client',
    slug: 'nexus-cultural',
    name: 'Nexus Cultural',
    metaTitle: 'Propuesta de Alianza | Nexus Cultural × Punto de Fe | TNO Network',
    metaDescription:
      'Cultura para el Alma: Nexus Cultural en Punto de Fe con Margarita Toledo. Conexión Nexus, «Alma en Cartelera» (agenda semanal en vivo y en TNO Digest), rotativa 12 spots/día, entrevista mensual, reels y banners web. Inversión $300/mes.',
    ogImage: '/images/nexus.webp',
    content: {},
    isActive: true,
    order: 6.25,
  },
  {
    type: 'client',
    slug: 'atache',
    name: 'Atache (Vital Pharma)',
    metaTitle: 'Propuesta Comercial | Atache & TNO Network',
    metaDescription: 'Belleza con Evidencia: Atache en Expedientes del Delito con Angélica Lugo. Dermocosmética científica, 90 años de Vital Pharma. Inversión total $950/mes.',
    content: {},
    isActive: true,
    order: 7,
  },
  {
    type: 'client',
    slug: 'danimar',
    name: 'Danimar',
    metaTitle: 'Propuesta Comercial | Danimar & TNO Network & Expedientes del Delito',
    metaDescription: 'Danimar & TNO Network & Expedientes del Delito. Justicia Implacable contra la Suciedad: estética de estudio, 3 historias semanales en streaming con Angélica y Santiago, micro Expediente Danimar, TNO Digest SEO (informes Angélica Lugo) y reels. $300/mes.',
    content: {},
    isActive: true,
    order: 8,
  },
  {
    type: 'client',
    slug: 'danimar-express',
    name: 'Danimar Express (TNO Network)',
    metaTitle: 'Propuesta Express | Danimar x TNO Network',
    metaDescription:
      'Danimar en todo el ecosistema TNO Network: 12 spots diarios, 4 historias semanales en streaming, 4 reels al mes, 4 artículos TNO Digest al mes siempre (orden, limpieza, bienestar), banners web. $99/mes (60% cash $59.40 + 40% trade $39.60).',
    content: {},
    isActive: true,
    order: 8.5,
  },
  {
    type: 'client',
    slug: 'chemical',
    name: 'Chemical',
    metaTitle: 'Propuesta Comercial | Chemical x TNO Network',
    metaDescription:
      'Chemical x TNO Network: TNO Business, Franquicias en Venezuela, La Hora del Emprendedor Chemical, 4 artículos TNO Digest al mes (siempre), 12 spots diarios, presencia en programas ancla y Reel pitch. Reclutamiento de distribuidores. $400/mes.',
    content: {},
    isActive: true,
    order: 8.6,
  },
  {
    type: 'client',
    slug: 'altamiravillage',
    name: 'Hotel Altamira Village',
    metaTitle: 'Propuesta Estratégica | Hotel Altamira Village x TNO Network',
    metaDescription: 'Hospitalidad y bienestar: propuesta estratégica para Hotel Altamira Village con streaming 24/7, Punto de Fe con Margarita, TNO Digest SEO, cápsulas On-Demand, presencia web y publicidad tipo UGC con redes sociales. Inversión mensual total: $390.',
    content: {
      hero: {
        overline: 'Propuesta Estratégica',
        title: 'Hotel Altamira Village x TNO Network',
        subtitle: 'Hospitalidad y Bienestar: La Evolución de la Experiencia',
        analysisTitle: '1. El Valor de la Alianza',
        analysisText: 'El Hotel Altamira Village es un icono de exclusividad en Caracas. TNO Network, como plataforma pionera en streaming, es el aliado natural para proyectar esa sofisticación en una infraestructura tecnológica impecable, moderna y disruptiva.',
      },
      products: [
        {
          id: 'punto-fe',
          title: 'Programa Ancla: Punto de Fe con Margarita',
          product: 'Integración orgánica y menciones de autor en contenido editorial en vivo.',
          investment: 'Incluido en plan mensual',
          impacts: [
            'Posicionamiento del hotel como refugio de paz en la ciudad.',
            'Asociación con bienestar, equilibrio y estilo de vida consciente.',
            '3 menciones semanales integradas al contenido del programa.',
            'Recomendaciones cercanas que generan confianza superior a la publicidad tradicional.',
          ],
        },
        {
          id: 'tno-digest',
          title: 'TNO Digest (SEO y Autoridad)',
          product: 'Motor de Conversión editorial con enfoque SEO y enlaces rastreables.',
          investment: 'Incluido en plan mensual',
          impacts: [
            '2 reseñas especializadas al mes escritas por periodistas expertos en SEO.',
            '1 artículo comparativo o de estilo de vida con enlaces directos a reservas.',
            'Mejor posicionamiento orgánico para búsquedas de hospedaje premium en Caracas.',
            'Conversión de lectura en reserva mediante estrategia de afiliados y links trazables.',
          ],
        },
        {
          id: 'streaming-presencia',
          title: 'Streaming, On-Demand y Presencia Web',
          product: 'Visibilidad continua en streaming, cápsulas a la carta y banner en portal.',
          investment: 'Incluido en plan mensual',
          impacts: [
            'Streaming 24/7 con 12 spots diarios de 20 segundos.',
            'Disponibilidad de cápsulas visuales tipo Tour para consumo On-Demand.',
            'Banner de aliado en el portal principal de TNO Network.',
            'Presencia de alta rotación para recordación y conversión.',
          ],
        },
        {
          id: 'redes-ugc',
          title: 'Redes Sociales y UGC',
          product: 'Colaboraciones y publicidad tipo UGC con CTAs rastreables.',
          investment: 'Incluido en plan mensual',
          impacts: [
            'Piezas tipo UGC para redes inspiradas en experiencias del hotel.',
            'Colaboraciones con autores y comunidades del ecosistema TNO.',
            'Difusión multiplataforma para aumentar alcance y engagement.',
            'Llamados a la acción con links trazables para convertir en reserva.',
          ],
        },
      ],
      valueAdd: {
        title: '5. Por qué TNO Network para Altamira Village',
        items: [
          'Trabajamos con mística, cercanía y rigor técnico en cada ejecución.',
          'El hotel no es un cliente más: se integra a una familia que cuida su imagen con calidez humana.',
          'Un hotel disruptivo merece un medio que ha evolucionado la forma de comunicar.',
        ],
      },
      investmentSummary: {
        items: [
          { product: 'Streaming 24/7', detail: '12 spots diarios de 20 segundos en señal de streaming', investment: 'Incluido' },
          { product: 'Punto de Fe con Margarita', detail: '3 menciones semanales integradas al contenido', investment: 'Incluido' },
          { product: 'TNO Digest', detail: '2 reseñas SEO + 1 artículo comparativo con enlaces a reserva', investment: 'Incluido' },
          { product: 'On-Demand', detail: 'Cápsulas visuales tipo Tour para consumo a la carta', investment: 'Incluido' },
          { product: 'Presencia Web', detail: 'Banner de aliado en portal principal de TNO Network', investment: 'Incluido' },
          { product: 'Redes Sociales, UGC y Colaboraciones', detail: 'Publicidad tipo UGC + colaboraciones del ecosistema TNO + difusión en redes con CTAs', investment: 'Incluido' },
        ],
        totalLabel: 'Inversión Mensual Total',
        totalValue: '$390',
      },
      areYouReady: {
        title: 'Activemos Bienestar Village',
        description: 'Listos para convertir la experiencia Altamira Village en una narrativa de bienestar, autoridad y conversión sostenible dentro del ecosistema TNO.',
        ctaText: 'Quiero esta propuesta',
        footerText: '© TNO Network 2026 | Hotel Altamira Village x TNO Network | The Content Evolution.',
      },
    },
    isActive: true,
    order: 9,
  },
  {
    type: 'client',
    slug: 'smarttraining',
    name: 'Smart Training',
    metaTitle: 'Propuesta Estratégica | Smart Training x TNO Network',
    metaDescription: 'Inteligencia en Movimiento: propuesta estratégica para Smart Training con streaming 24/7, Vivir en Equilibrio con Liliana Fernandes, TNO Digest SEO, cápsulas On-Demand y presencia digital. Inversión mensual total: $160.',
    content: {
      hero: {
        overline: 'Propuesta Estratégica',
        title: 'Smart Training x TNO Network',
        subtitle: 'Inteligencia en Movimiento: La Evolución del Entrenamiento',
        analysisTitle: '1. El Valor de la Alianza',
        analysisText: 'Smart Training representa la nueva era del fitness, donde la metodología y el rendimiento se unen. TNO Network, como plataforma pionera en streaming, es el socio ideal para proyectar esa modernidad. Garantizamos que la marca se posicione en una infraestructura digital de vanguardia, con un despliegue visual de alto impacto que conecta con una audiencia activa y digital.',
      },
      products: [
        {
          id: 'vivir-en-equilibrio',
          title: 'Programa Ancla: Vivir en Equilibrio con Liliana Fernandes',
          product: 'Integración orgánica y menciones de estilo de vida en contenido editorial en vivo.',
          investment: 'Incluido en plan mensual',
          impacts: [
            'Smart Training se presenta como centro de soluciones para el bienestar 360 grados.',
            'Posicionamiento del gimnasio como espacio de transformación personal y disciplina.',
            '3 menciones semanales integradas estratégicamente al contenido del programa.',
            'Conexión emocional con audiencia activa que prioriza salud y equilibrio.',
          ],
        },
        {
          id: 'tno-digest-seo',
          title: 'TNO Digest (SEO y Autoridad)',
          product: 'Motor de conversión editorial con periodismo experto en SEO y enlaces rastreables.',
          investment: 'Incluido en plan mensual',
          impacts: [
            '2 reseñas especializadas al mes sobre servicios o clases específicas del gimnasio.',
            '1 artículo mensual de tendencias fitness con enlaces directos a inscripción.',
            'Mayor visibilidad orgánica en búsquedas relacionadas con entrenamiento inteligente y bienestar.',
            'Conversión de lectura en acción mediante estrategia de afiliados hacia planes o contacto de ventas.',
          ],
        },
        {
          id: 'streaming-ondemand-presencia',
          title: 'Streaming, On-Demand y Presencia Digital',
          product: 'Cobertura continua en streaming, cápsulas Smart Tips y banner en portal.',
          investment: 'Incluido en plan mensual',
          impacts: [
            'Streaming 24/7 con 12 spots diarios de 20 segundos.',
            '2 cápsulas mensuales tipo Smart Tips con consejos grabados en sedes.',
            'Banner de aliado en el portal principal de TNO Network.',
            'Alcance permanente para recordación de marca y captación de nuevos miembros.',
          ],
        },
      ],
      valueAdd: {
        title: '5. Por qué TNO Network para Smart Training',
        items: [
          'Trabajamos con mística y cercanía para construir relaciones de largo plazo con nuestros aliados.',
          'Cuidamos a Smart Training como parte de nuestra familia, combinando rigor técnico y calidez humana.',
          'Somos el medio disruptivo ideal para una marca que redefine los límites del fitness.',
        ],
      },
      investmentSummary: {
        items: [
          { product: 'Streaming 24/7', detail: '12 spots diarios de 20 segundos en nuestra señal de streaming', investment: 'Incluido' },
          { product: 'Vivir en Equilibrio con Liliana Fernandes', detail: '3 menciones semanales integradas estratégicamente al contenido', investment: 'Incluido' },
          { product: 'TNO Digest (SEO Premium)', detail: '2 reseñas especializadas al mes + 1 artículo de tendencias fitness con enlaces directos', investment: 'Incluido' },
          { product: 'On-Demand', detail: '2 cápsulas mensuales tipo Smart Tips grabadas en sedes', investment: 'Incluido' },
          { product: 'Presencia Digital', detail: 'Banner de aliado en el portal principal de TNO Network', investment: 'Incluido' },
        ],
        totalLabel: 'Inversión Mensual Total',
        totalValue: '$160',
      },
      areYouReady: {
        title: 'Activemos Equilibrio Smart',
        description: 'Listos para convertir el entrenamiento inteligente de Smart Training en una narrativa de bienestar, autoridad y conversión sostenible dentro del ecosistema TNO.',
        ctaText: 'Quiero esta propuesta',
        footerText: '© TNO Network 2026 | Smart Training x TNO Network | The Content Evolution.',
      },
    },
    isActive: true,
    order: 10,
  },
  {
    type: 'client',
    slug: 'ledhome',
    name: 'Iluminación LED Home',
    metaTitle: 'Iluminación LED Home x TNO Network',
    metaDescription: 'Iluminación LED Home x TNO Network: 2 jornadas de producción in situ, paquete UGC mensual, 12 spots diarios en Radio Visual, 2 artículos SEO semanales en TNO Digest y autoridad web. Inversión mensual total: $460.',
    ogImage: '/images/led_home.jpg',
    content: {
      hero: {
        overline: 'Propuesta Estratégica Mensual',
        title: 'Iluminación LED Home x TNO Network',
        subtitle: 'Luz Real para Gente Real',
        image: '/images/led_home.jpg',
        imageAlt: 'Logo Iluminación LED Home',
        analysisTitle: '1. El Eje de Producción: In Situ + UGC (separados)',
        analysisText: 'Separamos la producción in situ y el UGC como unidades distintas para cubrir objetivo visual y objetivo de conexión orgánica, ejecutando alternancia semanal: una semana UGC y la otra in situ.',
      },
      productsExplained: {
        overline: 'Implementación',
        title: '2. Explicación Estratégica de los Productos',
        description: 'Antes del desglose resumido por bloques, esta sección explica cómo se ejecuta cada producto para lograr presencia, autoridad y conversión en el ecosistema TNO.',
        items: [
          {
            id: 'detalle-insitu',
            title: 'Producción In Situ (2 jornadas mensuales)',
            icon: 'store',
            badge: 'Producción',
            summary: 'Cobertura profesional en tienda para mostrar inventario, tecnología LED y novedades con calidad cinematográfica.',
            topics: [
              'Semana in situ: grabación de showroom, productos destacados y nuevas colecciones.',
              'Recorridos guiados por categorías: hogar, oficina, vitrinas y exteriores.',
              'Comparativas de iluminación antes/después en espacios reales.',
              'Piezas maestras para señal de Radio Visual y bibliotecas de marca.',
            ],
          },
          {
            id: 'detalle-ugc',
            title: 'Paquete UGC mensual (separado)',
            icon: 'ugc',
            badge: 'Contenido orgánico',
            summary: 'UGC (User Generated Content) es contenido con estética de usuario real: formato cercano, espontáneo y auténtico. En esta propuesta se produce de forma guiada por TNO para que la marca se vea creíble, humana y relevante en redes.',
            topics: [
              '¿Qué es UGC? Piezas tipo selfie, review, unboxing o experiencia de uso que se sienten naturales y no como pauta tradicional.',
              '¿Por qué es importante? Aumenta la confianza porque el público conecta más con experiencias reales que con anuncios rígidos.',
              '¿Por qué impulsa resultados? Mejora retención, comentarios, guardados y compartidos en Instagram y TikTok.',
              '¿Por qué acelera decisiones de compra? Muestra producto, beneficio y contexto de uso en segundos.',
              'Semana UGC: piezas estilo selfie con narrativa humana y directa.',
              'Review honesta: pros, temperatura de color y usos ideales por espacio.',
              'Unboxing de iluminación con foco en diseño, potencia y ahorro.',
              'Testimonios cortos y microdemostraciones de instalación rápida.',
            ],
          },
          {
            id: 'detalle-rotativa',
            title: 'Rotativa TNO Network (12 spots diarios)',
            icon: 'broadcast',
            badge: 'Señal 24/7',
            summary: 'La rotativa mantiene frecuencia constante para posicionar marca y recordar ofertas clave durante todo el mes.',
            topics: [
              '¿Por qué es importante estar en un medio? Porque tu marca gana contexto, reputación y recordación dentro de una programación real, no en un anuncio aislado.',
              '¿Qué aporta un medio? Audiencia acumulada, validación editorial y presencia sostenida que acelera confianza y decisión de compra.',
              '¿Por qué TNO? Combina señal de Radio Visual, portal web y ecosistema social para amplificar el mismo mensaje en múltiples puntos de contacto.',
              'Spots de marca: recordación de LED Home como referencia en iluminación.',
              'Spots de oferta: promociones semanales o productos destacados.',
              'Spots de temporada: iluminación para oficina en casa, terrazas o comercios.',
              'Spots de tráfico: llamado directo a visitar showroom, WhatsApp o Instagram.',
            ],
          },
          {
            id: 'detalle-digest',
            title: 'Estrategia SEO Global en TNO Digest (8 artículos mensuales)',
            icon: 'seo',
            badge: 'SEO editorial',
            summary: 'Publicamos contenido educativo optimizado para búsquedas reales de usuarios, fortaleciendo posicionamiento orgánico y autoridad técnica.',
            topics: [
              'Cómo elegir la luz perfecta para tu oficina en casa.',
              'Iluminación inteligente: tendencias 2026 para hogares modernos.',
              'Guía práctica: luz cálida vs luz fría según cada ambiente.',
              '5 errores comunes al iluminar una sala y cómo evitarlos.',
              'Lámparas modernas en Caracas: qué revisar antes de comprar.',
              'Iluminación LED para vitrinas comerciales: vender con mejor exhibición.',
            ],
          },
          {
            id: 'detalle-web',
            title: 'Autoridad Web (banners y presencia fija)',
            icon: 'web',
            badge: 'Presencia digital',
            summary: 'La presencia fija en el portal TNO refuerza el embudo: visibilidad, clic y contacto comercial.',
            topics: [
              'Banners con CTA directo a WhatsApp para cotizaciones rápidas.',
              'Piezas orientadas a campañas mensuales de inventario nuevo.',
              'Mensajes de autoridad: eficiencia energética, diseño y asesoría técnica.',
              'Rotación creativa para mantener frescura visual y CTR activo.',
            ],
          },
        ],
      },
      products: [
        {
          id: 'produccion-sitio',
          title: 'Producción In Situ',
          product: '2 jornadas al mes de grabación profesional en tienda para señal y redes.',
          investment: '$120',
          impacts: [
            'Cobertura de inventario, nuevos ingresos y tecnología LED en tienda.',
            'Recorridos de showroom y demostraciones visuales por categoría.',
            'Contenido audiovisual de alta calidad para posicionamiento de marca.',
          ],
        },
        {
          id: 'ugc-separado',
          title: 'Paquete UGC',
          product: 'Contenido tipo creador separado del in situ, con enfoque orgánico para redes sociales.',
          investment: '$80',
          impacts: [
            'Piezas UGC estilo selfie, review honesta y unboxing.',
            'Formato cercano que aumenta confianza y probabilidad de interacción.',
            'Alta adaptabilidad para Instagram Reels y TikTok.',
          ],
        },
        {
          id: 'rotativa-tno',
          title: 'Rotativa TNO Network',
          product: '12 spots diarios en señal de Radio Visual 24/7 (360 al mes).',
          investment: '$100',
          impacts: [
            'Alta frecuencia de exposición para posicionamiento de marca.',
            'Presencia sostenida en una señal multiplataforma activa todo el día.',
            'Cobertura audiovisual continua para reforzar recordación y confianza.',
          ],
        },
        {
          id: 'seo-global',
          title: 'Estrategia SEO Global',
          product: '2 artículos semanales en el portal editorial TNO Digest (8 al mes).',
          investment: '$120',
          impacts: [
            'Impulso orgánico en búsquedas como "iluminación inteligente" y "lámparas modernas en Caracas".',
            'Contenido de valor para posicionar a LED Home como referencia técnica.',
            'Activo digital permanente que capta clientes potenciales 24/7.',
          ],
        },
        {
          id: 'autoridad-web',
          title: 'Autoridad Web',
          product: 'Banners y presencia fija en el portal web de TNO Network.',
          investment: '$40',
          impacts: [
            'Visibilidad adicional en espacios editoriales y de alto tráfico.',
            'Refuerzo de marca para conversión de audiencias digitales a tienda/redes.',
            'Complemento ideal para la estrategia de autoridad en Google.',
          ],
        },
      ],
      valueAdd: {
        layout: 'features',
        overline: 'Diferenciales',
        title: 'La Importancia del SEO y Autoridad en Google',
        items: [
          {
            title: 'Dominio de búsquedas',
            description: 'Al publicar 2 artículos semanales especializados en TNO Digest, la marca se vuelve encontrable y gana posiciones en resultados orgánicos para búsquedas clave del sector.',
            icon: 'chart',
          },
          {
            title: 'Autoridad de marca',
            description: 'El contenido educativo posiciona a Iluminación LED Home como experto técnico, no solo como vendedor, elevando credibilidad frente al comprador.',
            icon: 'shield',
          },
          {
            title: 'Legado digital',
            description: 'Mientras un post social se diluye en horas, los artículos en TNO Digest siguen atrayendo clientes desde Google durante todo el año.',
            icon: 'network',
          },
        ],
      },
      investmentSummary: {
        items: [
          { product: 'Producción In Situ', detail: '2 jornadas al mes de grabación profesional en tienda', investment: '$120' },
          { product: 'Paquete UGC', detail: 'Contenido UGC mensual separado del in situ (una semana UGC, otra semana in situ)', investment: '$80' },
          { product: 'Rotativa TNO Network', detail: '12 spots diarios (360 al mes) en señal de Radio Visual 24/7', investment: '$100' },
          { product: 'Estrategia SEO Global', detail: '2 artículos semanales (8 al mes) en el portal editorial TNO Digest', investment: '$120' },
          { product: 'Autoridad Web', detail: 'Banners y presencia fija en el portal web de TNO Network', investment: '$40' },
        ],
        totalLabel: 'TOTAL MENSUAL | Plan de Autoridad de Marca y Conexión Real',
        totalValue: '$460',
      },
      areYouReady: {
        title: 'Por qué esta alianza es imbatible',
        description: 'Estrategia separada in situ + UGC para equilibrar producción premium y conexión orgánica, con presencia omnicanal en video, web, SEO y redes sociales.',
        ctaText: 'Quiero activar LED Home x TNO',
        footerText: '© TNO Network 2026 | The Content Evolution.',
      },
    },
    isActive: true,
    order: 11.5,
  },
  {
    type: 'client',
    slug: 'mercosur',
    name: 'Mercosur Casa de Bolsa',
    metaTitle: 'Liderazgo Financiero y Autoridad Económica en la Era Digital',
    metaDescription: 'Alianza Mercosur x TNO Network: circuito de nicho en economía/emprendimiento con alcance acumulado multiplataforma, micros semanales en estudio, cintillo bursátil en Noticias TNO, Digest quincenal SEO y banners estratégicos. $400/mes.',
    ogImage: '/images/Logo-Naranja-mercosur.png',
    content: {
      hero: {
        overline: 'Propuesta Estratégica Mensual',
        title: 'Mercosur Casa de Bolsa x TNO Network',
        subtitle: 'Liderazgo Financiero y Autoridad Económica en la Era Digital',
        image: '/images/Logo-Naranja-mercosur.png',
        imageAlt: 'Logo Mercosur Casa de Bolsa',
        analysisTitle: '1. Resumen Ejecutivo',
        analysisText: 'Transformamos la capacidad analítica de Mercosur Casa de Bolsa en una operación mensual enfocada en educación financiera, presencia estratégica en programas de economía y emprendimiento, y permanencia editorial SEO para sostener visibilidad de marca.',
      },
      tnoEcosystem: {
        title: 'El ecosistema TNO',
        description:
          'Somos un ecosistema de comunicación integral que no se detiene. Unimos la inmediatez con la profundidad a través de tres ejes:',
        highlights: [
          {
            value: '30.000+',
            label: 'Conexiones mensuales al streaming de TNO Network.',
          },
          {
            value: '300.000+',
            label: 'Alcance promedio en Instagram (últimos 3 meses).',
          },
          {
            value: '52.700',
            label: 'Seguidores actuales en Instagram.',
          },
          {
            value: '430.000+',
            label: 'Alcance en Instagram durante mayo.',
          },
        ],
      },
      productsExplained: {
        overline: 'Ejecución',
        title: '2. Estructura de la Alianza Mensual',
        itemsLayout: 'fullRow',
        description: 'Cada producto cumple una función estratégica dentro del embudo de visibilidad, confianza y conversión: presencia diaria, autoridad editorial y cercanía con el público inversionista.',
        items: [
          {
            id: 'produccion-especializada',
            title: 'Producción de Contenido Especializado (Sede + micros especializados)',
            icon: 'ugc',
            badge: 'Producción mensual',
            summary: 'Micros semanales de 3 minutos grabados en estudio TNO para convertir indicadores y análisis técnico en cápsulas claras y accionables.',
            topics: [
              'Micros de 3 minutos con formato educativo y enfoque práctico para audiencia financiera.',
              'Se graba 1 micro por semana con indicadores y contexto de mercado.',
              'Cada pieza rueda la semana siguiente en programación, redes y distribución del ecosistema TNO.',
              'Producción en estudio TNO para mantener consistencia visual, ritmo y claridad editorial.',
              'Contenido atemporal de valor: útil más allá de la coyuntura diaria.',
            ],
          },
          {
            id: 'programas-economia-emprendimiento',
            title: 'Programación sugerida: Economía y Emprendimiento',
            icon: 'event',
            badge: 'Inventario de emisión',
            summary: 'Los spots y micros se distribuyen antes o después de cada programa dentro de un circuito de nicho, para vender efectividad por alcance acumulado y no por una sola hora en vivo.',
            topics: [
              'TNO Business: audiencia de tomadores de decisión, marca y negocios.',
              'Franquicias en Venezuela: enfoque en inversión, expansión y modelos escalables.',
              'Marketing en Movimiento: contexto de marca, crecimiento y mercado emprendedor.',
              'Noticias TNO (bloques de negocios y economía): ventana informativa de alta credibilidad.',
              'Enfoque comercial: el vivo genera contenido; la grilla, las reposiciones y las píldoras en redes generan volumen acumulado de impacto.',
            ],
          },
          {
            id: 'rotativa-radio-visual',
            title: 'Rotativa Radio Visual 24/7',
            icon: 'broadcast',
            badge: 'Señal continua',
            summary: '12 impactos diarios en señal multiplataforma para mantener presencia permanente de marca y mensajes clave.',
            topics: [
              '360 impactos mensuales con enfoque en educación financiera e inversión responsable.',
              'Alta frecuencia para mantener top of mind en audiencias interesadas en economía.',
              'Mensajes adaptados a coyuntura, oportunidades y llamados a la acción.',
            ],
          },
          {
            id: 'dominio-seo',
            title: 'Dominio SEO en TNO Digest',
            icon: 'seo',
            badge: 'Autoridad editorial',
            summary: 'Dos artículos mensuales (cada 15 días) en TNO Digest para sostener autoridad en buscadores con contenido de alto valor.',
            topics: [
              'Mercosur comparte el contenido base y TNO lo adapta/edita con estructura SEO.',
              'Optimización para keywords de intención real: inversión, bolsa y finanzas personales.',
              'Publicación quincenal para construir presencia sostenida y autoridad de marca.',
            ],
          },
          {
            id: 'segmento-noticiero',
            title: 'Cintillo Bursátil en Noticias TNO (Sol Vargas)',
            icon: 'broadcast',
            badge: 'Difusión semanal',
            summary: 'Cintillo bursátil semanal dentro de Noticias TNO, narrado por Sol Vargas, con el mensaje o dato de mercado que Mercosur quiera destacar esa semana.',
            topics: [
              '1 cintillo bursátil semanal en la señal de Noticias TNO.',
              'Formato ágil tipo ticker: coyuntura, oportunidad o alerta de mercado en segundos de alto impacto.',
              'Contenido ad hoc según la prioridad comercial o comunicacional de Mercosur cada semana.',
              'Voz de Sol Vargas para reforzar credibilidad periodística y claridad del mensaje.',
              'Presencia recurrente de marca en el espacio informativo de mayor alcance del ecosistema TNO.',
            ],
          },
          {
            id: 'retargeting-rebranding-redes',
            title: 'Retargeting y Rebranding en Redes Sociales',
            icon: 'ugc',
            badge: 'Social amplification',
            summary: 'La marca aparece en redes sociales como insert dentro de los clips que se generen cada semana, reforzando recordación y frecuencia digital.',
            topics: [
              '3 inserts semanales en redes sociales a partir de los contenidos del plan.',
              'Se extraen clips de los micros para convertirlos en piezas sociales de alto consumo.',
              'Retargeting orgánico con repetición del mensaje en formatos cortos.',
              'Rebranding constante para mantener top of mind en audiencia digital.',
            ],
          },
          {
            id: 'autoridad-web',
            title: 'Autoridad Web y Banners',
            icon: 'web',
            badge: 'Presencia digital',
            summary: 'Visibilidad de banners en programas de interés, home, sección de negocios de TNO y TNO Digest para reforzar recordación y conversión.',
            topics: [
              'Banners en los programas de economía y emprendimiento definidos en la pauta mensual.',
              'Presencia fija en home para alcance transversal del ecosistema.',
              'Banner en la sección de negocios de TNO para audiencia de perfil empresarial.',
              'Ubicación en TNO Digest para captar audiencia de lectura y búsqueda activa.',
            ],
          },
        ],
      },
      products: [
        {
          id: 'mercosur-produccion',
          title: 'Producción de Contenido Especializado',
          product: 'Micros semanales de 3 minutos, grabados en estudio TNO, con rotación la semana siguiente.',
          investment: '$120',
          impacts: [
            'Producción de 1 micro semanal con indicadores y lectura de mercado.',
            'Formato de 3 minutos con narrativa clara y enfoque práctico.',
            'Distribución en la semana siguiente para mantener continuidad editorial.',
            'Contenido atemporal con valor pedagógico y de posicionamiento.',
          ],
        },
        {
          id: 'mercosur-rotativa',
          title: 'Rotativa Radio Visual 24/7',
          product: '12 impactos diarios (360 al mes) en señal multiplataforma de TNO Network.',
          investment: '$100',
          impacts: [
            'Presencia constante frente a audiencias de negocios y emprendimiento.',
            'Top of mind sostenido para educación financiera y oportunidades de inversión.',
            'Base de alcance acumulado mensual con retransmisiones en grilla automatizada.',
            'Retargeting natural del mensaje con repetición inteligente a lo largo del mes.',
          ],
        },
        {
          id: 'mercosur-seo',
          title: 'Dominio SEO (TNO Digest)',
          product: '2 artículos mensuales (cada 15 días), con contenido de Mercosur y edición SEO por TNO.',
          investment: '$80',
          impacts: [
            'Optimización editorial SEO para mejorar indexación y relevancia.',
            'Posicionamiento orgánico en consultas de inversión y mercado bursátil.',
            'Piezas quincenales de autoridad para tráfico de mediano y largo plazo.',
          ],
        },
        {
          id: 'mercosur-web',
          title: 'Autoridad Web y Banners',
          product: 'Banners en programas de interés, home, sección de negocios de TNO y TNO Digest.',
          investment: '$40',
          impacts: [
            'Cobertura en espacios de economía y emprendimiento definidos en pauta.',
            'Visibilidad transversal en home para mayor alcance total.',
            'Presencia en sección de negocios de TNO para tráfico de perfil corporativo.',
            'Presencia en Digest para reforzar conversión de audiencia de lectura.',
          ],
        },
        {
          id: 'mercosur-noticiero',
          title: 'Cintillo Bursátil en Noticias TNO (Sol Vargas)',
          product: '1 cintillo bursátil semanal en Noticias TNO, narrado por Sol Vargas, con mensaje ad hoc según lo que Mercosur quiera impulsar.',
          investment: '$60',
          impacts: [
            'Ticker financiero semanal con lectura clara y ritmo noticioso.',
            'Mercosur visible en el bloque informativo de mayor credibilidad de TNO.',
            'Flexibilidad semanal para destacar coyuntura, oportunidad o mensaje institucional.',
            'Refuerzo de autoridad bursátil con la voz de Sol Vargas dentro del alcance acumulado del ecosistema.',
          ],
        },
        {
          id: 'mercosur-redes-retargeting',
          title: 'Retargeting y Rebranding en Redes Sociales',
          product: '3 inserts semanales en redes, derivados de clips extraídos de los micros y contenidos del plan.',
          investment: 'Incluido',
          impacts: [
            'Presencia recurrente de marca en piezas sociales de alto consumo.',
            'Recorte de clips desde los micros para ampliar distribución digital.',
            'Refuerzo de recuerdo de marca con frecuencia semanal sostenida.',
            'Mayor continuidad entre streaming, noticiero y redes sociales.',
          ],
        },
      ],
      valueAdd: {
        layout: 'features',
        overline: 'Diferenciales',
        title: '3. Valor Diferencial: SEO + micros especializados para Liderazgo de Opinión',
        items: [
          {
            title: 'Autoridad en Google',
            description: 'A diferencia de la pauta efímera, los artículos SEO crean presencia permanente en búsquedas sobre bolsa, inversión y economía, elevando la visibilidad orgánica de Mercosur.',
            icon: 'chart',
          },
          {
            title: 'Conexión humana y confianza',
            description: 'Los micros especializados de contenido de valor permiten que voceros y analistas hablen de forma cercana, traduciendo lenguaje técnico a mensajes claros que facilitan la toma de decisiones.',
            icon: 'handshake',
          },
          {
            title: 'Ecosistema de impacto',
            description: 'Combinamos señal en vivo, redes, portal y contenido editorial para vender alcance acumulado mensual con lógica multiplataforma, no una exposición aislada.',
            icon: 'network',
          },
          {
            title: 'Cintillo bursátil con Sol Vargas',
            description: 'El cintillo semanal en Noticias TNO, narrado por Sol Vargas, coloca a Mercosur en el pulso del mercado con formato ticker: breve, contundente y con el respaldo periodístico que la audiencia ya confía.',
            icon: 'shield',
          },
        ],
      },
      metricsSection: {
        overline: 'Data',
        title: '4. Estadísticas',
        description:
          'Estadísticas promedio desde febrero de 2026, cuando se lanzó la nueva plataforma de TNO Network. Incluye usuarios en streaming por mes en los programas del circuito de economía y emprendimiento —incluido Noticias TNO, con la autoridad de Sol Vargas en señal TNO Network— y alcance en redes para sustentar la estrategia de nicho.',
      },
      metrics: [
        {
          value: '600',
          label: 'TNO Business',
          subtitle: 'Usuarios en streaming por mes.',
          secondaryValue: '430000',
          secondarySuffix: '+',
          secondaryLabel: 'Alcance en la cuenta de TNO Network.',
          mediaKitSlug: 'tnobusiness',
          linkLabel: 'Ver media kit',
          icon: 'store',
          suffix: '',
        },
        {
          value: '2500',
          label: 'Franquicias en Venezuela',
          subtitle: 'Usuarios en streaming por mes.',
          secondaryValue: '61000',
          secondarySuffix: '+',
          secondaryLabel: 'Alcance en Instagram.',
          mediaKitSlug: 'franquiciasenvenezuela',
          linkLabel: 'Ver media kit',
          icon: 'mic',
          suffix: '',
        },
        {
          value: '1300',
          label: 'Noticias TNO',
          subtitle:
            'Programa nuevo en la plataforma (promedio desde febrero 2026). Usuarios en streaming por mes en bloques de negocios y economía.',
          suffix: '',
          secondaryValue: '430000',
          secondarySuffix: '+',
          secondaryLabel:
            'Sale por TNO Network con la autoridad periodística de Sol Vargas: alcance acumulado en la cuenta oficial del ecosistema.',
          mediaKitSlug: 'noticias',
          linkLabel: 'Ver media kit',
          icon: 'event',
        },
      ],
      investmentSummary: {
        items: [
          { product: 'Producción de Contenido Especializado', detail: 'Micros semanales de 3 minutos en estudio TNO (1 por semana)', investment: '$120' },
          { product: 'Rotativa Radio Visual 24/7', detail: '12 impactos diarios (360 al mes)', investment: '$100' },
          { product: 'Dominio SEO (TNO Digest)', detail: '2 artículos mensuales (cada 15 días) con contenido de Mercosur + edición SEO TNO', investment: '$80' },
          { product: 'Autoridad Web y Banners', detail: 'Banners en programas de interés + home + sección de negocios TNO + TNO Digest', investment: '$40' },
          { product: 'Cintillo Bursátil en Noticias TNO (Sol Vargas)', detail: '1 cintillo bursátil semanal, narrado por Sol Vargas, con mensaje ad hoc', investment: '$60' },
          { product: 'Retargeting y Rebranding en Redes', detail: '3 inserts semanales con clips extraídos de los micros para redes sociales', investment: 'Incluido' },
        ],
        totalLabel: 'Total mensual',
        totalValue: '$400',
      },
      areYouReady: {
        title: 'Activemos Mercosur x TNO',
        description: 'Esta alianza busca consolidar a Mercosur Casa de Bolsa como voz líder en economía y mercado de valores, con una estrategia de contenido sostenible, medible y orientada a resultados.',
        ctaText: 'Quiero activar esta propuesta',
        whatsappNumber: '04123964576',
        footerText: '© TNO Network 2026 | Mercosur Casa de Bolsa x TNO Network | The Content Evolution.',
      },
    },
    isActive: true,
    order: 11.75,
  },
  {
    type: 'client',
    slug: 'mercosur-podcast',
    name: 'Mercosur Casa de Bolsa · Producción de Podcast',
    metaTitle: 'Propuesta 2 | Producción de Podcast | Mercosur Casa de Bolsa × TNO Media Lab',
    metaDescription:
      'TNO Media Lab produce el podcast de Mercosur: paquete gráfico, asesoría con Margarita Toledo y Yelitza Yánez, 4 episodios de 45 min al mes, 12 clips por episodio, manejo del canal de YouTube, campañas de promoción y empaquetado listo para distribución.',
    ogImage: '/images/Logo-Naranja-mercosur.png',
    content: {
      hero: {
        overline: 'Propuesta 2 · Casa productora',
        title: 'Mercosur Casa de Bolsa × TNO Media Lab',
        subtitle: 'Producción integral de podcast: de la idea al empaquetado listo para que ustedes distribuyan',
        image: '/images/Logo-Naranja-mercosur.png',
        imageAlt: 'Logo Mercosur Casa de Bolsa',
        analysisTitle: '1. Resumen ejecutivo',
        analysisText:
          'Mercosur Casa de Bolsa confía en TNO Media Lab como casa productora. Margarita Toledo y Yelitza Yánez lideran la producción con entrega mensual de cuatro programas de 45 minutos —cada uno con preproducción, producción y postproducción—, doce clips para redes por episodio, manejo operativo del canal de YouTube, campañas de promoción multicanal y la “lata” final lista para publicar en todas las plataformas de Mercosur.',
      },
      tnoEcosystem: {
        title: 'TNO Media Lab: su partner de producción',
        subtitle: 'Infraestructura broadcast + talento senior',
        description:
          'El Media Lab es el brazo de producción avanzada de TNO Network: estudios, ingeniería de sonido e imagen, flujos de postproducción y formación continua para creadores. Para Mercosur, eso significa un solo interlocutor que asume el proyecto de punta a punta y devuelve contenido profesional empaquetado, listo para publicar.',
        axes: [
          {
            title: 'Preproducción',
            text: 'Guion, orden del día, invitados y lineamientos editoriales acordados con Mercosur.',
          },
          {
            title: 'Producción',
            text: 'Grabación en estudio Media Lab con estándar broadcast y conducción experta.',
          },
          {
            title: 'Postproducción',
            text: 'Edición, mezcla, master y piezas derivadas para redes y promoción.',
          },
        ],
        highlights: [
          { value: '4', label: 'Episodios mensuales de 45 minutos.' },
          { value: '12', label: 'Clips para redes por cada episodio (48 al mes).' },
          { value: '2', label: 'Productoras senior: Margarita Toledo y Yelitza Yánez.' },
          {
            value: '1',
            label: 'Plan de promoción',
            subtitle: 'Campaña mensual multicanal + impulso cruzado con clips y YouTube.',
          },
        ],
      },
      productsExplained: {
        overline: 'Alcance mensual',
        title: '2. Qué incluye el plan de producción',
        itemsLayout: 'fullRow',
        description:
          'El plan arranca con identidad y arquitectura del show (fase de arranque: paquete gráfico + asesoría con Margarita y Yeyé, $200 USD total); luego opera en ciclo mensual de cuatro programas, cada uno con las tres fases de producción, clips, manejo de YouTube y promoción activa del podcast.',
        initialPhaseTotal: '$200 USD',
        items: [
          {
            id: 'mercosur-podcast-grafico',
            title: 'Paquete gráfico del podcast',
            icon: 'web',
            badge: 'Fase de arranque',
            investment: '$200 USD total (con asesoría)',
            summary:
              'Identidad visual del programa: arte de portada, plantillas para redes, lower thirds y elementos gráficos coherentes con la marca Mercosur.',
            topics: [
              'Diseño de cover / miniatura para plataformas de audio y video.',
              'Plantillas para publicaciones en Instagram, LinkedIn y otras redes.',
              'Manual breve de uso de marca en el podcast.',
              'Base gráfica reutilizable en campañas de promoción.',
            ],
          },
          {
            id: 'mercosur-podcast-asesoria',
            title: 'Asesoría y montaje de proyecto',
            icon: 'mic',
            badge: 'Margarita y Yeyé',
            investment: 'Incluido en fase de arranque ($200 USD)',
            summary:
              'Sesiones de trabajo con Margarita Toledo y Yelitza Yánez para definir formato, tono, estructura de episodios, voceros y calendario editorial.',
            topics: [
              'Definición de propuesta de valor y público objetivo (inversionistas, emprendedores, mercado local).',
              'Arquitectura del show: bloques, duración, invitados y llamados a la acción.',
              'Guía de conducción y lineamientos de lenguaje financiero claro.',
              'Calendario mensual de grabación y entregables.',
            ],
          },
          {
            id: 'mercosur-podcast-pre',
            title: 'Preproducción (×4 programas al mes)',
            icon: 'event',
            badge: 'Por episodio',
            summary:
              'Por cada uno de los cuatro programas mensuales: investigación, guion o orden del día, coordinación de invitados y checklist técnico previo a grabación.',
            topics: [
              'Brief y temario acordado con Mercosur.',
              'Coordinación de invitados y materiales de apoyo.',
              'Guion u orden del día para episodio de ~45 minutos.',
              'Preparación de estudio y equipos en Media Lab.',
            ],
          },
          {
            id: 'mercosur-podcast-prod',
            title: 'Producción (×4 programas al mes)',
            icon: 'ugc',
            badge: '45 min · estudio',
            summary:
              'Grabación en TNO Media Lab de cuatro episodios de 45 minutos con producción técnica profesional y conducción a cargo del equipo liderado por Margarita y Yelitza.',
            topics: [
              '4 jornadas de grabación mensuales en estudio.',
              'Episodios de 45 minutos de duración final editada.',
              'Captura multicámara / audio broadcast según formato acordado.',
              'Respaldo de archivos maestros para postproducción.',
            ],
          },
          {
            id: 'mercosur-podcast-post',
            title: 'Postproducción (×4 programas al mes)',
            icon: 'broadcast',
            badge: 'Entrega master',
            summary:
              'Edición, mezcla de audio, normalización, corrección y exportación del episodio final en formatos listos para Spotify, YouTube, Apple Podcasts y web de Mercosur.',
            topics: [
              'Edición narrativa y eliminación de tiempos muertos.',
              'Mezcla, master y metadatos del episodio.',
              'Versiones para audio puro y video (si aplica).',
              'Archivo fuente archivado para continuidad del proyecto.',
            ],
          },
          {
            id: 'mercosur-podcast-clips',
            title: 'Clips para redes sociales',
            icon: 'ugc',
            badge: '12 por episodio',
            summary:
              'Doce piezas cortas por episodio (48 al mes): momentos clave, tips de mercado, citas de invitados y ganchos para Instagram Reels, TikTok, LinkedIn y X.',
            topics: [
              '12 clips editados y subtitulados por cada episodio de 45 min.',
              'Formatos vertical y cuadrado según red destino.',
              'Branding Mercosur en cada pieza.',
              'Entrega organizada por episodio para calendario de publicación de Mercosur.',
            ],
          },
          {
            id: 'mercosur-podcast-youtube',
            title: 'Manejo del canal de YouTube',
            icon: 'mic',
            badge: 'Plataforma principal',
            summary:
              'Operación mensual del canal de YouTube de Mercosur para el podcast: publicación de episodios completos, Shorts derivados de los clips, optimización de títulos, descripciones, miniaturas y playlists.',
            topics: [
              'Subida programada de los 4 episodios mensuales (video o audio con imagen de marca).',
              'Publicación de Shorts y piezas cortas a partir de los 12 clips por episodio.',
              'Optimización SEO en YouTube: títulos, descripciones, etiquetas y capítulos.',
              'Organización en playlists (por temática, serie o campaña comercial).',
              'Lineamientos de Community Tab y respuestas sugeridas para engagement.',
            ],
          },
          {
            id: 'mercosur-podcast-promo',
            title: 'Campañas de promoción',
            icon: 'broadcast',
            badge: 'Difusión multicanal',
            summary:
              'Estrategia y ejecución de promoción del podcast en YouTube, redes sociales y touchpoints de Mercosur: lanzamiento, estreno de cada episodio y refuerzo de recordación de marca.',
            topics: [
              'Plan de promoción mensual alineado al calendario de grabación y publicación.',
              'Creatividades de lanzamiento, avances y estreno de cada episodio.',
              'Copies y calendario sugerido para Instagram, LinkedIn, X, newsletter y web.',
              'Cruces con campañas comerciales de Mercosur Casa de Bolsa.',
              'Impulso cruzado: clips en redes → tráfico al canal de YouTube y viceversa.',
            ],
          },
          {
            id: 'mercosur-podcast-empaquetado',
            title: 'Empaquetado “lata” para distribución',
            icon: 'store',
            badge: 'Listo para publicar',
            summary:
              'Entrega consolidada por episodio: master final, clips, assets gráficos y nomenclatura estándar para que el equipo de Mercosur suba y distribuya sin fricción.',
            topics: [
              'Carpeta por episodio con master, clips y gráficos.',
              'Nombres de archivo y metadatos acordados.',
              'Checklist de publicación (YouTube, Spotify, Apple Podcasts, redes y web).',
              'Coordinación con el manejo del canal de YouTube y el plan de promoción mensual.',
            ],
          },
        ],
      },
      products: [
        {
          id: 'mercosur-podcast-p0-grafico',
          title: 'Paquete gráfico',
          product: 'Identidad visual del podcast: cover, plantillas RRSS y elementos de marca.',
          investment: '$200 USD*',
          impacts: [
            'Coherencia visual con Mercosur Casa de Bolsa.',
            'Materiales reutilizables en promoción y clips.',
          ],
        },
        {
          id: 'mercosur-podcast-p0-asesoria',
          title: 'Asesoría y montaje de proyecto',
          product: 'Trabajo con Margarita Toledo y Yelitza Yánez: formato, tono, calendario y guía editorial.',
          investment: 'Incluido*',
          impacts: [
            'Show profesional desde el primer episodio.',
            'Alineación con objetivos comerciales y educativos de Mercosur.',
          ],
        },
        {
          id: 'mercosur-podcast-p1-mensual',
          title: 'Producción mensual (4 programas)',
          product:
            'Por mes: 4 episodios de 45 min con preproducción, producción en Media Lab y postproducción (master listo).',
          investment: '$200 USD/mes',
          impacts: [
            '4 episodios completos entregados cada mes.',
            'Flujo predecible: grabación, edición y entrega en ventanas acordadas.',
            'Continuidad editorial en educación financiera y mercado de valores.',
          ],
        },
        {
          id: 'mercosur-podcast-p2-clips',
          title: 'Clips para redes sociales',
          product: '12 clips por episodio (48 clips mensuales), editados y con marca Mercosur.',
          investment: 'Incluido',
          impacts: [
            'Multiplicador de alcance en Instagram, TikTok, LinkedIn y X.',
            'Contenido de alto consumo sin grabaciones adicionales.',
          ],
        },
        {
          id: 'mercosur-podcast-p3-youtube',
          title: 'Manejo del canal de YouTube',
          product:
            'Publicación de 4 episodios al mes, Shorts desde clips, SEO en canal, playlists y soporte de Community Tab.',
          investment: 'Incluido',
          impacts: [
            'Canal activo y profesional sin carga operativa interna para Mercosur.',
            'Mayor descubrimiento orgánico en la segunda plataforma de búsqueda del mundo.',
          ],
        },
        {
          id: 'mercosur-podcast-p4-promo',
          title: 'Campañas de promoción',
          product:
            'Plan mensual multicanal: creatividades, copies, calendario de estreno y refuerzo en redes + YouTube.',
          investment: 'Incluido',
          impacts: [
            'Cada episodio llega con impulso coordinado, no solo con la subida del archivo.',
            'Refuerzo de recordación de marca Mercosur en educación financiera.',
          ],
        },
        {
          id: 'mercosur-podcast-p5-lata',
          title: 'Empaquetado para distribución',
          product: '“Lata” por episodio: master + clips + assets listos para que Mercosur publique.',
          investment: 'Incluido',
          impacts: [
            'Mercosur controla dónde y cuándo publicar.',
            'TNO Media Lab entrega producción llave en mano.',
          ],
        },
      ],
      valueAdd: {
        layout: 'features',
        overline: 'Por qué Media Lab',
        title: '3. Valor diferencial para Mercosur',
        items: [
          {
            title: 'Un activo de marca propio',
            description:
              'Mercosur recibe un podcast profesional, reproducible en cualquier plataforma y reutilizable en campañas comerciales y educación financiera.',
            icon: 'network',
          },
          {
            title: 'Margarita Toledo y Yelitza Yánez',
            description:
              'Dos voces con trayectoria en comunicación y producción TNO lideran asesoría, conducción y estándar de calidad del proyecto.',
            icon: 'handshake',
          },
          {
            title: '48 piezas sociales al mes',
            description:
              'Doce clips por episodio convierten cada grabación de 45 minutos en contenido de alto volumen para redes, sin producciones paralelas.',
            icon: 'chart',
          },
          {
            title: 'YouTube + promoción incluidos',
            description:
              'No solo entregamos archivos: gestionamos el canal de YouTube y ejecutamos campañas de promoción para que cada episodio tenga visibilidad desde el día uno.',
            icon: 'shield',
          },
        ],
      },
      metricsSection: {
        overline: 'Volumen mensual',
        title: '4. Entregables en números',
        description:
          'Referencia de producción acordada para el podcast de Mercosur Casa de Bolsa bajo operación mensual del TNO Media Lab.',
      },
      metrics: [
        {
          value: '4',
          label: 'Episodios al mes',
          subtitle: '45 minutos cada uno, con las tres fases de producción.',
        },
        {
          value: '48',
          label: 'Clips para redes',
          subtitle: '12 piezas cortas por episodio, listas para publicar.',
        },
        {
          value: '3',
          label: 'Fases por programa',
          subtitle: 'Preproducción, producción y postproducción.',
        },
        {
          value: '4',
          label: 'Episodios en YouTube',
          subtitle: 'Publicación mensual en canal gestionado por el Media Lab.',
        },
      ],
      investmentSummary: {
        items: [
          {
            product: 'Paquete gráfico del podcast',
            detail: 'Cover, plantillas RRSS, lower thirds y manual de marca Mercosur',
            investment: 'Incl.',
          },
          {
            product: 'Asesoría y montaje de proyecto',
            detail: 'Margarita Toledo y Yelitza Yánez — formato, tono, calendario y guía editorial',
            investment: 'Incl.',
          },
          {
            product: 'Fase de arranque (total)',
            detail: 'Paquete gráfico + asesoría y montaje — pago único',
            investment: '$200',
          },
          {
            product: 'Producción mensual',
            detail: '4 episodios × 45 min (pre + producción + post en Media Lab)',
            investment: '$200/mes',
          },
          { product: 'Clips para redes', detail: '12 clips por episodio (48 al mes)', investment: 'Incluido' },
          {
            product: 'Manejo de YouTube',
            detail: '4 episodios/mes + Shorts, SEO, playlists y Community Tab',
            investment: 'Incluido',
          },
          {
            product: 'Campañas de promoción',
            detail: 'Plan multicanal mensual: estreno, avances y refuerzo de marca',
            investment: 'Incluido',
          },
          {
            product: 'Empaquetado “lata”',
            detail: 'Master + clips + gráficos + assets para todas las plataformas',
            investment: 'Incluido',
          },
        ],
        monthlyTotalLabel: 'Total mensual',
        monthlyTotalValue: '$200 USD',
        totalLabel: 'Fase de arranque (única vez)',
        totalValue: '$200 USD',
        totalNote:
          'Arranque: paquete gráfico + asesoría (Margarita y Yeyé) por $200 USD, pago único. Mensual: $200 USD/mes incluye producción de 4 episodios, clips, manejo de YouTube, promoción y empaquetado. Propuesta de visibilidad TNO: /mercosur.',
      },
      areYouReady: {
        title: 'Produzcamos el podcast de Mercosur',
        description:
          'Agendemos una sesión con Margarita y Yeyé para definir el arranque: paquete gráfico, montaje del proyecto y calendario del primer mes de cuatro episodios.',
        ctaText: 'Quiero esta propuesta de producción',
        whatsappNumber: '04123964576',
        footerText:
          '© TNO Network 2026 | Mercosur Casa de Bolsa × TNO Media Lab · Propuesta 2 Producción | The Content Evolution.',
      },
    },
    isActive: true,
    order: 11.76,
  },
  {
    type: 'client',
    slug: 'bolsa-valores-descentralizada',
    name: 'Bolsa de Valores Descentralizada',
    metaTitle: 'Producción TNO Media Lab | Bolsa de Valores Descentralizada',
    metaDescription:
      'Producción en TNO Media Lab para la Bolsa de Valores Descentralizada: programa, micros en rotativa, clips para redes, 4 clips promocionales YouTube por episodio y tráfico hacia TNO Network. Bonificación: 4 artículos en TNO Digest.',
    content: {
      hero: {
        showTnoLogo: true,
        skipWhoSection: true,
        overline: 'TNO Media Lab · Propuesta por bloques',
        title: 'Bolsa de Valores Descentralizada',
        subtitle:
          'Arranque con identidad gráfica y operación mensual en TNO: programa, rotativa, clips para redes, 4 clips promocionales en YouTube por episodio y audiencia dirigida a TNO Network. Bonificación: 4 artículos en TNO Digest.',
      },
      tnoEcosystem: {
        title: 'Cuatro bloques, una operación mensual',
        subtitle: 'Producido en TNO Network',
        description:
          'Cada bloque responde a una etapa del recorrido de audiencia: profundidad editorial, recordación en señal, consumo en redes y conversión hacia TNO Network.',
        axes: [
          {
            title: 'Bloque 1 · Programa',
            text: 'Producción del espacio principal en TNO Media Lab: preproducción, grabación y postproducción.',
          },
          {
            title: 'Bloque 2 · Rotativa',
            text: 'Micros en la rotativa 24/7 de TNO Network para presencia constante de marca.',
          },
          {
            title: 'Bloque 3 · Redes',
            text: '12 clips por episodio para Instagram, LinkedIn, TikTok y X — incluidos en el paquete de clips ($50 USD/mes).',
          },
          {
            title: 'Bloque 4 · YouTube promocional',
            text: '4 clips promocionales por episodio (Shorts y avances) — mismo paquete de $50 USD/mes, con CTAs a TNO Network.',
          },
        ],
        highlights: [
          { value: '4', label: 'Bloques integrados en un solo plan mensual.' },
          { value: '24/7', label: 'Rotativa TNO Network para los micros de la bolsa.' },
          { value: '$230', label: 'USD mensuales — producción completa en TNO Media Lab.' },
          {
            value: '→',
            label: 'TNO Network',
            subtitle: 'Plataforma destino: streaming, on-demand y tnonetwork.com.',
          },
        ],
      },
      productsExplained: {
        overline: 'Alcance',
        title: 'Fase inicial y plan mensual',
        description:
          'Primero definimos la identidad visual del proyecto; luego opera el plan mensual de cuatro bloques, producido en TNO Media Lab y publicado en el ecosistema TNO Network.',
        items: [
          {
            id: 'bvd-bloque-0-grafico',
            title: 'Fase inicial · Tapas, logo y paquete gráfico',
            icon: 'web',
            badge: 'Pago único',
            investment: '$120 USD',
            summary:
              'Identidad visual de arranque para el programa y la marca Bolsa de Valores Descentralizada: tapas, logo y materiales gráficos reutilizables en todos los bloques.',
            topics: [
              'Diseño de tapas / covers para podcast, YouTube y plataformas digitales.',
              'Logo y variantes para uso en video, rotativa y redes.',
              'Paquete gráfico: plantillas, lower thirds y piezas para promoción.',
              'Manual breve de uso de marca para el equipo de la bolsa.',
            ],
          },
          {
            id: 'bvd-bloque-1-programa',
            title: 'Bloque 1 · Producción de programa',
            icon: 'mic',
            badge: 'TNO Media Lab',
            investment: '$100 USD/mes',
            summary:
              'Producción del programa de la Bolsa de Valores Descentralizada en estudios TNO: formato acordado, grabación profesional y episodio editado listo para publicar.',
            topics: [
              'Preproducción: temario, guion u orden del día y coordinación de voceros.',
              'Producción en estudio TNO Media Lab (audio/video según formato).',
              'Postproducción: edición, mezcla y master para entrega.',
              'Archivo y nomenclatura listos para publicación en TNO Network (streaming, on-demand y canales digitales de la red).',
            ],
          },
          {
            id: 'bvd-bloque-2-rotativa',
            title: 'Bloque 2 · Micros en rotativa TNO',
            icon: 'broadcast',
            badge: 'Rotativa 24/7',
            investment: '$80 USD/mes',
            summary:
              'Micros de la bolsa integrados en la rotativa de TNO Network: piezas cortas que circulan en la grilla regular de la señal multiplataforma.',
            topics: [
              'Producción de micros con mensajes de educación bursátil y marca BVD.',
              'Inserción en rotativa 24/7 del ecosistema TNO Network.',
              'Recordación constante frente a audiencias de negocios y emprendimiento.',
              'Sincronización con temas del programa y campañas en TNO Network.',
            ],
          },
          {
            id: 'bvd-bloque-3-clips',
            title: 'Bloque 3 · Clips para redes sociales',
            icon: 'ugc',
            badge: '12 por episodio',
            investment: '$50 USD/mes (paquete clips)',
            summary:
              'Por cada episodio: doce clips para redes y cuatro clips promocionales para YouTube, todo bajo un solo paquete de $50 USD/mes — sin costo adicional por clip.',
            topics: [
              '12 clips por episodio: vertical y cuadrado para Instagram, LinkedIn, TikTok y X.',
              'Subtítulos, branding y ganchos listos para publicar.',
              'Calendario sugerido de publicación en redes con enlaces a TNO Network.',
              'Extraídos del programa y de los micros de rotativa.',
            ],
          },
          {
            id: 'bvd-bloque-4-youtube',
            title: 'Bloque 4 · Clips promocionales en YouTube',
            icon: 'broadcast',
            badge: '4 promocionales · por episodio',
            investment: 'Incl. en paquete clips',
            summary:
              'Cuatro clips promocionales por episodio (Shorts y avances del programa) para impulsar audiencia hacia TNO Network — incluidos en el paquete de $50 USD/mes.',
            topics: [
              '4 clips promocionales por episodio: ganchos, avances y llamados a ver el programa en TNO.',
              'Títulos, descripciones y cards con enlace a tnonetwork.com y la grilla / on-demand de TNO Network.',
              'Coordinación con el estreno del episodio en la plataforma y la campaña en redes.',
              'Impulso cruzado: redes → YouTube promocional → TNO Network.',
            ],
          },
        ],
      },
      products: [
        {
          id: 'bvd-p0-grafico',
          title: 'Fase inicial · Tapas, logo y paquete gráfico',
          product: 'Covers, logo, plantillas y elementos gráficos de marca.',
          investment: '$120 USD',
          impacts: [
            'Identidad coherente antes del primer episodio.',
            'Materiales listos para rotativa, redes y YouTube.',
          ],
        },
        {
          id: 'bvd-p1-programa',
          title: 'Bloque 1 · Producción de programa',
          product: 'Programa producido en TNO Media Lab (pre + producción + post).',
          investment: '$100/mes',
          impacts: [
            'Contenido de autoridad con estándar broadcast TNO.',
            'Episodio listo para publicar en TNO Network.',
          ],
        },
        {
          id: 'bvd-p2-rotativa',
          title: 'Bloque 2 · Micros en rotativa',
          product: 'Micros en rotativa 24/7 de TNO Network.',
          investment: '$80/mes',
          impacts: [
            'Presencia en señal sin depender de un solo horario.',
            'Top of mind en audiencia de economía y emprendimiento.',
          ],
        },
        {
          id: 'bvd-p3-clips',
          title: 'Bloques 3 y 4 · Clips redes y YouTube',
          product: '12 clips redes + 4 clips promocionales YouTube por episodio — $50 USD/mes el paquete completo.',
          investment: '$50/mes',
          impacts: [
            '16 piezas por episodio: alcance social y promoción del programa en YouTube hacia TNO Network.',
            'Paquete fijo mensual sin costo por clip individual.',
          ],
        },
      ],
      metricsSection: {
        overline: 'Plan mensual',
        title: 'En números',
        description: 'Operación mensual producida en TNO Media Lab por $230 USD.',
      },
      metrics: [
        {
          value: '4',
          label: 'Bloques de producción',
          subtitle: 'Programa, rotativa, redes y YouTube promocional → TNO Network.',
        },
        {
          value: '$100',
          label: 'Programa en estudio',
          subtitle: 'Producción mensual en TNO Media Lab.',
        },
        {
          value: '12+4',
          label: 'Clips por episodio',
          subtitle: '12 para redes y 4 promocionales YouTube — $50 USD/mes el paquete completo.',
        },
        {
          value: '$230',
          label: 'Inversión mensual',
          subtitle: 'USD — programa $100 + micros $80 + paquete clips $50.',
        },
        {
          value: '1',
          label: 'Plataforma destino',
          subtitle: 'TNO Network — CTAs desde redes y clips promocionales de YouTube.',
        },
        {
          value: '4',
          label: 'Artículos TNO Digest',
          subtitle: 'Bonificación mensual — autoridad SEO y permanencia digital.',
        },
      ],
      bonificacion: {
        overline: 'Bonificación incluida',
        title: '4 artículos mensuales en TNO Digest',
        badge: 'Sin costo adicional',
        highlight: 'Autoridad editorial permanente en tnonetwork.com',
        description:
          'Como bonificación del plan mensual, la Bolsa de Valores Descentralizada recibe cuatro artículos al mes en TNO Digest: contenido optimizado para buscadores que amplifica el mensaje de la bolsa más allá del video y la rotativa.',
        items: [
          '4 publicaciones mensuales en TNO Digest (uno por semana, según calendario editorial).',
          'Redacción y edición SEO a partir de temas, voceros o material de la bolsa.',
          'Presencia en TNO Digest (tnonetwork.com) con enlaces al programa en TNO Network.',
          'Contenido evergreen: sigue posicionando en Google semanas después de publicarse.',
        ],
        investment: 'Incluido en el plan',
      },
      comercializacion: {
        overline: 'Comercialización del programa',
        title: 'Reparto de ingresos por patrocinio',
        badge: 'Opcional · por cliente cerrado',
        highlight: '30% TNO si trae el cliente la bolsa · 50/50 si lo trae la radio',
        description:
          'Si comercializan el programa (venta de espacios o patrocinios sobre el contenido), el reparto de cada ingreso depende de quién originó al cliente:',
        items: [
          'Cliente traído por la Bolsa de Valores Descentralizada: la bolsa paga a TNO Network (la radio) el 30% por cada cliente que cierre.',
          'Cliente traído por TNO Network (la radio): reparto 50% y 50% entre la bolsa y TNO por cada cliente que cierre.',
          'Aplica por patrocinador o anunciante cerrado, sobre el monto neto acordado en el contrato de comercialización.',
          'La producción mensual ($230 USD) es independiente; la comercialización es ingreso adicional opcional.',
        ],
      },
      investmentSummary: {
        items: [
          {
            product: 'Fase inicial · Identidad gráfica',
            detail: 'Tapas, logo y paquete gráfico (pago único)',
            investment: '$120',
          },
          {
            product: 'Bloque 1 · Producción de programa',
            detail: 'Preproducción, grabación y post en TNO Media Lab',
            investment: '$100',
          },
          {
            product: 'Bloque 2 · Micros en rotativa',
            detail: 'Piezas en rotativa 24/7 de TNO Network',
            investment: '$80',
          },
          {
            product: 'Bloques 3 y 4 · Clips redes y YouTube promocional',
            detail: '12 clips redes + 4 clips promocionales YouTube por episodio — paquete mensual',
            investment: '$50',
          },
          {
            product: 'Bonificación · TNO Digest',
            detail: '4 artículos mensuales optimizados SEO en TNO Digest',
            investment: 'Incluido',
          },
          {
            product: 'Comercialización del programa',
            detail: 'Cliente de la bolsa: 30% TNO · Cliente de la radio: 50/50',
            investment: 'Por cliente',
          },
        ],
        monthlyTotalLabel: 'Total mensual',
        monthlyTotalValue: '$230 USD',
        totalLabel: 'Fase inicial (única vez)',
        totalValue: '$120 USD',
        totalNote:
          'Arranque: tapas, logo y paquete gráfico por $120 USD (pago único). Mensual: programa ($100) + micros ($80) + paquete clips redes y YouTube ($50) = $230 USD/mes por episodio producido. Bonificación: 4 artículos en TNO Digest incluidos. Comercialización opcional: si la bolsa trae al patrocinador, paga 30% a TNO por cliente; si lo trae la radio, 50% y 50%.',
      },
      areYouReady: {
        title: 'Activemos el plan para la Bolsa de Valores Descentralizada',
        description:
          'Coordinemos el paquete gráfico de arranque y el primer mes: programa, micros a rotativa, clips para redes y promoción en YouTube hacia TNO Network.',
        ctaText: 'Quiero activar este plan',
        whatsappNumber: '04123964576',
        footerText:
          '© TNO Network 2026 | Bolsa de Valores Descentralizada × TNO Media Lab | The Content Evolution.',
      },
    },
    isActive: true,
    order: 11.77,
  },
  {
    type: 'client',
    slug: 'andreina-roa',
    name: 'Andreina Roa',
    metaTitle: 'Producción TNO Media Lab | Andreina Roa',
    metaDescription:
      'Producción en TNO Media Lab para Andreina Roa: programa en estudio, clips para redes y YouTube promocional ($50 USD adicionales), publicación en TNO Network. Bonificación: 4 artículos en TNO Digest.',
    content: {
      hero: {
        showTnoLogo: true,
        skipWhoSection: true,
        overline: 'TNO Media Lab · Propuesta por bloques',
        title: 'Andreina Roa',
        subtitle:
          'Arranque con identidad gráfica y operación mensual en TNO: producción del programa, clips para redes y 4 promocionales en YouTube por episodio ($50 USD adicionales), publicación en TNO Network. Bonificación: 4 artículos en TNO Digest.',
      },
      tnoEcosystem: {
        title: 'Tres bloques, una operación mensual',
        subtitle: 'Producido en TNO Network',
        description:
          'Programa en estudio, clips para redes y piezas promocionales en YouTube — todo con destino TNO Network.',
        axes: [
          {
            title: 'Bloque 1 · Programa',
            text: 'Producción en TNO Media Lab: preproducción, grabación y postproducción del episodio.',
          },
          {
            title: 'Bloque 2 · Redes',
            text: '12 clips por episodio para Instagram, LinkedIn, TikTok y X — incluidos en el paquete de clips ($50 USD/mes adicional).',
          },
          {
            title: 'Bloque 3 · YouTube promocional',
            text: '4 clips promocionales por episodio (Shorts y avances) — mismo paquete de $50 USD/mes, con CTAs a TNO Network.',
          },
        ],
        highlights: [
          { value: '3', label: 'Bloques integrados en un solo plan mensual.' },
          { value: '$150', label: 'USD mensuales — programa $100 + clips $50.' },
          {
            value: '→',
            label: 'TNO Network',
            subtitle: 'Plataforma destino: streaming, on-demand y tnonetwork.com.',
          },
        ],
      },
      productsExplained: {
        overline: 'Alcance',
        title: 'Fase inicial y plan mensual',
        description:
          'Primero definimos la identidad visual del proyecto; luego opera el plan mensual de tres bloques, producido en TNO Media Lab y publicado en TNO Network.',
        items: [
          {
            id: 'ar-bloque-0-grafico',
            title: 'Fase inicial · Tapas, logo y paquete gráfico',
            icon: 'web',
            badge: 'Pago único',
            investment: '$120 USD',
            summary:
              'Identidad visual de arranque para el programa y la marca Andreina Roa: tapas, logo y materiales gráficos reutilizables.',
            topics: [
              'Diseño de tapas / covers para podcast, YouTube y plataformas digitales.',
              'Logo y variantes para uso en video y redes.',
              'Paquete gráfico: plantillas, lower thirds y piezas para promoción.',
              'Manual breve de uso de marca para el equipo de Andreina Roa.',
            ],
          },
          {
            id: 'ar-bloque-1-programa',
            title: 'Bloque 1 · Producción de programa',
            icon: 'mic',
            badge: 'TNO Media Lab',
            investment: '$100 USD/mes',
            summary:
              'Producción del programa de Andreina Roa en estudios TNO: formato acordado, grabación profesional y episodio editado listo para publicar en TNO Network.',
            topics: [
              'Preproducción: temario, guion u orden del día y coordinación de voceros.',
              'Producción en estudio TNO Media Lab (audio/video según formato).',
              'Postproducción: edición, mezcla y master para entrega.',
              'Archivo y nomenclatura listos para publicación en TNO Network (streaming, on-demand y canales digitales de la red).',
            ],
          },
          {
            id: 'ar-bloque-2-clips',
            title: 'Bloque 2 · Clips para redes sociales',
            icon: 'ugc',
            badge: '12 por episodio',
            investment: '$50 USD/mes (paquete clips)',
            summary:
              'Por cada episodio: doce clips para redes y cuatro clips promocionales para YouTube, todo bajo un solo paquete adicional de $50 USD/mes — sin costo por clip.',
            topics: [
              '12 clips por episodio: vertical y cuadrado para Instagram, LinkedIn, TikTok y X.',
              'Subtítulos, branding y ganchos listos para publicar.',
              'Calendario sugerido de publicación en redes con enlaces a TNO Network.',
              'Extraídos del programa producido en TNO Media Lab.',
            ],
          },
          {
            id: 'ar-bloque-3-youtube',
            title: 'Bloque 3 · Clips promocionales en YouTube',
            icon: 'broadcast',
            badge: '4 promocionales · por episodio',
            investment: 'Incl. en paquete clips',
            summary:
              'Cuatro clips promocionales por episodio (Shorts y avances del programa) para impulsar audiencia hacia TNO Network — incluidos en el paquete adicional de $50 USD/mes.',
            topics: [
              '4 clips promocionales por episodio: ganchos, avances y llamados a ver el programa en TNO.',
              'Títulos, descripciones y cards con enlace a tnonetwork.com y la grilla / on-demand de TNO Network.',
              'Coordinación con el estreno del episodio en la plataforma y la campaña en redes.',
              'Impulso cruzado: redes → YouTube promocional → TNO Network.',
            ],
          },
        ],
      },
      products: [
        {
          id: 'ar-p0-grafico',
          title: 'Fase inicial · Tapas, logo y paquete gráfico',
          product: 'Covers, logo, plantillas y elementos gráficos de marca.',
          investment: '$120 USD',
          impacts: [
            'Identidad coherente antes del primer episodio.',
            'Materiales listos para redes y YouTube.',
          ],
        },
        {
          id: 'ar-p1-programa',
          title: 'Bloque 1 · Producción de programa',
          product: 'Programa producido en TNO Media Lab (pre + producción + post).',
          investment: '$100/mes',
          impacts: [
            'Contenido de autoridad con estándar broadcast TNO.',
            'Episodio listo para publicar en TNO Network.',
          ],
        },
        {
          id: 'ar-p2-clips',
          title: 'Bloques 2 y 3 · Clips redes y YouTube',
          product: '12 clips redes + 4 clips promocionales YouTube por episodio — $50 USD/mes adicional.',
          investment: '$50/mes',
          impacts: [
            '16 piezas por episodio: alcance social y promoción del programa en YouTube hacia TNO Network.',
            'Paquete fijo mensual sin costo por clip individual.',
          ],
        },
      ],
      metricsSection: {
        overline: 'Plan mensual',
        title: 'En números',
        description: 'Operación mensual producida en TNO Media Lab por $150 USD.',
      },
      metrics: [
        {
          value: '3',
          label: 'Bloques de producción',
          subtitle: 'Programa, redes y YouTube promocional → TNO Network.',
        },
        {
          value: '$100',
          label: 'Programa en estudio',
          subtitle: 'Producción mensual en TNO Media Lab.',
        },
        {
          value: '12+4',
          label: 'Clips por episodio',
          subtitle: '12 para redes y 4 promocionales YouTube — $50 USD/mes adicional.',
        },
        {
          value: '$150',
          label: 'Inversión mensual',
          subtitle: 'USD — programa $100 + paquete clips $50.',
        },
        {
          value: '1',
          label: 'Plataforma destino',
          subtitle: 'TNO Network — CTAs desde redes y clips promocionales de YouTube.',
        },
        {
          value: '4',
          label: 'Artículos TNO Digest',
          subtitle: 'Bonificación mensual — autoridad SEO y permanencia digital.',
        },
      ],
      bonificacion: {
        overline: 'Bonificación incluida',
        title: '4 artículos mensuales en TNO Digest',
        badge: 'Sin costo adicional',
        highlight: 'Autoridad editorial permanente en tnonetwork.com',
        description:
          'Como bonificación del plan mensual, Andreina Roa recibe cuatro artículos al mes en TNO Digest: contenido optimizado para buscadores que amplifica su mensaje más allá del video.',
        items: [
          '4 publicaciones mensuales en TNO Digest (uno por semana, según calendario editorial).',
          'Redacción y edición SEO a partir de temas, voceros o material del programa.',
          'Presencia en TNO Digest (tnonetwork.com) con enlaces al programa en TNO Network.',
          'Contenido evergreen: sigue posicionando en Google semanas después de publicarse.',
        ],
        investment: 'Incluido en el plan',
      },
      comercializacion: {
        overline: 'Comercialización del programa',
        title: 'Reparto de ingresos por patrocinio',
        badge: 'Opcional · por cliente cerrado',
        highlight: '30% TNO si trae el cliente Andreina Roa · 50/50 si lo trae la radio',
        description:
          'Si comercializan el programa (venta de espacios o patrocinios sobre el contenido), el reparto de cada ingreso depende de quién originó al cliente:',
        items: [
          'Cliente traído por Andreina Roa: Andreina paga a TNO Network (la radio) el 30% por cada cliente que cierre.',
          'Cliente traído por TNO Network (la radio): reparto 50% y 50% entre Andreina Roa y TNO por cada cliente que cierre.',
          'Aplica por patrocinador o anunciante cerrado, sobre el monto neto acordado en el contrato de comercialización.',
          'La producción mensual ($150 USD) es independiente; la comercialización es ingreso adicional opcional.',
        ],
      },
      investmentSummary: {
        items: [
          {
            product: 'Fase inicial · Identidad gráfica',
            detail: 'Tapas, logo y paquete gráfico (pago único)',
            investment: '$120',
          },
          {
            product: 'Bloque 1 · Producción de programa',
            detail: 'Preproducción, grabación y post en TNO Media Lab',
            investment: '$100',
          },
          {
            product: 'Bloques 2 y 3 · Clips redes y YouTube promocional',
            detail: '12 clips redes + 4 clips promocionales YouTube por episodio — paquete adicional',
            investment: '$50',
          },
          {
            product: 'Bonificación · TNO Digest',
            detail: '4 artículos mensuales optimizados SEO en TNO Digest',
            investment: 'Incluido',
          },
          {
            product: 'Comercialización del programa',
            detail: 'Cliente de Andreina Roa: 30% TNO · Cliente de la radio: 50/50',
            investment: 'Por cliente',
          },
        ],
        monthlyTotalLabel: 'Total mensual',
        monthlyTotalValue: '$150 USD',
        totalLabel: 'Fase inicial (única vez)',
        totalValue: '$120 USD',
        totalNote:
          'Arranque: tapas, logo y paquete gráfico por $120 USD (pago único). Mensual: programa ($100) + paquete clips redes y YouTube ($50 adicional) = $150 USD/mes por episodio producido. Bonificación: 4 artículos en TNO Digest incluidos. Comercialización opcional: si Andreina Roa trae al patrocinador, paga 30% a TNO por cliente; si lo trae la radio, 50% y 50%.',
      },
      areYouReady: {
        title: 'Activemos el plan para Andreina Roa',
        description:
          'Coordinemos el paquete gráfico de arranque y el primer mes: programa, clips para redes y promoción en YouTube hacia TNO Network.',
        ctaText: 'Quiero activar este plan',
        whatsappNumber: '04123964576',
        footerText:
          '© TNO Network 2026 | Andreina Roa × TNO Media Lab | The Content Evolution.',
      },
    },
    isActive: true,
    order: 11.78,
  },
  {
    type: 'client',
    slug: 'harrison-mozo',
    name: 'Harrison Mozo · @harriviral',
    metaTitle: 'Espacio en TNO Network | Harrison Mozo (@harriviral)',
    metaDescription:
      'Tarifas para el espacio de Harrison Mozo (@harriviral) en TNO Network: programa en Media Lab, clips redes y YouTube promocional ($50 adicional), publicación en TNO Network. Bonificación: 4 artículos en TNO Digest.',
    content: {
      hero: {
        showTnoLogo: true,
        skipWhoSection: true,
        overline: 'TNO Media Lab · Espacio en TNO Network',
        title: 'Harrison Mozo',
        subtitle:
          '@harriviral — tarifas para operar tu programa en TNO Network: producción en Media Lab, clips para redes y 4 promocionales en YouTube por episodio ($50 USD adicionales), publicación en la plataforma TNO. Bonificación: 4 artículos en TNO Digest.',
      },
      tnoEcosystem: {
        title: 'Tu espacio en TNO: tres bloques mensuales',
        subtitle: 'Producido en TNO Network',
        description:
          'Programa propio en la grilla TNO, clips para redes y piezas promocionales en YouTube — con tarifas claras para que @harriviral sostenga su espacio en la plataforma.',
        axes: [
          {
            title: 'Bloque 1 · Programa',
            text: 'Producción en TNO Media Lab: preproducción, grabación y postproducción del episodio para tu espacio en TNO Network.',
          },
          {
            title: 'Bloque 2 · Redes',
            text: '12 clips por episodio para Instagram, TikTok, LinkedIn y X — paquete adicional de $50 USD/mes.',
          },
          {
            title: 'Bloque 3 · YouTube promocional',
            text: '4 clips promocionales por episodio (Shorts y avances) — mismo paquete de $50 USD/mes, con CTAs a tu espacio en TNO Network.',
          },
        ],
        highlights: [
          { value: '@harriviral', label: 'Espacio editorial propio en TNO Network.' },
          { value: '$150', label: 'USD mensuales — programa $100 + clips $50 adicional.' },
          {
            value: '→',
            label: 'TNO Network',
            subtitle: 'Plataforma destino: streaming, on-demand y tnonetwork.com.',
          },
        ],
      },
      productsExplained: {
        overline: 'Tarifas',
        title: 'Fase inicial y plan mensual',
        description:
          'Identidad visual de arranque (pago único) y operación mensual de tres bloques para sostener el espacio de Harrison Mozo en TNO Network.',
        items: [
          {
            id: 'hm-bloque-0-grafico',
            title: 'Fase inicial · Tapas, logo y paquete gráfico',
            icon: 'web',
            badge: 'Pago único',
            investment: '$120 USD',
            summary:
              'Identidad visual de arranque para el programa y la marca @harriviral: tapas, logo y materiales gráficos reutilizables en todos los bloques.',
            topics: [
              'Diseño de tapas / covers para podcast, YouTube y plataformas digitales.',
              'Logo y variantes para uso en video y redes.',
              'Paquete gráfico: plantillas, lower thirds y piezas para promoción.',
              'Manual breve de uso de marca para el equipo de Harrison Mozo.',
            ],
          },
          {
            id: 'hm-bloque-1-programa',
            title: 'Bloque 1 · Producción de programa',
            icon: 'mic',
            badge: 'Tu espacio TNO',
            investment: '$100 USD/mes',
            summary:
              'Producción del programa de Harrison Mozo en estudios TNO: formato acordado, grabación profesional y episodio editado listo para publicar en su espacio en TNO Network.',
            topics: [
              'Preproducción: temario, guion u orden del día y coordinación de invitados.',
              'Producción en estudio TNO Media Lab (audio/video según formato).',
              'Postproducción: edición, mezcla y master para entrega.',
              'Publicación en TNO Network: streaming, on-demand y canales digitales de la red.',
            ],
          },
          {
            id: 'hm-bloque-2-clips',
            title: 'Bloque 2 · Clips para redes sociales',
            icon: 'ugc',
            badge: '12 por episodio',
            investment: '$50 USD/mes (paquete clips)',
            summary:
              'Por cada episodio: doce clips para redes y cuatro clips promocionales para YouTube, bajo un paquete adicional de $50 USD/mes — ideal para amplificar @harriviral hacia TNO Network.',
            topics: [
              '12 clips por episodio: vertical y cuadrado para Instagram, TikTok, LinkedIn y X.',
              'Subtítulos, branding @harriviral y ganchos listos para publicar.',
              'Calendario sugerido de publicación con enlaces al programa en TNO Network.',
              'Extraídos del episodio producido en TNO Media Lab.',
            ],
          },
          {
            id: 'hm-bloque-3-youtube',
            title: 'Bloque 3 · Clips promocionales en YouTube',
            icon: 'broadcast',
            badge: '4 promocionales · por episodio',
            investment: 'Incl. en paquete clips',
            summary:
              'Cuatro clips promocionales por episodio (Shorts y avances) para impulsar audiencia viral hacia el espacio de Harrison en TNO Network — incluidos en el paquete adicional de $50 USD/mes.',
            topics: [
              '4 clips promocionales por episodio: ganchos, avances y llamados a ver el programa en TNO.',
              'Títulos, descripciones y cards con enlace a tnonetwork.com y la grilla / on-demand.',
              'Coordinación con el estreno del episodio y la campaña en redes.',
              'Impulso cruzado: redes → YouTube promocional → espacio TNO de @harriviral.',
            ],
          },
        ],
      },
      products: [
        {
          id: 'hm-p0-grafico',
          title: 'Fase inicial · Tapas, logo y paquete gráfico',
          product: 'Covers, logo, plantillas y elementos gráficos de marca.',
          investment: '$120 USD',
          impacts: [
            'Identidad coherente antes del primer episodio en TNO Network.',
            'Materiales listos para redes y YouTube.',
          ],
        },
        {
          id: 'hm-p1-programa',
          title: 'Bloque 1 · Producción de programa',
          product: 'Programa producido en TNO Media Lab (pre + producción + post).',
          investment: '$100/mes',
          impacts: [
            'Espacio propio en TNO Network con estándar broadcast.',
            'Episodio listo para streaming y on-demand.',
          ],
        },
        {
          id: 'hm-p2-clips',
          title: 'Bloques 2 y 3 · Clips redes y YouTube',
          product: '12 clips redes + 4 clips promocionales YouTube por episodio — $50 USD/mes adicional.',
          investment: '$50/mes',
          impacts: [
            '16 piezas por episodio: viralidad social y promoción hacia TNO Network.',
            'Paquete fijo mensual sin costo por clip individual.',
          ],
        },
      ],
      metricsSection: {
        overline: 'Plan mensual',
        title: 'Tarifas en números',
        description: 'Operación mensual del espacio @harriviral en TNO Media Lab por $150 USD.',
      },
      metrics: [
        {
          value: '3',
          label: 'Bloques de producción',
          subtitle: 'Programa, redes y YouTube promocional → TNO Network.',
        },
        {
          value: '$100',
          label: 'Programa en estudio',
          subtitle: 'Tarifa mensual — producción en TNO Media Lab.',
        },
        {
          value: '12+4',
          label: 'Clips por episodio',
          subtitle: '12 para redes y 4 promocionales YouTube — $50 USD/mes adicional.',
        },
        {
          value: '$150',
          label: 'Inversión mensual',
          subtitle: 'USD — programa $100 + paquete clips $50.',
        },
        {
          value: '1',
          label: 'Espacio en TNO Network',
          subtitle: 'Streaming, on-demand y tnonetwork.com.',
        },
        {
          value: '4',
          label: 'Artículos TNO Digest',
          subtitle: 'Bonificación mensual — autoridad SEO y permanencia digital.',
        },
      ],
      bonificacion: {
        overline: 'Bonificación incluida',
        title: '4 artículos mensuales en TNO Digest',
        badge: 'Sin costo adicional',
        highlight: 'Autoridad editorial permanente en tnonetwork.com',
        description:
          'Como bonificación del plan mensual, Harrison Mozo recibe cuatro artículos al mes en TNO Digest: contenido SEO que amplifica @harriviral más allá del video y refuerza su espacio en TNO Network.',
        items: [
          '4 publicaciones mensuales en TNO Digest (uno por semana, según calendario editorial).',
          'Redacción y edición SEO a partir de temas, invitados o material del programa.',
          'Presencia en tnonetwork.com con enlaces al espacio de Harrison en TNO Network.',
          'Contenido evergreen: sigue posicionando en Google semanas después de publicarse.',
        ],
        investment: 'Incluido en el plan',
      },
      comercializacion: {
        overline: 'Comercialización del espacio',
        title: 'Reparto de ingresos por patrocinio',
        badge: 'Opcional · por cliente cerrado',
        highlight: '30% TNO si trae el cliente Harrison · 50/50 si lo trae la radio',
        description:
          'Si comercializan el espacio (venta de patrocinios sobre el programa), el reparto de cada ingreso depende de quién originó al anunciante:',
        items: [
          'Patrocinador traído por Harrison Mozo (@harriviral): Harrison paga a TNO Network el 30% por cada cliente que cierre.',
          'Patrocinador traído por TNO Network: reparto 50% y 50% entre Harrison y TNO por cada cliente que cierre.',
          'Aplica por patrocinador o anunciante cerrado, sobre el monto neto acordado en el contrato de comercialización.',
          'La producción mensual ($150 USD) es independiente; la comercialización es ingreso adicional opcional.',
        ],
      },
      investmentSummary: {
        items: [
          {
            product: 'Fase inicial · Identidad gráfica',
            detail: 'Tapas, logo y paquete gráfico (pago único)',
            investment: '$120',
          },
          {
            product: 'Bloque 1 · Producción de programa',
            detail: 'Preproducción, grabación y post en TNO Media Lab — espacio TNO Network',
            investment: '$100',
          },
          {
            product: 'Bloques 2 y 3 · Clips redes y YouTube promocional',
            detail: '12 clips redes + 4 clips promocionales YouTube por episodio — paquete adicional',
            investment: '$50',
          },
          {
            product: 'Bonificación · TNO Digest',
            detail: '4 artículos mensuales optimizados SEO en TNO Digest',
            investment: 'Incluido',
          },
          {
            product: 'Comercialización del espacio',
            detail: 'Patrocinador de Harrison: 30% TNO · Patrocinador de la radio: 50/50',
            investment: 'Por cliente',
          },
        ],
        monthlyTotalLabel: 'Total mensual',
        monthlyTotalValue: '$150 USD',
        totalLabel: 'Fase inicial (única vez)',
        totalValue: '$120 USD',
        totalNote:
          'Arranque: tapas, logo y paquete gráfico por $120 USD (pago único). Mensual: programa ($100) + paquete clips redes y YouTube ($50 adicional) = $150 USD/mes por episodio producido. Bonificación: 4 artículos en TNO Digest incluidos. Comercialización opcional: si Harrison trae al patrocinador, paga 30% a TNO; si lo trae la radio, 50% y 50%.',
      },
      areYouReady: {
        title: 'Activemos el espacio de Harrison Mozo en TNO Network',
        description:
          'Coordinemos identidad de arranque y el primer mes: programa, clips para redes y promoción en YouTube hacia tu espacio en TNO.',
        ctaText: 'Quiero activar mi espacio',
        whatsappNumber: '04123964576',
        footerText:
          '© TNO Network 2026 | Harrison Mozo (@harriviral) × TNO Media Lab | The Content Evolution.',
      },
    },
    isActive: true,
    order: 11.79,
  },
  {
    type: 'client',
    slug: 'opticaberlud',
    name: 'Óptica Berlud',
    metaTitle: 'Óptica Berlud × TNO Network | Comercialización 360',
    metaDescription:
      'Propuesta Óptica Berlud × TNO Network: noticiero, rotativa 24/7, TNO Digest, inserts y micros en estudio (Plata/Oro). Bronce $290 · Plata $480 · Oro $720/mes.',
    content: {
      hero: {
        overline: 'Propuesta de Comercialización 360',
        title: 'Óptica Berlud × TNO Network',
        subtitle: 'Tradición, calidad y servicio ante tus ojos',
        analysisTitle: '1. Resumen ejecutivo',
        analysisText:
          'Óptica Berlud se posiciona en el ecosistema TNO Network con noticiero (Noticias TNO), rotativa Radio Visual 24/7, TNO Digest e inserts en redes. En Plata y Oro se incluyen micros en video producidos sin grabar en la óptica: estudio TNO, material gráfico de la marca, piezas animadas, derivados editoriales o entrevista con vocero. Planes Bronce, Plata y Oro escalan rotativas, Digest, noticiero, inserts y micros.',
      },
      tnoEcosystem: {
        title: 'El ecosistema TNO',
        description:
          'Streaming 24/7, On-Demand, portal editorial y redes: un solo aliado para que Berlud esté donde la audiencia informa, recuerda, busca y comparte.',
      },
      productsExplained: {
        overline: 'Inventario',
        title: '2. Productos de la alianza',
        itemsLayout: 'fullRow',
        description:
          'Cada formato cumple una función: el noticiero aporta credibilidad, la rotativa aporta frecuencia, el Digest aporta permanencia en buscadores y los inserts llevan la marca al consumo social.',
        items: [
          {
            id: 'noticiero',
            title: 'Noticias TNO — el noticiero (Sol Vargas)',
            icon: 'broadcast',
            badge: 'Prestigio periodístico',
            summary:
              'Menciones y cintillos semanales de salud visual en el espacio informativo de mayor autoridad del ecosistema TNO.',
            topics: [
              'Plan Bronce: 1 mención mensual informativa.',
              'Planes Plata y Oro: 1 cintillo semanal (4 al mes) con tips de cuidado visual y servicios Berlud.',
              'Plan Oro: bloque mensual «Tu vista, tu bienestar» en antena.',
              'Narración y contexto con Sol Vargas — mensaje en entorno noticioso, no spot aislado.',
            ],
          },
          {
            id: 'rotativa',
            title: 'Rotativa Radio Visual 24/7',
            icon: 'broadcast',
            badge: 'Más rotativas por plan',
            summary: 'Micros en la señal continua de TNO Network. Bronce: 6 impactos/día · Plata y Oro: 12 impactos/día.',
            topics: [
              'Hasta 360 impactos mensuales en Plan Plata y Oro.',
              'Mensajes de salud visual, exámenes, monturas y promociones Berlud.',
              'Recordación sostenida en audio y video durante toda la programación.',
            ],
          },
          {
            id: 'digest',
            title: 'TNO Digest — más artículos SEO',
            icon: 'seo',
            badge: 'Autoridad en Google',
            summary: 'Artículos en tnonetwork.com optimizados para búsquedas de salud visual y óptica.',
            topics: [
              'Bronce: 1 artículo/mes · Plata: 2 · Oro: 4 artículos mensuales.',
              'Temas: prevención, lentes, exámenes, estilo y servicios de la óptica.',
              'Huella digital permanente que sigue generando tráfico orgánico.',
            ],
          },
          {
            id: 'inserts-redes',
            title: 'Inserts en redes',
            icon: 'ugc',
            badge: 'Más inserts por plan',
            summary: 'Logo, claqueta y cintillo en Reels, Shorts y clips del ecosistema TNO.',
            topics: [
              'Bronce: 2 inserts/mes · Plata: 6 · Oro: 12 + difusión en cuentas oficiales TNO.',
              'Piezas reutilizables en Instagram y WhatsApp de Berlud.',
              'Refuerzo visual en el contenido que ya consume la audiencia.',
            ],
          },
          {
            id: 'micros-video',
            title: 'Micros en video (sin ir a la óptica)',
            icon: 'ugc',
            badge: 'Plata y Oro',
            summary:
              'Piezas de 1–3 min producidas por TNO: estudio con presentador, montaje con fotos/catálogo de Berlud, motion, clip desde Digest o entrevista con vocero.',
            topics: [
              'Micro en estudio TNO con tips de salud visual y branding Berlud.',
              'Pieza con material de marca (monturas, promos, logos) que nos compartan.',
              'Motion / plantilla animada con eslogan y llamado a la acción.',
              'Derivado de TNO Digest o programa de salud del ecosistema.',
              'Plan Oro: entrevista con vocero de Berlud por videollamada al set TNO.',
              'Adaptación de reels o videos que ya tenga la marca.',
            ],
          },
          {
            id: 'sintonia-salud',
            title: 'En Sintonía con tu Salud (Plan Oro)',
            icon: 'mic',
            badge: 'Exclusivo Oro',
            summary: 'Bloque mensual con la Dra. Grecia Andreina: prevención y chequeo visual con Berlud como aliado.',
            topics: [
              'Integración en programa de bienestar y salud de TNO Network.',
              'Credibilidad clínica asociada a la marca óptica.',
            ],
          },
        ],
      },
      products: [
        {
          id: 'berlud-bronce',
          title: 'Plan Bronce — $290/mes',
          product: '6 rotativas/día · 1 Digest · 1 mención Noticias TNO · 2 inserts · banner web.',
          investment: '$290',
          impacts: ['Presencia esencial en señal, noticiero y redes.', 'Ideal para iniciar en el ecosistema TNO.'],
        },
        {
          id: 'berlud-plata',
          title: 'Plan Plata — $480/mes',
          product: '12 rotativas/día · 2 Digest · 4 cintillos noticiero · 6 inserts · 2 micros (estudio o material de marca) · web.',
          investment: '$480',
          impacts: ['Top of mind sostenido y noticiero semanal.', 'Video profesional sin grabación en sucursal.'],
        },
        {
          id: 'berlud-oro',
          title: 'Plan Oro — $720/mes',
          product:
            '12 rotativas + On-Demand · 4 Digest · noticiero completo · 12 inserts · 4 micros + vocero · En Sintonía con tu Salud · aliados 24/7.',
          investment: '$720',
          impacts: ['Comercialización 360°.', 'Liderazgo de categoría en salud visual y medios.'],
        },
      ],
      valueAdd: {
        layout: 'features',
        overline: 'Diferenciales',
        title: '3. Por qué esta propuesta es completa',
        items: [
          {
            title: 'Noticiero + rotativa',
            description: 'Credibilidad periodística y frecuencia diaria en un solo plan: Berlud no depende de un solo canal.',
            icon: 'shield',
          },
          {
            title: 'Más Digest, más SEO',
            description: 'Cada plan superior duplica o cuadruplica los artículos mensuales para dominar búsquedas de salud visual.',
            icon: 'chart',
          },
          {
            title: 'Más inserts en redes',
            description: 'De 2 a 12 piezas mensuales con logo TNO: la marca acompaña el clip que ya se comparte.',
            icon: 'network',
          },
          {
            title: 'Micros sin visita a la óptica',
            description:
              'Estudio TNO, material gráfico de la marca, motion, derivados editoriales o vocero por videollamada: video de calidad sin desplazamiento a sucursal.',
            icon: 'handshake',
          },
        ],
      },
      investmentSummary: {
        items: [
          { product: 'Plan Bronce', detail: '6 rotativas/día · 1 Digest · 1 mención noticiero · 2 inserts · web', investment: '$290' },
          { product: 'Plan Plata', detail: '12 rotativas/día · 2 Digest · 4 cintillos · 6 inserts · 2 micros en video', investment: '$480' },
          { product: 'Plan Oro', detail: '4 Digest · 12 inserts · 4 micros + vocero · En Sintonía con tu Salud', investment: '$720' },
        ],
        totalLabel: 'Planes mensuales referenciales (USD)',
        totalValue: 'Bronce $290 · Plata $480 · Oro $720',
      },
      areYouReady: {
        title: 'Activemos Óptica Berlud × TNO',
        description:
          'Noticiero, rotativa, Digest, inserts y micros en estudio. Planes Bronce, Plata y Oro.',
        ctaText: 'Quiero activar esta propuesta',
        whatsappNumber: '04123964576',
        footerText: '© TNO Network 2026 | Óptica Berlud × TNO Network | The Content Evolution.',
      },
    },
    isActive: true,
    order: 11.76,
  },
  {
    type: 'client',
    slug: 'corporacion-tisemte',
    name: 'Corporación Tisemte',
    metaTitle: 'Corporación Tisemte × TNO Network | Comercialización 360',
    metaDescription:
      'TNO Network gestiona la campaña 360 de Corporación Tisemte: noticiero, rotativa con micros, TNO Digest, inserts en clips y TNO Business. Servicio integral, no solo pauta. Bronce €290 · Plata €480 · Oro €720/mes.',
    content: {
      ui: {
        enhanced: true,
        showProgramIndex: true,
        plansLayout: 'horizontal',
        brand: {
          primary: '#E65100',
          accent: '#FFC107',
          ctaSecondary: '#FFC107',
          ctaSecondaryText: '#1a1a1a',
          heroGradient:
            'linear-gradient(145deg, #1a1208 0%, #E65100 38%, #FF8F00 68%, #263238 100%)',
        },
      },
      hero: {
        overline: 'Propuesta de Comercialización 360',
        title: 'Corporación Tisemte × TNO Network',
        subtitle: 'Servicio y mantenimiento de obras civiles · Campaña 360 gestionada por TNO',
        analysisTitle: '1. Resumen ejecutivo',
        analysisText:
          'TNO Network no entrega pautas sueltas: ofrece un servicio 360 que gestiona la campaña completa de Corporación Tisemte — estrategia, calendario editorial, producción de piezas, publicación en señal (rotativa con micros, Noticias TNO), TNO Digest, inserts en clips para redes, micros en video y entrevistas en TNO Business. Tisemte contrata un solo aliado; TNO coordina la ejecución mes a mes. Planes Bronce, Plata y Oro escalan frecuencias y profundidad de la campaña.',
      },
      tnoEcosystem: {
        title: 'El ecosistema TNO',
        description:
          'Somos un ecosistema de comunicación integral que no se detiene. TNO no solo coloca publicidad: diseña, produce y opera la campaña de punta a punta. Unimos la inmediatez con la profundidad a través de tres ejes:',
        highlights: [
          {
            value: '30.000+',
            label: 'Conexiones mensuales al streaming de TNO Network.',
            icon: 'streaming',
          },
          {
            value: '300.000+',
            label: 'Alcance promedio en Instagram (últimos 3 meses).',
            icon: 'reach',
          },
          {
            value: '52.700',
            label: 'Seguidores actuales en Instagram.',
            icon: 'followers',
          },
          {
            value: '430.000+',
            label: 'Alcance en Instagram durante mayo.',
            icon: 'visibility',
          },
        ],
      },
      productsExplained: {
        overline: 'Inventario',
        title: '2. Productos de la alianza',
        itemsLayout: 'fullRow',
        description:
          'TNO gestiona cada formato como parte de una misma campaña: el noticiero aporta credibilidad, la rotativa con micros aporta frecuencia, el Digest aporta permanencia en buscadores, los inserts llevan la marca al consumo social y TNO Business posiciona la empresa ante contratistas e industria — con un calendario único coordinado por el equipo TNO.',
        items: [
          {
            id: 'noticiero',
            title: 'Noticias TNO — el noticiero (Sol Vargas)',
            icon: 'broadcast',
            badge: 'Prestigio periodístico',
            summary:
              'Menciones y cintillos semanales sobre infraestructura, mantenimiento de obras civiles y formación técnica en el espacio informativo de mayor autoridad del ecosistema TNO.',
            topics: [
              'Plan Bronce: 1 mención mensual informativa sobre servicios Tisemte.',
              'Plan Plata: 2 cintillos al mes (quincenales) con tips de mantenimiento y seguridad en obra.',
              'Plan Oro: 1 cintillo semanal (4 al mes) + bloque mensual «Obra segura, obra bien hecha» en antena.',
              'Narración y contexto con Sol Vargas — mensaje en entorno noticioso, no spot aislado.',
            ],
          },
          {
            id: 'rotativa',
            title: 'Rotativa Radio Visual 24/7 — con micros',
            icon: 'broadcast',
            badge: 'Más rotativas por plan',
            summary:
              'Micros de 30–45 segundos en la señal continua de TNO Network. Bronce: 4/día · Plata: 8/día · Oro: 12/día.',
            topics: [
              'Plan Bronce: 4 impactos diarios (~120 al mes).',
              'Plan Plata: 8 impactos diarios (~240 al mes).',
              'Plan Oro: 12 impactos diarios (~360 al mes) + refuerzo On-Demand.',
              'Mensajes de servicio de obras civiles, mantenimiento, consultoría e infraestructura.',
            ],
          },
          {
            id: 'digest',
            title: 'TNO Digest — más artículos SEO',
            icon: 'seo',
            badge: 'Autoridad en Google',
            summary:
              'Artículos en tnonetwork.com optimizados para búsquedas de obras civiles, mantenimiento industrial y formación técnica.',
            topics: [
              'Bronce: 2 artículos/mes · Plata: 4 (1 por semana) · Oro: 8 (2 por semana).',
              'Temas: mantenimiento de infraestructura, seguridad en obra, rehabilitación civil, consultoría técnica y servicios Tisemte.',
              'Huella digital permanente que sigue generando tráfico orgánico.',
            ],
          },
          {
            id: 'inserts-redes',
            title: 'Inserts en redes',
            icon: 'ugc',
            badge: 'Más inserts por plan',
            summary: 'Logo, claqueta y cintillo en Reels, Shorts y clips del ecosistema TNO.',
            topics: [
              'Bronce: 2 inserts/mes (1 cada 15 días) · Plata: 6/mes (~1,5/semana) · Oro: 12/mes (3/semana) + difusión TNO.',
              'Piezas reutilizables en Instagram, Facebook y WhatsApp de Tisemte.',
              'Refuerzo visual en el contenido que ya consume la audiencia.',
            ],
          },
          {
            id: 'micros-video',
            title: 'Micros en video (sin ir a la obra)',
            icon: 'ugc',
            badge: 'Plata y Oro',
            summary:
              'Piezas de 1–3 min producidas por TNO: estudio con presentador, montaje con fotos/proyectos de Tisemte, motion, clip desde Digest o entrevista con vocero.',
            topics: [
              'Plan Bronce: sin micros en video (solo rotativa con micros cortos).',
              'Plan Plata: 1 micro/mes en estudio TNO o con material de marca.',
              'Plan Oro: 4 micros/mes (1 por semana) + entrevista con vocero por videollamada al set TNO.',
            ],
          },
          {
            id: 'tnobusiness',
            title: 'TNO Business — entrevista corporativa',
            icon: 'mic',
            badge: 'Plata y Oro',
            summary:
              'Entrevista en estudio TNO con el programa de autoridad empresarial: trayectoria, servicios de obra civil, mantenimiento y propuesta de valor de la empresa.',
            topics: [
              'Plan Bronce: no incluye entrevista en TNO Business.',
              'Plan Plata: 1 entrevista trimestral en TNO Business (4 al año).',
              'Plan Oro: 1 entrevista mensual en TNO Business + reel en colaboración para redes.',
              'Posicionamiento B2B ante líderes, contratistas, industria e inversionistas.',
              'Derivado editorial en TNO Digest y difusión en cuentas oficiales TNO.',
            ],
          },
        ],
      },
      plansSection: {
        overline: 'Planes de inversión',
        title: 'Bronce, Plata y Oro — mensual',
        description:
          'Tres niveles con frecuencias distintas por formato. En los tres, TNO asume la gestión integral de la campaña — no solo la pauta: producción, publicación, Digest, redes y seguimiento editorial. Inversión referencial en EUR/mes (neto, sin IVA).',
        currencyLabel: '/ mes · EUR referencial (neto, sin IVA)',
        comparativa:
          '<strong>Bronce (€290):</strong> 4 rotativas/día · 2 Digest/mes · 1 mención noticiero/mes · 2 inserts (quincenales). <strong>Plata (€480):</strong> 8 rotativas/día · 4 Digest (semanales) · 2 cintillos noticiero/mes · 6 inserts · 1 micro/mes · TNO Business trimestral. <strong>Oro (€720):</strong> 12 rotativas/día · 8 Digest (2/semana) · 4 cintillos + bloque mensual · 12 inserts · 4 micros/semana · TNO Business mensual.',
      },
      products: [
        {
          id: 'tisemte-bronce',
          tag: 'Plan Bronce',
          subtitle: 'Presencia esencial en el ecosistema TNO',
          investment: '€290',
          highlight: false,
          publicationFrequencies: [
            { format: 'Rotativa 24/7', frequency: '4/día · ~120/mes' },
            { format: 'TNO Digest', frequency: '2/mes · quincenal' },
            { format: 'Noticias TNO', frequency: '1 mención/mes' },
            { format: 'Inserts en clips', frequency: '2/mes · quincenal' },
            { format: 'Micros en video', frequency: '—' },
            { format: 'TNO Business', frequency: '—' },
          ],
          features: [
            'Rotativa con micros cortos de servicios de obra civil y mantenimiento Tisemte.',
            'Dos artículos quincenales en TNO Digest sobre infraestructura, seguridad en obra o servicios civiles.',
            'Mención informativa mensual en Noticias TNO con Sol Vargas.',
            'Dos inserts de marca en clips del ecosistema TNO (uno cada 15 días).',
            'Banner de aliado en el portal tnonetwork.com.',
          ],
          breakdown: {
            Rotativa: 85,
            'TNO Digest': 45,
            'Noticias TNO': 55,
            'Inserts redes': 40,
            Web: 65,
          },
          idealFor: 'Inicio en TNO: recordación diaria en señal, noticiero, SEO y presencia en redes.',
        },
        {
          id: 'tisemte-plata',
          tag: 'Plan Plata',
          subtitle: 'Autoridad digital, noticiero y TNO Business',
          investment: '€480',
          highlight: true,
          publicationFrequencies: [
            { format: 'Rotativa 24/7', frequency: '8/día · ~240/mes' },
            { format: 'TNO Digest', frequency: '4/mes · semanal' },
            { format: 'Noticias TNO', frequency: '2 cintillos/mes' },
            { format: 'Inserts en clips', frequency: '6/mes · ~1,5/sem.' },
            { format: 'Micros en video', frequency: '1/mes' },
            { format: 'TNO Business', frequency: '1/trimestre' },
          ],
          features: [
            'Rotativa con mayor frecuencia: ocho impactos diarios en señal continua TNO Network.',
            'Cuatro artículos mensuales en TNO Digest (uno por semana): obras civiles, mantenimiento e infraestructura.',
            'Dos cintillos mensuales en Noticias TNO con Sol Vargas (cadencia quincenal).',
            'Seis inserts en Reels, Shorts y derivados de programas (~1,5 por semana).',
            'Un micro mensual en estudio TNO o con material gráfico de Tisemte.',
            'Una entrevista trimestral en TNO Business · banner web y menciones de apoyo.',
          ],
          breakdown: {
            Rotativa: 110,
            'TNO Digest': 90,
            'Noticias TNO': 65,
            'Inserts redes': 80,
            'Micros en video': 85,
            Web: 50,
          },
          idealFor: 'Top of mind en señal y noticiero, con video profesional sin grabación en obra.',
        },
        {
          id: 'tisemte-oro',
          tag: 'Plan Oro',
          subtitle: 'Comercialización 360° — máximo alcance',
          investment: '€720',
          highlight: false,
          publicationFrequencies: [
            { format: 'Rotativa 24/7', frequency: '12/día · ~360/mes' },
            { format: 'TNO Digest', frequency: '8/mes · 2/semana' },
            { format: 'Noticias TNO', frequency: '4 cintillos/mes + bloque' },
            { format: 'Inserts en clips', frequency: '12/mes · 3/semana' },
            { format: 'Micros en video', frequency: '4/mes · semanal' },
            { format: 'TNO Business', frequency: '1/mes + reel' },
          ],
          features: [
            'Rotativa máxima: doce impactos diarios + refuerzo en momentos On-Demand de programas corporativos.',
            'Ocho artículos mensuales en TNO Digest (2 por semana): cobertura SEO completa de obra civil y consultoría.',
            'Un cintillo semanal en Noticias TNO (Sol Vargas) + bloque mensual «Obra segura, obra bien hecha».',
            'Doce inserts mensuales (~3 por semana) + publicación en cuentas oficiales TNO Network.',
            'Cuatro micros mensuales (uno por semana): estudio, material de marca o vocero por videollamada.',
            'Una entrevista mensual en TNO Business + reel en colaboración para redes.',
            'Banner web, logo en espacio de aliados 24/7 y dossier editorial para canales propios de Tisemte.',
          ],
          breakdown: {
            Rotativa: 115,
            'TNO Digest': 150,
            'Noticias TNO': 95,
            'Inserts redes': 110,
            'Micros en video': 125,
            'TNO Business': 80,
            Web: 45,
          },
          idealFor: 'Máximo alcance: noticiero, rotativa, Digest intensivo, inserts, micros y presencia B2B mensual.',
        },
      ],
      valueAdd: {
        layout: 'features',
        overline: 'Diferenciales',
        title: '3. Por qué TNO es un servicio 360',
        items: [
          {
            title: 'TNO gestiona la campaña, no solo la pauta',
            description:
              'Un solo equipo coordina estrategia, calendario, producción de micros e inserts, publicación en señal, artículos Digest y entrevistas en TNO Business. Tisemte no arma piezas ni persigue fechas: TNO opera la campaña mes a mes.',
            icon: 'handshake',
          },
          {
            title: 'Noticiero + rotativa con micros',
            description:
              'Credibilidad periodística y frecuencia diaria en un solo plan: Tisemte no depende de un solo canal para vender sus servicios de obra civil.',
            icon: 'shield',
          },
          {
            title: 'Más Digest, más SEO',
            description:
              'De 2 a 8 artículos mensuales en TNO Digest (cada plan duplica el anterior) para dominar búsquedas de obras civiles, mantenimiento e infraestructura.',
            icon: 'chart',
          },
          {
            title: 'Más inserts en redes',
            description:
              'De 2 quincenales a 12 semanales: la marca acompaña el clip que ya se comparte en Instagram y Facebook.',
            icon: 'network',
          },
          {
            title: 'TNO Business + micros en video',
            description:
              'Entrevistas corporativas para contratos B2B y micros producidos en estudio TNO sin ir a la obra — integrados al mismo calendario de campaña.',
            icon: 'network',
          },
        ],
      },
      investmentSummary: {
        items: [
          {
            product: 'Plan Bronce',
            detail: '4 rotativas/día · 2 Digest/mes · 1 mención noticiero/mes · 2 inserts quincenales · web',
            investment: '€290',
          },
          {
            product: 'Plan Plata',
            detail:
              '8 rotativas/día · 4 Digest semanales · 2 cintillos/mes · 6 inserts · 1 micro/mes · TNO Business trimestral',
            investment: '€480',
          },
          {
            product: 'Plan Oro',
            detail:
              '12 rotativas/día · 8 Digest (2/semana) · 4 cintillos + bloque/mes · 12 inserts · 4 micros/semana · TNO Business mensual',
            investment: '€720',
          },
        ],
        totalLabel: 'Planes mensuales referenciales (EUR)',
        totalValue: 'Bronce €290 · Plata €480 · Oro €720',
      },
      areYouReady: {
        title: 'Activemos Corporación Tisemte × TNO',
        description:
          'Servicio 360 TNO: gestionamos la campaña completa de Tisemte — noticiero, rotativa, Digest, inserts, micros y TNO Business. Planes Bronce, Plata y Oro.',
        ctaText: 'Quiero activar esta propuesta',
        whatsappNumber: '04123964576',
        footerText:
          '© TNO Network 2026 | Corporación Tisemte × TNO Network | The Content Evolution.',
      },
    },
    isActive: true,
    order: 11.755,
  },
  {
    type: 'client',
    slug: 'canguro',
    name: 'Canguro',
    metaTitle: 'Canguro × TNO Network | Inbound Marketing 360°',
    metaDescription:
      'Propuesta Canguro × TNO Network: inbound 360° con planes Bronce, Plata y Oro. Rotativa, programas selectivos, TNO Digest, inserts y redes. Desde 218 €/mes (neto, sin IVA).',
    ogImage: '/images/logos/canguro-logo-black.svg',
    content: {
      hero: {
        overline: 'Propuesta Comercial',
        title: 'Canguro × TNO Network',
        subtitle: 'Tu Vida Digital sin Interrupciones',
        analysisTitle: 'Resumen ejecutivo',
        analysisText:
          'Canguro integra inbound 360° en TNO Network: rotativa Tips Canguro, programas selectivos (TNO Business, Franquicias en Venezuela, Noticias TNO), TNO Digest, inserts en señal y redes. Planes Bronce, Plata y Oro escalan frecuencia, SEO y producción.',
      },
      areYouReady: {
        title: 'Activemos Canguro × TNO',
        description:
          'Elige Bronce, Plata u Oro y conectemos tu marca con la audiencia de TNO Network.',
        ctaText: 'Quiero activar esta propuesta',
        whatsappNumber: '04123964576',
        footerText: '© TNO Network 2026 | Canguro × TNO Network | The Content Evolution.',
      },
    },
    isActive: true,
    order: 11.765,
  },
  {
    type: 'client',
    slug: 'merpoeste',
    name: 'Merpoeste',
    metaTitle: 'Alianza Estratégica | Merpoeste x TNO Network',
    metaDescription: 'Ecosistema de aceleración comercial 360 para Merpoeste: 20 entrevistas Business, 20 artículos SEO, 20 reels en colaboración, 2 coberturas in situ y 4 micros semanales. Inversión mensual total: $300.',
    content: {
      hero: {
        overline: 'Alianza Estratégica',
        title: 'Merpoeste x TNO Network',
        subtitle: 'Ecosistema de Aceleración Comercial 360°',
        analysisTitle: 'El Valor de la Alianza',
        analysisText: 'Esta propuesta está diseñada para ser un Ganar-Ganar absoluto. Para el equipo de Mercadeo de Merpoeste, funciona como una herramienta de gestión externa que resuelve contenido y publicidad de los locatarios. Para los comerciantes, representa un salto definitivo a la profesionalización con visibilidad, autoridad digital y piezas de alto impacto.',
        image: '/images/merpoeste.jpg',
        imageAlt: 'Logo Merpoeste',
      },
      productsLayout: 'whatWeDo',
      products: [
        {
          id: 'impulso-locatario',
          title: 'El Core: Impulso al Locatario (20 tiendas por mes)',
          product: 'Experiencia de marca integral para locatarios seleccionados en Merpoeste.',
          icon: 'mic',
          illustration: '/images/merpoeste-coverage-vintage-mic.svg',
          illustrationAlt: 'Ilustración de micrófono vintage para entrevistas',
          investment: 'Incluido en plan mensual',
          impacts: [
            'Entrevista en sede TNO Network dentro del programa TNO Business.',
            'Artículo especializado en TNO Digest optimizado para SEO local, incluyendo número de local.',
            '1 reel en colaboración (Instagram Collab) derivado de la entrevista, con estética profesional para redes sociales.',
            'Posicionamiento práctico para destacar en Chacao y atraer tráfico calificado.',
          ],
        },
        {
          id: 'cobertura-eventos',
          title: 'Cobertura de Eventos (2 veces por mes)',
          product: 'Cobertura presencial en actividades clave del centro comercial (no incluye host).',
          icon: 'event',
          illustration: '/images/merpoeste-events.svg',
          illustrationAlt: 'Ilustración tipo calendario para cobertura de eventos',
          investment: 'Incluido en plan mensual',
          impacts: [
            'Presencia de TNO Network en dos eventos mensuales: promociones, ferias o aniversarios (sin servicio de host).',
            'Creación de contenido audiovisual para redes de Merpoeste.',
            'Notas de prensa digitales en TNO Digest para registro histórico y posicionamiento SEO.',
            'Difusión multiplataforma para elevar alcance y recordación de marca.',
          ],
        },
        {
          id: 'espacio-mercadeo',
          title: 'Espacio Exclusivo: Departamento de Mercadeo',
          product: 'Micro semanal fijo para promociones generales y anuncios institucionales.',
          icon: 'megaphone',
          illustration: '/images/merpoeste-marketing.svg',
          illustrationAlt: 'Ilustración de espacio de mercadeo',
          investment: 'Incluido en plan mensual',
          impacts: [
            '4 micros semanales para rotar promociones del mall durante toda la semana.',
            'Formato flexible para campañas, eventos y comunicación institucional.',
            'Opciones de naming sugeridas: Merpoeste al Aire, Pasillo Business, El Radar Merpoeste, Conexión Chacao.',
            'Canal recurrente para mantener presencia activa ante la audiencia digital.',
          ],
        },
      ],
      metricsSection: {
        overline: 'Métricas',
        title: 'Audiencia TNO Network',
        description:
          'Merpoeste se apoya en el alcance real del ecosistema TNO: tráfico web y editorial, comunidad en redes y producción en vivo y On-Demand.',
      },
      metrics: [
        {
          value: '30000',
          label: 'Visitas mensuales al ecosistema TNO',
          subtitle: 'Tráfico mensual en plataforma web y contenido editorial.',
          icon: 'LanguageIcon',
          suffix: '+',
        },
        {
          value: '400000',
          label: 'Cuentas alcanzadas en Instagram',
          subtitle: 'Más de 400 mil cuentas alcanzadas en la cuenta de TNO Network al cierre de mayo.',
          icon: 'InstagramIcon',
          suffix: '+',
        },
        {
          value: '90000',
          label: 'Seguidores en todas las redes',
          subtitle: 'Más de 90 mil entre YouTube, Instagram, Facebook y X (Twitter) del ecosistema TNO.',
          icon: 'YouTubeIcon',
          suffix: '+',
        },
        {
          value: '40',
          label: 'Producciones originales On-Demand',
          subtitle: 'Biblioteca desarrollada en el TNO Media Lab.',
          icon: 'OndemandVideoIcon',
          suffix: '+',
        },
        {
          value: '25',
          label: 'Programas en vivo por semana',
          subtitle: 'Señal con distribución global por TNO Network.',
          icon: 'LiveTvIcon',
          suffix: '+',
        },
      ],
      valueAdd: {
        layout: 'features',
        overline: 'Diferenciales',
        title: 'Por qué esta alianza es imbatible',
        items: [
          {
            title: 'Mística y cercanía',
            description: 'Más que un proveedor, somos un equipo que acompaña al comerciante y al departamento de mercadeo.',
            icon: 'handshake',
          },
          {
            title: 'Resultados tangibles',
            description: 'Cada locatario obtiene entrevista, posicionamiento en Google y contenido de alta calidad para sus propias redes.',
            icon: 'chart',
          },
          {
            title: 'Eficiencia de costos',
            description: 'Por una inversión equivalente a un post en algunos perfiles, Merpoeste acciona una maquinaria integral de medios y PR.',
            icon: 'wallet',
          },
          {
            title: 'Escalabilidad mensual',
            description: 'El modelo permite mantener una frecuencia constante y medible para seguir sumando locatarios mes a mes.',
            icon: 'network',
          },
          {
            title: 'Respaldo de marca',
            description: 'Merpoeste consolida una comunicación institucional coherente y sostenida, con mayor confianza para locatarios y visitantes.',
            icon: 'shield',
          },
        ],
      },
      investmentSummary: {
        items: [
          { product: '20 Entrevistas Business', detail: 'Grabadas en sede TNO para locatarios seleccionados', investment: 'Incluido' },
          { product: '20 Artículos SEO', detail: 'Notas en TNO Digest para posicionamiento en Google', investment: 'Incluido' },
          { product: '20 Reels en colaboración', detail: 'Piezas para Instagram y TikTok derivadas de las 20 entrevistas Business', investment: 'Incluido' },
          { product: '2 Coberturas in situ', detail: 'Presencia de TNO Network en eventos del centro comercial (no incluye host)', investment: 'Incluido' },
          { product: '4 Micros semanales', detail: 'Espacio fijo para el Dpto. de Mercadeo en formato rotativa', investment: 'Incluido' },
        ],
        totalLabel: 'Inversión Mensual Total',
        totalValue: '$300',
      },
      areYouReady: {
        title: 'Activemos Dominio Merpoeste',
        description: 'Listos para convertir a Merpoeste en el centro comercial más digital de Caracas, con una operación mensual constante de contenido, SEO y visibilidad para locatarios y mercadeo institucional.',
        ctaText: 'Quiero esta propuesta',
        whatsappNumber: '04126562824',
        footerText: '© TNO Network 2026 | Merpoeste x TNO Network | The Content Evolution.',
      },
    },
    isActive: true,
    order: 11,
  },
  {
    type: 'client',
    slug: 'banco-activo-metas-con-proposito',
    name: 'Metas con Propósito × Banco Activo',
    metaTitle: 'Propuesta de Alianza | Metas con Propósito × Banco Activo | TNO Network',
    metaDescription:
      'Metas con Propósito (@metasconproposito) y Banco Activo: micro educativo financiero con cintillo de marca, 12 impactos diarios en streaming en la señal de Radio Visual, 8 artículos SEO en TNO Digest, On-Demand y banners. Plan mensual total $290.',
    content: {
      ui: { preset: 'tno' },
      hero: {
        overline: 'Propuesta de alianza estratégica',
        title: 'Metas con Propósito × Banco Activo',
        subtitle: 'Educación financiera con respaldo de autoridad · Rosmelí Pelayo y Ana Belén · @metasconproposito',
        analysisTitle: 'Concepto',
        analysisText:
          'Elevamos la colaboración entre Metas con Propósito (Rosmelí Pelayo y Ana Belén, @metasconproposito) y Banco Activo. TNO Network actúa como amplificador que transforma sus consejos de redes sociales en una campaña de autoridad nacional e internacional. Presencia premium 24/7: micro de 1 a 3 minutos con cintillo de marca («Banco Activo presenta») en pantalla durante todo el contenido; actualización semanal y 12 emisiones diarias en streaming en la señal de Radio Visual.',
      },
      planIntegralStrip: {
        title: 'Plan mensual integral',
        description:
          'Un solo plan de $290 al mes reúne streaming en la señal de Radio Visual con cintillo de marca, autoridad editorial en TNO Digest y legado On-Demand con presencia web. Detalle línea por línea en la tabla siguiente.',
      },
      productsExplained: {
        overline: 'Estructura del plan',
        title: 'Tres pilares de la alianza mensual',
        description:
          'Las voces del micro: un tono cercano que rompe la frialdad bancaria tradicional y genera confianza emocional con el público, con la solidez visual del patrocinio.',
        items: [
          {
            id: 'banco-radio-visual',
            title: 'Pauta y rotativa en streaming en la señal de Radio Visual',
            icon: 'broadcast',
            badge: 'Señal 24/7',
            summary:
              '12 impactos diarios (360 al mes) en streaming en la señal de Radio Visual, con presentación exclusiva: logo de Banco Activo en pantalla durante todo el micro de 1 a 3 minutos.',
            topics: [
              'Cintillo de marca permanente: asociación constante entre cada consejo y la institución.',
              'Contenido educativo financiero con voces @metasconproposito.',
              'Alta frecuencia para recordación y prestigio en streaming en la señal de Radio Visual.',
            ],
          },
          {
            id: 'banco-seo',
            title: 'Dominio SEO (TNO Digest)',
            icon: 'seo',
            badge: 'Autoridad editorial',
            summary: '2 artículos semanales (8 al mes) en nuestro portal, co-brandeados con el Banco.',
            topics: [
              'Contenido técnico y educativo orientado a búsquedas en Google.',
              'Complementa y amplifica el mensaje orgánico de Instagram con piezas de consulta permanente.',
              'Huella en dominio de autoridad (tnonetwork.com).',
            ],
          },
          {
            id: 'banco-ondemand',
            title: 'Producción y On-Demand',
            icon: 'web',
            badge: 'Legado digital',
            summary: 'Alojamiento permanente de los micros en el portal y banners web fijos de la alianza.',
            topics: [
              'Biblioteca de consulta para clientes actuales y potenciales.',
              'Banners fijos en el ecosistema digital TNO.',
              'Activos reutilizables más allá del aire en vivo.',
            ],
          },
        ],
      },
      products: [],
      metricsSection: {
        overline: 'Alcance TNO',
        title: 'Contexto de difusión',
        description: 'El plan se apoya en la infraestructura de TNO Network: streaming en la señal de Radio Visual, portal y editorial.',
      },
      metrics: [
        {
          value: '12',
          label: 'Impactos diarios en streaming en la señal de Radio Visual',
          subtitle: '360 rotaciones al mes en streaming en la señal de Radio Visual 24/7.',
          icon: 'LiveTvIcon',
        },
        {
          value: '8',
          label: 'Artículos TNO Digest al mes',
          subtitle: '2 piezas semanales co-brandeadas con el Banco para SEO.',
          icon: 'LanguageIcon',
        },
        {
          value: '30000',
          label: 'Visitas mensuales al ecosistema TNO',
          subtitle: 'Tráfico web, editorial y presencia en streaming en la señal de Radio Visual.',
          icon: 'LanguageIcon',
          suffix: '+',
        },
        {
          value: '90000',
          label: 'Seguidores del ecosistema TNO',
          subtitle: 'Audiencia acumulada en redes del grupo.',
          icon: 'YouTubeIcon',
          suffix: '+',
        },
      ],
      valueAdd: {
        layout: 'features',
        overline: 'Diferenciales',
        title: 'El poder del SEO y el legado digital',
        items: [
          {
            title: 'Autoridad en Google',
            description:
              'Al publicar dos veces por semana en TNO Digest, cuando un usuario busque temas como consejos para ahorrar o créditos para emprendedores en Venezuela, Banco Activo puede aparecer asociado a información útil y de referencia.',
            icon: 'chart',
          },
          {
            title: 'Contenido que complementa redes',
            description:
              'Instagram sigue siendo el motor de cercanía y conversación en tiempo real; TNO complementa y amplifica ese mensaje con cápsulas y artículos que permanecen como biblioteca de consulta para clientes actuales y potenciales del banco.',
            icon: 'network',
          },
          {
            title: 'Conexión real',
            description:
              'El estilo cercano de Rosmelí y Ana Belén rompe la frialdad bancaria tradicional y refuerza confianza frente a mensajes que de otro modo sonarían solo institucionales.',
            icon: 'handshake',
          },
        ],
      },
      investmentSummary: {
        items: [
          {
            product: 'Pauta y rotativa en streaming en la señal de Radio Visual',
            detail:
              '12 impactos diarios (360 al mes) en streaming en la señal de Radio Visual 24/7; micro 1–3 min con logo Banco Activo en pantalla durante todo el micro («Banco Activo presenta»).',
            investment: '$120',
          },
          {
            product: 'Dominio SEO (TNO Digest)',
            detail: '2 artículos semanales (8 al mes); contenido técnico y educativo co-brandeado con el Banco.',
            investment: '$80',
          },
          {
            product: 'Producción y On-Demand',
            detail: 'Alojamiento permanente de micros en portal + banners web fijos de la alianza.',
            investment: '$90',
          },
        ],
        totalLabel: 'TOTAL MENSUAL | Plan de Liderazgo y Autoridad Financiera',
        totalValue: '$290',
      },
      areYouReady: {
        title: 'Conclusión comercial',
        description:
          'Estamos integrando la agilidad de las redes sociales con la autoridad del streaming en la señal de Radio Visual y el posicionamiento en Google. Banco Activo no solo patrocina un espacio; se adueña de una conversación necesaria sobre finanzas, posicionándose 12 veces al día ante una audiencia de alto perfil y asegurando su presencia en los motores de búsqueda de forma orgánica y profesional.',
        ctaText: 'Hablar con comercial',
        whatsappNumber: '584123964576',
        contactEmail: 'mercadeoyventas@tnonetwork.com',
        footerText: '© TNO Network 2026 | Metas con Propósito × Banco Activo | The Content Evolution.',
      },
    },
    isActive: true,
    order: 12,
  },
  {
    type: 'client',
    slug: 'empire-keeway',
    name: 'Empire Keeway × Comercio Corporativo',
    metaTitle: 'Propuesta Estratégica | Empire Keeway en Comercio Corporativo | TNO Network',
    metaDescription:
      'Empire Keeway en Comercio Corporativo con Darío Ascanio: producción in situ 2 visitas/mes, 12 spots diarios en streaming en la señal de Radio Visual, 8 artículos SEO en TNO Digest y autoridad web. Plan mensual total $380.',
    content: {
      ui: { preset: 'tno' },
      hero: {
        overline: 'Propuesta estratégica mensual',
        title: 'Empire Keeway en Comercio Corporativo',
        subtitle: 'Con Darío Ascanio · liderazgo industrial y expansión de marca en Venezuela',
        analysisTitle: 'Concepto: El motor de la economía venezolana',
        analysisText:
          'Empire Keeway (EK) es la ensambladora número uno del país y un referente en expansión. En Comercio Corporativo elevamos la marca desde gestión y éxito empresarial: certificación Great Place to Work, red que supera los 180 concesionarios y lanzamientos de alta gama como la TX II 150 y la EK Thunder 180. Espacio para que directivos hablen del parque industrial en Charallave (232 mil m²) y del impacto en empleo.',
      },
      planIntegralStrip: {
        title: 'Plan de liderazgo industrial',
        description:
          'Un plan de $380 al mes combina producción en terreno, presencia en Comercio Corporativo en streaming en la señal de Radio Visual con narrativa de caso de éxito empresarial, dominio editorial en Google y refuerzo en el portal de negocios de TNO Network. Detalle por línea en la tabla siguiente.',
      },
      productsExplained: {
        overline: 'Estructura de la alianza',
        title: 'Cuatro pilares editoriales y de medios',
        description:
          'A diferencia de una pauta estándar, integramos contenido de valor, streaming en la señal de Radio Visual y SEO para que EK proyecte solidez industrial y cercanía con compradores y concesionarios.',
        items: [
          {
            id: 'ek-produccion',
            title: 'Producción in situ (formato cercano + producción pro)',
            icon: 'build',
            badge: '2 visitas mensuales',
            summary:
              'Recorridos a concesionarios o planta: detrás de cámaras, testimoniales y piezas que humanizan la marca.',
            topics: [
              'Visitas mensuales para renovar historias y territorios de marca.',
              'Piezas orientadas a confianza y prueba social en punto de venta.',
              'Soporte de producción TNO para ritmo constante de publicación.',
            ],
          },
          {
            id: 'ek-comercio',
            title: 'Pauta en Comercio Corporativo',
            icon: 'broadcast',
            badge: 'Caso de éxito empresarial',
            summary:
              '12 spots diarios en streaming en la señal de Radio Visual; el micro de EK se presenta como caso de éxito empresarial dentro del programa de Darío Ascanio.',
            topics: [
              'Asociación con análisis de negocio y liderazgo de mercado.',
              'Frecuencia diaria para recordación y autoridad.',
              'Alineación editorial con hitos comerciales e industriales de EK.',
            ],
          },
          {
            id: 'ek-seo',
            title: 'Dominio SEO (TNO Digest)',
            icon: 'seo',
            badge: '2 artículos semanales',
            summary: '8 piezas al mes con enfoque técnico: financiamiento, nuevos lanzamientos (TX II, Xpress Lite) y expansión de la red de concesionarios.',
            topics: [
              'Intención de búsqueda en motos, repuestos y planes de pago.',
              'Activos permanentes que siguen atrayendo tráfico orgánico.',
              'Coherencia con mensajes de Comercio Corporativo y campaña.',
            ],
          },
          {
            id: 'ek-web',
            title: 'Autoridad web',
            icon: 'web',
            badge: 'Presencia fija',
            summary: 'Banners estratégicos y presencia sostenida en la sección de negocios de TNO Network.',
            topics: [
              'Refuerzo de marca en entorno editorial de negocios.',
              'Enlaces y CTAs hacia concesionarios y canales de contacto.',
              'Complemento del trabajo SEO y del streaming en la señal de Radio Visual.',
            ],
          },
        ],
      },
      products: [],
      metricsSection: {
        title: 'El valor de lo orgánico',
        description: 'No perseguimos números vacíos; gestionamos audiencias fieles.',
        footerLine: 'Conectamos con personas reales en sus manos y en sus hogares.',
      },
      metrics: [
        {
          value: '30.000+',
          label: 'Visitas mensuales al streaming (señal en vivo y plataforma).',
          accent: '#00695c',
        },
        {
          value: '90.000+',
          label: 'Seguidores orgánicos en nuestro ecosistema social.',
          accent: '#00838f',
        },
        {
          value: '40+',
          label: 'Producciones originales On-Demand.',
          accent: '#5e35b1',
        },
        {
          value: '25+',
          label: 'Programas en vivo semanales.',
          accent: '#c62828',
        },
      ],
      valueAdd: {
        layout: 'features',
        overline: 'Por qué importa',
        title: 'SEO, Google y credibilidad con Darío Ascanio',
        items: [
          {
            title: 'Dominio de búsqueda',
            description:
              'Cuando un usuario busque motos con financiamiento en Venezuela o repuestos Empire Keeway, las piezas del TNO Digest pueden posicionar a la marca en resultados relevantes y enviar tráfico a concesionarios.',
            icon: 'chart',
          },
          {
            title: 'Credibilidad corporativa',
            description:
              'Validación en Comercio Corporativo: EK no solo vende motos; proyecta la solidez de una empresa que impulsa la economía del país, con el tono analítico de Darío Ascanio.',
            icon: 'shield',
          },
          {
            title: 'Contenido de experiencia en punto de venta',
            description:
              'En concesionario: prueba de manejo y sensaciones de la TX II 150; explicación clara de planes de financiamiento; post-venta con disponibilidad de repuestos originales. Piezas con estética cercana para reducir fricción frente al papeleo.',
            icon: 'handshake',
          },
        ],
      },
      investmentSummary: {
        items: [
          {
            product: 'Producción in situ (formato cercano + producción pro)',
            detail: '2 visitas mensuales a concesionarios o planta; detrás de cámaras y testimoniales en punto de venta.',
            investment: '$120',
          },
          {
            product: 'Pauta en Comercio Corporativo',
            detail:
              '12 spots diarios en streaming en la señal de Radio Visual; micro como caso de éxito empresarial con Darío Ascanio.',
            investment: '$100',
          },
          {
            product: 'Dominio SEO (TNO Digest)',
            detail: '2 artículos semanales (8 al mes): financiamiento, lanzamientos (TX II, Xpress Lite) y red de concesionarios.',
            investment: '$120',
          },
          {
            product: 'Autoridad web',
            detail: 'Banners estratégicos y presencia fija en la sección de negocios de TNO Network.',
            investment: '$40',
          },
        ],
        totalLabel: 'TOTAL MENSUAL | Plan de liderazgo industrial',
        totalValue: '$380',
      },
      areYouReady: {
        title: 'Documentar el liderazgo industrial de Empire Keeway',
        description:
          'Empire Keeway es la moto que mueve a Venezuela; Comercio Corporativo es el programa que explica cómo se mueve el país. Al unir fuerzas, no estamos haciendo publicidad: estamos documentando el éxito industrial de EK y asegurando que cada búsqueda en Google sobre motocicletas tenga una narrativa profesional que derive hacia la red de concesionarios.',
        ctaText: 'Hablar con comercial',
        whatsappNumber: '584123964576',
        contactEmail: 'mercadeoyventas@tnonetwork.com',
        footerText: '© TNO Network 2026 | Empire Keeway × Comercio Corporativo | The Content Evolution.',
      },
    },
    isActive: true,
    order: 13,
  },
  {
    type: 'client',
    slug: 'hyundai-venezuela',
    name: 'Hyundai Venezuela × TNO Network',
    metaTitle: 'Propuesta Estratégica | Hyundai Venezuela × TNO Network',
    metaDescription:
      'Hyundai Venezuela en TNO Network: Punto de Fe en Tucson, Somos Padres, TNO Business y Creative Tech (Ruta de Calma, Licencia Emocional, Atlas del Silencio, Copiloto de Fe). Plan $450.',
    content: {
      ui: { preset: 'tno' },
      hero: {
        overline: 'Propuesta estratégica mensual',
        title: 'Hyundai Venezuela × TNO Network',
        subtitle:
          'Tucson · Punto de Fe (Margarita Toledo) · Somos Padres (Yelitza Yánez) · TNO Business · @hyundaivenezuela',
        analysisTitle: 'Concepto central',
        analysisText:
          'No vendemos una pauta de carros tradicional: posicionamos a Hyundai Venezuela en estilo de vida, seguridad familiar y conexión espiritual. La Tucson es el corazón de la campaña. En Punto de Fe, el programa «Camino a la Iluminación» (1 hora) se graba desde la Hyundai Tucson: Margarita Toledo conduce y conversa sobre fe y valores familiares mientras recorre la ciudad; en Somos Padres, la marca acompaña la crianza y la seguridad de quienes deciden en familia; en TNO Business, proyectamos solidez, red de concesionarios y el modelo que deseen promover ante tomadores de decisión.',
      },
      planIntegralStrip: {
        title: 'Campaña de posicionamiento premium',
        description:
          'Un plan de $450 al mes (cash) integra Punto de Fe en la Tucson, SEO, rotativa 24/7, Somos Padres, TNO Business, redes y Hub Creative Tech (4 experiencias en tnonetwork.com). El recorrido virtual 360° es upgrade del Hub con presupuesto aparte. Detalle por línea en la tabla siguiente.',
      },
      productsExplained: {
        overline: 'Ecosistema de contenido',
        title: 'Programas ancla y narrativas de campaña',
        description:
          'Tres espacios de TNO Network — fe, familia y negocios — más los frentes Tucson y «Madre al Volante» conectan emoción, crianza y autoridad comercial.',
        columnsPerRow: 2,
        items: [
          {
            id: 'hyundai-madre',
            title: 'Madre al Volante, Familia Segura',
            icon: 'broadcast',
            badge: 'Segmento decisor',
            summary:
              'Orientado a mujeres y madres profesionales que toman las decisiones de seguridad en el hogar.',
            topics: [
              'El carro como extensión de la casa: conversaciones camino al colegio y confort tras la jornada.',
              'Seguridad activa: asistentes de manejo, frenado autónomo y airbags.',
              'Tecnología y conectividad para la rutina diaria de mamá.',
            ],
          },
          {
            id: 'hyundai-tucson',
            title: 'Hyundai Tucson: Redefiniendo el Viaje',
            icon: 'build',
            badge: 'Hero product',
            summary:
              'SUV premium, tecnológica y robusta: ciudad y paisajes de Venezuela en familia.',
            topics: [
              'Innovación, diseño disruptivo y versatilidad.',
              '12 spots diarios en rotativa 24/7 enfocados en Tucson y «Madre al Volante».',
              'Narrativa de asistente de manejo inteligente, espacio premium y seguridad familiar.',
            ],
          },
          {
            id: 'hyundai-punto-fe',
            title: 'Punto de Fe · Camino a la Iluminación',
            icon: 'web',
            badge: 'Margarita Toledo · Programa grabado en Tucson',
            summary:
              'El programa Punto de Fe se graba a bordo de la Hyundai Tucson: Margarita Toledo conduce durante 1 hora mientras conversa con invitados o reflexiona sobre fe, paz mental y valores familiares en ruta.',
            topics: [
              'Producción semanal in-car: programa de 1 hora; el set es la Tucson en movimiento por la ciudad.',
              '4 programas al mes (1 semanal); 12 emisiones diarias en rotativa TNO.',
              'Cintillos e inserts animados en Punto de Fe; Reels/Shorts co-brandeados con @hyundaivenezuela.',
            ],
          },
          {
            id: 'hyundai-somos-padres',
            title: 'Somos Padres',
            icon: 'broadcast',
            badge: 'Yelitza Yánez · Familia y crianza',
            summary:
              'La marca acompaña el eje de crianza, seguridad infantil en ruta y decisiones familiares con un tono de confianza clínica y cercanía.',
            topics: [
              'Menciones y cintillos en vivo durante Somos Padres.',
              'Narrativa «Madre al Volante» reforzada ante padres y madres que eligen SUV familiar.',
              'Alineación con consejos de movilidad segura, rutina escolar y bienestar en el hogar.',
            ],
          },
          {
            id: 'hyundai-tno-business',
            title: 'TNO Business',
            icon: 'seo',
            badge: 'Autoridad corporativa',
            summary:
              'Presencia en la plataforma de referencia para negocios en Venezuela: liderazgo de marca, red de concesionarios y promoción del modelo que deseen destacar.',
            topics: [
              'Spots y menciones en TNO Business: Hyundai como caso de innovación y confianza en el sector.',
              'Entrevistas o segmentos con voceros de marca; foco editorial en el modelo que la marca defina cada mes (Tucson, Creta, Santa Fe, etc.).',
              'Credibilidad ante ejecutivos, emprendedores y audiencia de alto perfil que toma decisiones de compra.',
            ],
          },
          {
            id: 'hyundai-seo',
            title: 'Dominio SEO (TNO Digest)',
            icon: 'seo',
            badge: '8 artículos / mes',
            summary:
              '2 artículos semanales optimizados para que las búsquedas de vehículos en Venezuela encuentren a Hyundai.',
            topics: [
              'Frente «Madre al Volante»: seguridad en SUV familiar y tecnología a bordo.',
              'Frente Tucson: premios, confort en carretera y evolución en el mercado venezolano.',
              'Frente bienestar: tráfico como momento de paz mental (vinculado a Punto de Fe).',
            ],
          },
          {
            id: 'hyundai-visual',
            title: 'Presencia visual y redes',
            icon: 'broadcast',
            badge: 'Multiplataforma',
            summary:
              'Cintillos en Punto de Fe, Somos Padres y TNO Business; banner horizontal en portada web y estrategia social.',
            topics: [
              'Inserts y banners en el ecosistema digital TNO (fe, familia y negocios).',
              'Distribución de clips cortos en redes del grupo.',
              'Huella permanente más allá del aire en vivo.',
            ],
          },
          {
            id: 'hyundai-interactivo',
            title: 'Hub interactivo · Creative Tech',
            icon: 'web',
            badge: 'tnonetwork.com/hyundai-venezuela',
            summary:
              'Mini-sitio de campaña con cuatro experiencias incluidas en la alianza y un recorrido virtual 360° como upgrade premium (presupuesto aparte).',
            topics: [
              'Cuatro experiencias inmersivas programables por TNO (HTML/JS o WordPress), incluidas en el plan:',
              '«Tu Ruta de Calma» — viaje interactivo según tu estado de ánimo y ruta en Caracas.',
              '«Licencia Emocional — Mamá al Volante» — mini-juego con credencial digital compartible.',
              '«Atlas del Silencio» — mapa vivo de rincón de paz + clips del programa en Tucson.',
              '«El Copiloto de Fe» — chat con personalidad (tono Punto de Fe) hacia Test Drive.',
              '★ Recorrido virtual 360° (presupuesto aparte): tour interior/exterior del modelo que elija Hyundai.',
            ],
          },
        ],
      },
      products: [],
      metricsSection: {
        overline: 'Alcance TNO',
        title: 'Contexto de difusión',
        description:
          'Infraestructura 24/7: streaming en la señal de Radio Visual, portal tnonetwork.com, TNO Digest y redes del ecosistema.',
        footerLine: 'Hyundai compra contexto, autoridad y permanencia digital — no minutos de televisión tradicional.',
      },
      metrics: [
        {
          value: '12',
          label: 'Spots diarios en rotativa 24/7',
          subtitle: '360 impactos al mes en campaña Tucson y Madre al Volante.',
        },
        {
          value: '4',
          label: 'Programas Punto de Fe al mes',
          subtitle: 'Camino a la Iluminación — programa de 1 hora, grabado semanal desde la Tucson con Margarita Toledo.',
        },
        {
          value: '8',
          label: 'Artículos TNO Digest al mes',
          subtitle: '2 piezas semanales con SEO orientado a concesionarios.',
        },
        {
          value: '30000',
          label: 'Visitas mensuales al ecosistema TNO',
          subtitle: 'Web, editorial y streaming.',
          suffix: '+',
        },
      ],
      valueAdd: {
        layout: 'features',
        overline: 'Argumento comercial',
        title: 'Por qué esta alianza convierte',
        items: [
          {
            title: 'Contexto en fe, familia y negocios',
            description:
              'Punto de Fe grabado desde la Tucson (bienestar espiritual en ruta), Somos Padres (crianza y seguridad familiar) y TNO Business (autoridad comercial) cubren todo el embudo emocional y racional de compra.',
            icon: 'chart',
          },
          {
            title: 'SEO que trabaja meses',
            description:
              'Cada artículo del TNO Digest posiciona búsquedas de vehículos y deriva lecturas hacia clientes potenciales en concesionarios.',
            icon: 'network',
          },
          {
            title: 'Creative Tech que vende sin parecer pauta',
            description:
              'Licencias compartibles, rutas de calma y copiloto digital convierten la emoción del programa en clics medibles hacia concesionarios — la marca se siente innovadora, no invasiva.',
            icon: 'handshake',
          },
        ],
      },
      extraSections: [
        {
          id: 'programas',
          overline: 'Programas TNO',
          title: 'Punto de Fe, Somos Padres y TNO Business',
          description:
            'La campaña no vive solo en rotativa: se integra en tres programas ancla con audiencias complementarias.',
          groups: [
            {
              label: 'Punto de Fe — Margarita Toledo',
              bullets: [
                'El programa Punto de Fe (1 hora) se graba desde la Hyundai Tucson (Camino a la Iluminación).',
                'Margarita conduce en ruta; fe, paz mental y valores familiares con cintillos e inserts en pantalla.',
              ],
            },
            {
              label: 'Somos Padres — Yelitza Yánez',
              bullets: [
                'Endoso en antena y presencia visual en el espacio de crianza y familia.',
                'Refuerzo de «Madre al Volante»: movilidad segura, rutinas escolares y decisiones del hogar.',
              ],
            },
            {
              label: 'TNO Business',
              bullets: [
                'Narrativa de liderazgo automotriz, red de concesionarios y el modelo que Hyundai quiera promover.',
                'Validación corporativa para quien compra por confianza en la marca y en el canal.',
              ],
            },
          ],
        },
        {
          id: 'editorial',
          overline: 'TNO Digest',
          title: 'Calendario editorial SEO',
          description:
            'Frentes editoriales para dominar búsquedas de vehículos en Venezuela y reforzar cada narrativa de campaña.',
          groups: [
            {
              label: 'Madre al Volante',
              bullets: [
                'Guía para mamás: 5 elementos de seguridad indispensables al elegir una SUV familiar.',
                'Tecnología a bordo: cómo la inteligencia automotriz facilita la rutina de las madres modernas.',
              ],
            },
            {
              label: 'Hyundai Tucson',
              bullets: [
                'Hyundai Tucson: por qué es la SUV más premiada para el confort en carretera.',
                'Diseño y rendimiento: evolución de la Tucson en el mercado venezolano.',
              ],
            },
            {
              label: 'Bienestar y estilo de vida',
              bullets: [
                'Espacios de paz: cómo transformar el tráfico diario en un momento de bienestar mental y espiritual.',
              ],
            },
          ],
        },
        {
          id: 'mediatech',
          overline: 'Hub interactivo · Creative Tech TNO',
          title: 'Experiencias en tnonetwork.com/hyundai-venezuela',
          description:
            'Cuatro piezas incluidas en el plan $450 + recorrido virtual 360° como expansión del Hub (presupuesto aparte). Tú programas las experiencias base; el tour se cotiza según modelo, tomas y nivel de inmersión.',
          items: [
            {
              title: '★ Recorrido virtual 360° (presupuesto aparte)',
              description:
                'Tour inmersivo del modelo que desee Hyundai (Tucson, Creta, Santa Fe, etc.): exterior, cabina, tablero y tecnología con hotspots narrados. Integrable en la landing del Hub o en web de concesionario. Producción de captura + desarrollo TNO; inversión independiente del plan mensual.',
            },
            {
              title: '1. «Tu Ruta de Calma» — viaje interactivo (incluido)',
              description:
                'El usuario elige su estado (estrés, gratitud, cansancio de mamá al volante) y la web le arma una “ruta sagrada” en Caracas: audio ambiente opcional, tip de Margarita, qué asistente de la Tucson lo acompaña en ese trayecto y botón “Quiero sentirlo en Test Drive”. Storytelling, no test aburrido.',
            },
            {
              title: '2. «Licencia Emocional — Mamá al Volante» (incluido)',
              description:
                'Mini-juego de 4 preguntas con animación; al final genera una credencial digital descargable/compartible (nombre + nivel de “paz en ruta” + SUV recomendado). Viral en Instagram Stories; CTA al concesionario debajo del certificado.',
            },
            {
              title: '3. «Atlas del Silencio» — mapa vivo (incluido)',
              description:
                'Mapa donde la audiencia marca “mi rincón de paz” o explora los del programa Camino a la Iluminación; cada pin abre clip del episodio en Tucson + dato sensorial (“aquí el silencio de cabina se siente así”). Une fe, ciudad y producto.',
            },
            {
              title: '4. «El Copiloto de Fe» — chat con personalidad (incluido)',
              description:
                'Chatbot con voz de acompañante espiritual (tono Punto de Fe), no FAQ de concesionario: guía reflexión breve, sugiere modelo según la conversación y cierra con “Hablemos en tu Hyundai” → WhatsApp Test Drive. Tú defines el árbol de diálogo en código.',
            },
          ],
        },
        {
          id: 'mediatech-opciones',
          overline: 'Fase creativa 2',
          title: 'Si Hyundai quiere el “wow” visual',
          description:
            'Extensiones sobre la misma landing cuando la marca apruebe presupuesto o segundo hito:',
          items: [
            {
              title: '«Reto Mito o Realidad» — show en la web',
              description:
                'Trivia gamificada con temporizador y ranking semanal; Margarita lanza el reto en vivo; ganadores canjean experiencia en concesionario. Data de mitos de seguridad para marketing de Hyundai.',
            },
            {
              title: '«Selfie en tu Tucson» — marco AR ligero',
              description:
                'Overlay en cámara del móvil o subida de foto: “te sentamos” en la Tucson del mes; share a redes con hashtag de campaña. Alto engagement, bajo costo de desarrollo.',
            },
          ],
        },
      ],
      investmentSummary: {
        items: [
          {
            product: 'Punto de Fe «Camino a la Iluminación»',
            detail:
              'Programa de 1 hora grabado desde la Hyundai Tucson; 4 producciones mensuales (1 semanal); 12 emisiones diarias en rotativa TNO.',
            investment: '$150',
          },
          {
            product: 'Dominio SEO (TNO Digest)',
            detail: '2 artículos semanales (8 al mes) optimizados para Google; calendario editorial co-brandeado.',
            investment: '$120',
          },
          {
            product: 'Pauta y rotativa general',
            detail:
              '12 spots diarios (360 al mes) en señal 24/7: Tucson, «Madre al Volante» y refuerzo en parrilla TNO Business.',
            investment: '$100',
          },
          {
            product: 'Presencia visual (inserts + banners)',
            detail:
              'Cintillos en Punto de Fe, Somos Padres y TNO Business + banner horizontal en portada web.',
            investment: '$40',
          },
          {
            product: 'Estrategia en redes',
            detail: 'Clips Reels/Shorts de Camino a la Iluminación co-brandeados con @hyundaivenezuela.',
            investment: '$40',
          },
          {
            product: 'Hub Creative Tech · 4 experiencias inmersivas',
            detail:
              'Tu Ruta de Calma, Licencia Emocional, Atlas del Silencio y Copiloto de Fe — desarrollo e integración en tnonetwork.com/hyundai-venezuela (incluido en la alianza).',
            investment: 'Incluido',
          },
          {
            product: 'Recorrido virtual 360° (upgrade del Hub)',
            detail:
              'Tour inmersivo interior/exterior del modelo a elección; hotspots, captura 360 y embed en landing o concesionario. Cotización según alcance.',
            investment: 'Presupuesto aparte',
          },
        ],
        totalLabel: 'TOTAL MENSUAL | Campaña de posicionamiento premium (sin recorrido virtual)',
        totalValue: '$450 (Cash)',
      },
      areYouReady: {
        title: 'Hyundai: contexto, autoridad y permanencia digital',
        description:
          'Hyundai Venezuela no está comprando minutos de televisión vieja: está comprando Punto de Fe (1 hora) grabado en la Tucson, Somos Padres y TNO Business, más autoridad en Google y herramientas Media-Tech. Cada programa en ruta, cada artículo del Digest y cada presencia en antena trabajan para convertir audiencia de TNO en visitas a concesionarios.',
        ctaText: 'Hablar con comercial',
        whatsappNumber: '584123964576',
        contactEmail: 'mercadeoyventas@tnonetwork.com',
        footerText: '© TNO Network 2026 | Hyundai Venezuela × TNO Network | The Content Evolution.',
      },
    },
    isActive: true,
    order: 14,
  },
  {
    type: 'client',
    slug: 'dhl-venezuela',
    name: 'DHL Venezuela × TNO Network',
    metaTitle: 'Propuesta Estratégica | DHL Venezuela × TNO Network',
    metaDescription:
      'DHL Venezuela en TNO Network: Expedientes del Delito, Noticias TNO, TNO Business, Comercio Corporativo, micros en rotativa 24/7 y TNO Digest (4 artículos/mes). Plan €530/mes.',
    ogImage: '/images/logos/dhl-logo.svg',
    content: {
      ui: {
        enhanced: true,
        showProgramIndex: true,
        brand: {
          primary: '#D40511',
          accent: '#FFCC00',
          ctaSecondary: '#FFCC00',
          ctaSecondaryText: '#1a1a1a',
          heroGradient:
            'linear-gradient(140deg, #141414 0%, #3d080c 38%, #D40511 72%, #9a0310 100%)',
        },
      },
      hero: {
        overline: 'Propuesta estratégica mensual',
        title: 'DHL Venezuela × TNO Network',
        image: '/images/logos/dhl-logo.svg',
        imageAlt: 'DHL',
        showTnoLogo: true,
        subtitle:
          'Logística global · Expedientes del Delito · Noticias TNO (Sol Vargas) · TNO Business · Comercio Corporativo (Darío Ascanio)',
        analysisTitle: 'De qué va la propuesta',
        analysisText:
          'La idea es simple: que DHL esté donde ya hay conversación seria — investigación, noticias, negocios y gestión empresarial — sin sonar a cuña repetida. Cada programa tiene su tono; la rotativa recuerda el servicio todos los días; el Digest deja piezas en Google que siguen leyéndose meses después.',
      },
      planIntegralStrip: {
        title: 'Plan mensual',
        description:
          '€530 al mes (euros): cuatro programas, micros en rotativa 24/7, cuatro artículos en TNO Digest y presencia visual en web. Abajo está el desglose línea por línea.',
      },
      tnoEcosystem: {
        title: 'El ecosistema TNO',
        description:
          'Somos un ecosistema de comunicación integral que no se detiene. Unimos la inmediatez con la profundidad a través de tres ejes:',
        highlights: [
          {
            value: '30.000+',
            label: 'Conexiones mensuales al streaming de TNO Network.',
            icon: 'streaming',
          },
          {
            value: '300.000+',
            label: 'Alcance promedio en Instagram (últimos 3 meses).',
            icon: 'reach',
          },
          {
            value: '52.700',
            label: 'Seguidores actuales en Instagram.',
            icon: 'followers',
          },
          {
            value: '430.000+',
            label: 'Alcance en Instagram durante mayo.',
            icon: 'visibility',
          },
        ],
      },
      productsExplained: {
        overline: 'Dónde aparece DHL',
        title: 'Programas y formatos',
        description:
          'Cada espacio cumple una función distinta. Lo que no cambia es el criterio: que la marca entre con el tono del programa, no como interrupción.',
        columnsPerRow: 2,
        items: [
          {
            id: 'dhl-expedientes',
            title: 'Expedientes del Delito',
            icon: 'broadcast',
            badge: 'Con Santiago Gutiérrez y Angélica Lugo',
            mediaKitSlug: 'expedientesdeldelito',
            mediaKitLinkLabel: 'Ir al media kit de Expedientes del Delito',
            summary:
              'En Expedientes la marca no “patrocina el crimen”: acompaña el relato cuando el caso pasa por evidencia, trazas y cosas que se mueven entre países. La idea es plantear, con Santiago y Angélica, por qué una cadena logística bien hecha importa tanto como una investigación bien llevada — sin convertir el programa en un manual de courier.',
            topics: [
              'Cintillos e inserts cuando el episodio toque contrabando, documentación sensible o historias donde el “rastro” del envío es parte del caso.',
              'Línea editorial reservada: un bloque al mes (3–5 min) donde la marca aporta contexto de cumplimiento y trazabilidad, acordado con producción.',
              'Tono forense-informativo; el detalle creativo y calendario se cierra con el equipo de DHL.',
            ],
          },
          {
            id: 'dhl-noticias',
            title: 'Noticias TNO',
            icon: 'broadcast',
            badge: 'Con Sol Vargas',
            mediaKitSlug: 'noticias',
            mediaKitLinkLabel: 'Ir al media kit de Noticias TNO',
            summary:
              'Con Sol Vargas, DHL entra en el momento en que el país habla de importaciones, aranceles o comercio exterior: un dato, un cintillo o un cierre breve que conecte la noticia del día con “cómo se mueve la mercancía”. Parece nota de redacción, no reclamo de 30 segundos.',
            topics: [
              '“Pulso logístico” semanal: Sol enlaza una noticia de economía con un hecho de conectividad o tiempos de envío (formato ticker, ~1 min).',
              'Presencia en bloques de negocios; mención orgánica cuando el día traiga puertos, couriers o cadena de suministro.',
              'Mensaje y frecuencia se afinan mes a mes según la prioridad comercial de DHL.',
            ],
          },
          {
            id: 'dhl-tno-business',
            title: 'TNO Business',
            icon: 'seo',
            badge: 'Programa de negocios',
            mediaKitSlug: 'tnobusiness',
            mediaKitLinkLabel: 'Ir al media kit de TNO Business',
            summary:
              'TNO Business es la vitrina B2B: aquí DHL puede mostrarse como quien resuelve lo que frena un negocio cuando quiere crecer fuera del país — plazos, papeles, última milla. No solo logo: conversación con quien ya mira el programa por decisiones de compra.',
            topics: [
              'Segmento patrocinado mensual (3–5 min): caso real o mini-entrevista sobre importación, e-commerce o fulfillment; guion con producción TNO.',
              'Menciones “Presentado por” / referencias en vivo en emisiones del mes, más cintillo en pantalla.',
              'Clips de antena para redes del patrocinador; el tema exacto del primer bloque se define en kickoff.',
            ],
          },
          {
            id: 'dhl-comercio-corporativo',
            title: 'Comercio Corporativo',
            icon: 'broadcast',
            badge: 'Con Darío Ascanio',
            mediaKitSlug: 'comerciocorporativo',
            mediaKitLinkLabel: 'Ir al media kit de Comercio Corporativo',
            summary:
              'Darío habla de gestión y mercado; DHL aparece cuando toca la parte que casi nadie ve en TV: qué pasa entre que se vende y que llega. Micro de “detrás del mostrador” o charla con vocero de la marca, siempre con números y contexto, nunca con speech de folleto.',
            topics: [
              'Micro mensual “La mercancía antes de la venta”: 2–3 min dentro de Comercio Corporativo, enlazado al tema que Darío trate esa semana.',
              'Posibilidad de vocero DHL en estudio cuando haya lanzamiento, dato de comercio exterior o tendencia de cadena de suministro.',
              'Edición y ángulo editorial los marca TNO con validación de la marca antes de salir al aire.',
            ],
          },
          {
            id: 'dhl-rotativa',
            title: 'Micros en la rotativa',
            icon: 'build',
            badge: 'Señal 24/7 · Radio Visual',
            summary:
              'La rotativa es el hilo conductor: tres familias de mensaje que se alternan en la señal 24/7 para que DHL no dependa de un solo programa ni de un solo horario. Corto, directo, memorable.',
            topics: [
              'Territorio Express (urgencia y mismo día), Internacional (puerta a puerta) y Rastreo (saber dónde está el envío); copy rotativo, no una sola cuña.',
              'Referencia del plan: 12 micros al día en Radio Visual; producción TNO adapta arte y voz.',
              'Calendario de mensajes se renueva cada mes según campaña o servicio que DHL quiera empujar.',
            ],
          },
          {
            id: 'dhl-digest',
            title: 'TNO Digest · 4 artículos al mes',
            icon: 'seo',
            badge: 'Un artículo por semana',
            summary:
              'El Digest es la biblioteca que queda: cuatro artículos al mes para quien busca en Google cómo enviar, importar o montar logística desde Venezuela. Cada pieza responde una pregunta real; DHL aparece como quien sabe, no como quien grita.',
            topics: [
              'Cuatro frentes editoriales al mes (uno por semana): guías prácticas, e-commerce, aduanas y operación internacional; títulos y enfoque se acuerdan con DHL.',
              'SEO y redacción a cargo de TNO; la marca valida datos sensibles (tarifas, tiempos, cobertura).',
              'Los artículos siguen posicionando meses después; el calendario detallado se arma en la reunión de arranque.',
            ],
          },
          {
            id: 'dhl-visual',
            title: 'Presencia visual y redes',
            icon: 'broadcast',
            badge: 'Web y redes TNO',
            summary:
              'Lo visual amarra todo: misma línea en antena, web y redes para que quien vea un programa reconozca a DHL en el siguiente. No es repetir logo: es coherencia entre vivo, portal y recortes.',
            topics: [
              'Lower thirds y cintillos en los cuatro programas del plan; banner en portada de tnonetwork.com actualizable por campaña.',
              'Derivados para redes TNO (y clips para el patrocinador cuando haya bloque en estudio o entrevista).',
              'Piezas gráficas las produce TNO a partir del manual de marca de DHL; cantidad de variantes se define en kickoff.',
            ],
          },
        ],
      },
      products: [],
      valueAdd: {
        layout: 'features',
        overline: 'Por qué encaja',
        title: 'Tres razones prácticas',
        items: [
          {
            title: 'Varios públicos, un mismo criterio',
            description:
              'Noticias y Expedientes tocan actualidad y confianza; TNO Business y Comercio Corporativo hablan con quien decide compras y operaciones. La rotativa y el Digest mantienen el nombre en circulación.',
            icon: 'chart',
          },
          {
            title: 'El Digest no caduca a los tres días',
            description:
              'Cuatro artículos al mes sobre envíos e importación siguen apareciendo en búsquedas y llevan tráfico al sitio de DHL.',
            icon: 'network',
          },
          {
            title: 'Suena al programa, no a la cuña',
            description:
              'En cada espacio la marca entra con el tono de ese espacio. Eso es lo que diferencia esta propuesta de comprar minutos sueltos.',
            icon: 'handshake',
          },
        ],
      },
      extraSections: [
        {
          id: 'programas',
          overline: 'En antena',
          title: 'Los cuatro programas',
          description: 'Además de la rotativa, DHL tiene presencia en estos espacios.',
          groups: [
            {
              label: 'Expedientes del Delito',
              mediaKitSlug: 'expedientesdeldelito',
              mediaKitLinkLabel: 'Ir al media kit de Expedientes del Delito',
              bullets: [
                'Bloque mensual reservado + cintillos cuando el caso toque trazas, evidencia o movimiento internacional.',
                'Producción y guion con validación de DHL; no se publica el detalle del primer episodio en esta propuesta.',
              ],
            },
            {
              label: 'Noticias TNO',
              mediaKitSlug: 'noticias',
              mediaKitLinkLabel: 'Ir al media kit de Noticias TNO',
              bullets: [
                '“Pulso logístico” semanal con Sol Vargas, enlazado a la economía del día.',
                'Cintillos en bloques de negocios; mensaje ad hoc según coyuntura.',
              ],
            },
            {
              label: 'TNO Business',
              mediaKitSlug: 'tnobusiness',
              mediaKitLinkLabel: 'Ir al media kit de TNO Business',
              bullets: [
                'Segmento patrocinado mensual (caso o entrevista) + menciones en vivo y en pantalla.',
                'Tema del primer bloque y vocero se definen en kickoff con producción.',
              ],
            },
            {
              label: 'Comercio Corporativo',
              mediaKitSlug: 'comerciocorporativo',
              mediaKitLinkLabel: 'Ir al media kit de Comercio Corporativo',
              bullets: [
                'Micro “La mercancía antes de la venta” con Darío Ascanio, alineado al tema de la semana.',
                'Vocero en estudio cuando el calendario editorial lo amerite.',
              ],
            },
          ],
        },
        {
          id: 'rotativa-digest',
          overline: 'Fuera del vivo',
          title: 'Rotativa y Digest',
          description: 'Lo que corre todos los días y lo que queda escrito en el portal.',
          groups: [
            {
              label: 'Micros en rotativa',
              bullets: [
                'Tres líneas de mensaje (Express, Internacional, Rastreo) en rotación; 12 micros/día de referencia.',
                'Renovación mensual de copy según prioridad de campaña DHL.',
              ],
            },
            {
              label: 'TNO Digest — 4 artículos al mes',
              bullets: [
                'Cuatro piezas SEO (guías, e-commerce, aduanas, operación internacional); títulos en kickoff.',
                'Redacción TNO + validación de datos por la marca.',
              ],
            },
          ],
        },
      ],
      investmentSummary: {
        items: [
          {
            product: 'Expedientes del Delito',
            detail:
              'Cintillos contextuales + bloque mensual reservado (3–5 min) con Santiago y Angélica; guion acordado con DHL.',
            investment: '€90',
          },
          {
            product: 'Noticias TNO',
            detail:
              '“Pulso logístico” semanal con Sol Vargas + cintillos en bloques de economía y comercio exterior.',
            investment: '€70',
          },
          {
            product: 'TNO Business',
            detail:
              'Segmento patrocinado mensual (3–5 min), menciones en vivo, cintillo en pantalla y clips para redes.',
            investment: '€80',
          },
          {
            product: 'Comercio Corporativo',
            detail:
              'Micro mensual “La mercancía antes de la venta” con Darío Ascanio; vocero en estudio según calendario.',
            investment: '€90',
          },
          {
            product: 'Micros en rotativa 24/7',
            detail:
              '12 micros/día; tres líneas de mensaje (Express, Internacional, Rastreo) con renovación mensual de copy.',
            investment: '€100',
          },
          {
            product: 'TNO Digest (4 artículos / mes)',
            detail:
              'Cuatro piezas SEO semanales; frentes guías, e-commerce, aduanas e internacional — títulos en kickoff.',
            investment: '€60',
          },
          {
            product: 'Presencia visual (inserts + banners)',
            detail:
              'Lower thirds en programas, banner web, derivados para redes; piezas según manual de marca DHL.',
            investment: '€40',
          },
        ],
        totalLabel: 'TOTAL MENSUAL | Campaña logística y corporativa multi-programa (EUR)',
        totalValue: '€530',
      },
      areYouReady: {
        title: 'Siguiente paso',
        description:
          'Si les hace sentido, armamos calendario de menciones por programa, mensajes de rotativa y temas del Digest según lo que DHL quiera priorizar este trimestre.',
        ctaText: 'Hablar con comercial',
        whatsappNumber: '584123964576',
        contactEmail: 'mercadeoyventas@tnonetwork.com',
        footerText: '© TNO Network 2026 | DHL Venezuela × TNO Network | The Content Evolution.',
      },
    },
    isActive: true,
    order: 14.2,
  },
  {
    type: 'client',
    slug: 'lg-venezuela',
    name: 'LG Venezuela × TNO Network',
    metaTitle: 'Propuesta Estratégica | LG Venezuela × TNO Network',
    metaDescription:
      'LG Venezuela en TNO Network: inbound marketing (Digest SEO, hub Life\'s Good, UGC), Expedientes del Delito, Vivir en Equilibrio, TNO Sports, Comercio Corporativo y Noticias TNO. Plan €730/mes.',
    ogImage: '/images/logos/lg-logo.svg',
    content: {
      ui: {
        enhanced: true,
        showProgramIndex: true,
        brand: {
          primary: '#A50034',
          accent: '#E4007F',
          ctaSecondary: '#E4007F',
          ctaSecondaryText: '#ffffff',
          heroGradient:
            'linear-gradient(145deg, #1a1a1a 0%, #3d0a1c 35%, #A50034 70%, #6B0030 100%)',
        },
      },
      hero: {
        overline: 'Propuesta estratégica · Inbound + grilla TNO',
        title: 'LG Venezuela × TNO Network',
        showTnoLogo: true,
        subtitle:
          'Life\'s Good en el hogar · Expedientes del Delito · Vivir en Equilibrio · TNO Sports · Comercio Corporativo (Darío Ascanio) · Noticias TNO',
        analysisTitle: 'De qué va la propuesta',
        analysisText:
          'LG no necesita más ruido: necesita que la gente llegue con una pregunta (“¿qué TV me conviene?”, “¿cómo elijo el aire para mi sala?”) y encuentre respuesta en TNO antes de ir a tienda. Mezclamos programas de hogar, deporte y negocio con inbound real: artículos que posicionan en Google, micros en rotativa, hub con herramientas útiles y presencia en la parrilla donde ya está la audiencia.',
      },
      planIntegralStrip: {
        title: 'Plan mensual',
        description:
          '€730 al mes: cuatro programas de grilla + Noticias, rotativa 24/7, hub inbound Life\'s Good, cuatro artículos Digest y UGC mensual. El desglose está en la tabla.',
      },
      tnoEcosystem: {
        title: 'El ecosistema TNO',
        description:
          'Somos un ecosistema de comunicación integral que no se detiene. Unimos la inmediatez con la profundidad a través de tres ejes:',
        highlights: [
          {
            value: '30.000+',
            label: 'Conexiones mensuales al streaming de TNO Network.',
            icon: 'streaming',
          },
          {
            value: '300.000+',
            label: 'Alcance promedio en Instagram (últimos 3 meses).',
            icon: 'reach',
          },
          {
            value: '52.700',
            label: 'Seguidores actuales en Instagram.',
            icon: 'followers',
          },
          {
            value: '430.000+',
            label: 'Alcance en Instagram durante mayo.',
            icon: 'visibility',
          },
        ],
      },
      productsExplained: {
        overline: 'Grilla sugerida + inbound',
        title: 'Dónde vive LG y cómo atrae demanda',
        description:
          'Cada programa cubre un momento del embudo: inspiración en casa, entretenimiento, negocio retail y búsqueda en Google. Abajo, el inbound que convierte interés en visita a tienda o contacto.',
        columnsPerRow: 2,
        items: [
          {
            id: 'lg-expedientes',
            title: 'Expedientes del Delito',
            icon: 'broadcast',
            badge: 'Santiago Gutiérrez · +800K en Instagram',
            mediaKitSlug: 'expedientesdeldelito',
            mediaKitLinkLabel: 'Ir al media kit de Expedientes del Delito',
            summary:
              'En investigación periodística la tecnología es parte del relato: pantallas que muestran evidencia, equipos que procesan video, hogares que se protegen. LG entra cuando el caso toca vigilancia, pruebas digitales, electrodomésticos en operativos o el rol de la pantalla en la reconstrucción de hechos — con el rigor de Santiago Gutiérrez y Angélica Lugo, y el alcance de más de 800 mil seguidores en Instagram de Santiago, además de la audiencia de TNO.',
            topics: [
              'Cintillos e inserts cuando el episodio hable de tecnología aplicada a investigación, CCTV, pantallas profesionales o seguridad en el hogar.',
              'Bloque mensual reservado (3–5 min): “Tecnología en la pista” — contexto LG sobre displays, monitoreo o equipos del hogar vinculados al tema del día.',
              'Clips y piezas acordadas: amplificación en Instagram de Santiago Gutiérrez (+800K seguidores); guion validado con LG antes de salir al aire.',
            ],
          },
          {
            id: 'lg-vivir-equilibrio',
            title: 'Vivir en Equilibrio',
            icon: 'broadcast',
            badge: 'Con Liliana Fernandes',
            mediaKitSlug: 'vivirenequilibrio',
            mediaKitLinkLabel: 'Ir al media kit de Vivir en Equilibrio',
            summary:
              'Bienestar pasa por el espacio donde vives: aire fresco, cocina más liviana, descanso. Con Liliana Fernandes, LG se vincula a climatización inteligente, electrodomésticos eficientes y “hogar que cuida” — mismo nivel de presencia que Expedientes: bloque en programa, cintillos y clips, además de la audiencia de TNO en antena y streaming.',
            topics: [
              'Bloque mensual reservado (3–5 min): “Hogar en equilibrio” — AC, purificación, electrodomésticos eficientes y confort con tono wellness, no ficha técnica.',
              'Cintillos e inserts en vivo cuando el episodio toque hábitos, salud en casa o renovación del hogar.',
              'Clips y piezas acordadas para patrocinador; enlace al hub (guía de eficiencia o checklist); guion validado con LG antes de salir al aire.',
            ],
          },
          {
            id: 'lg-tno-sports',
            title: 'TNO Sports',
            icon: 'broadcast',
            badge: 'Con Yeraldin Marrero · +100K en redes',
            mediaKitSlug: 'tnosports',
            mediaKitLinkLabel: 'Ir al media kit de TNO Sports',
            summary:
              'Deporte y pantalla grande van juntos: Vinotinta, MLB, agenda del fin de semana. LG aparece en el relato de “cómo vivir el partido en casa” — imagen, sonido, tamaño de TV — con la energía de Yeraldin Marrero: misma lógica que Expedientes (bloque + cintillos + clips), más de 100 mil seguidores en sus redes y la audiencia de TNO en antena y streaming.',
            topics: [
              'Bloque mensual reservado (3–5 min): “Partido en casa” — experiencia LG en pantalla, sonido y sala deportiva en fechas clave.',
              'Cintillos e inserts en vivo en la agenda deportiva; contenido cruzado con hub: “arma tu sala deportiva” (checklist + CTA).',
              'Clips y piezas acordadas: entrega al patrocinador y amplificación en las redes de Yeraldin (+100K seguidores); guion validado con LG.',
            ],
          },
          {
            id: 'lg-comercio-corporativo',
            title: 'Comercio Corporativo',
            icon: 'broadcast',
            badge: 'Con Darío Ascanio',
            mediaKitSlug: 'comerciocorporativo',
            mediaKitLinkLabel: 'Ir al media kit de Comercio Corporativo',
            summary:
              'Darío analiza economía, gestión y cómo se mueve el comercio en Venezuela. LG entra cuando toca inversión en tecnología, renovación de equipos en empresa, eficiencia operativa o tendencias de consumo B2B — vocero o micro con números y contexto de mercado, no speech de folleto.',
            topics: [
              'Micro mensual “Electro en la operación” (2–3 min): cómo la tecnología LG apoya retail, oficinas o producción según el tema de la semana.',
              'Entrevista o segmento con vocero LG cuando haya lanzamiento, dato de sector o alianza comercial.',
              'Menciones en vivo + cintillo; clips para redes del patrocinador.',
            ],
          },
          {
            id: 'lg-noticias',
            title: 'Noticias TNO',
            icon: 'broadcast',
            badge: 'Con Sol Vargas',
            mediaKitSlug: 'noticias',
            mediaKitLinkLabel: 'Ir al media kit de Noticias TNO',
            summary:
              'Cuando el noticiero toca economía, importaciones o consumo, un cintillo breve puede enlazar la noticia con tecnología en el hogar o disponibilidad de producto — sin desentonar con la línea informativa de Sol Vargas.',
            topics: [
              'Cintillo “Tecnología y consumo” según coyuntura (importaciones, promociones, temporada).',
              'Presencia en bloques de negocios cuando el día traiga retail o electrónica.',
              'Mensaje acordado mes a mes con equipo comercial LG.',
            ],
          },
          {
            id: 'lg-inbound-hub',
            title: 'Inbound · Hub Life\'s Good',
            icon: 'web',
            badge: 'tnonetwork.com/lg-venezuela',
            summary:
              'El embudo digital de la alianza: quien llega desde Digest o redes encuentra herramientas que capturan intención y derivan a canal de venta. No es brochure PDF: es experiencia web programable por TNO.',
            topics: [
              'Asistente “¿Qué LG necesitas?”: 4–5 preguntas (sala, cocina, lavado, gaming) → recomendación de línea + CTA WhatsApp / tienda.',
              'Guías descargables ligadas al Digest (PDF o página): elegir TV, AC o línea blanca en Venezuela.',
              'Localizador de tiendas autorizadas y campaña del mes embebidos en la landing.',
            ],
          },
          {
            id: 'lg-rotativa',
            title: 'Micros en la rotativa',
            icon: 'build',
            badge: 'Radio Visual 24/7',
            summary:
              'Tres mensajes en rotación: TV & entretenimiento, línea blanca / cocina, climatización & eficiencia. Mantienen top of mind entre programas.',
            topics: [
              '12 micros/día de referencia; copy renovado cada mes (lanzamiento, promo, temporada).',
              'QR opcional en pieza hacia hub o landing de campaña.',
              'Producción TNO con arte LG.',
            ],
          },
          {
            id: 'lg-digest',
            title: 'TNO Digest · 4 artículos al mes',
            icon: 'seo',
            badge: 'Motor inbound SEO',
            summary:
              'Cuatro piezas al mes para captar búsquedas: “mejor televisor”, “aire acondicionado inverter”, “lavadora LG”, “electrodomésticos Venezuela”. Cada artículo responde una pregunta y cierra con CTA al hub o tienda.',
            topics: [
              'Frentes: TV & cine en casa, climatización, cocina/lavado, compra inteligente y postventa.',
              'SEO TNO + validación técnica LG; enlaces internos al hub.',
              'Activos que siguen generando tráfico orgánico.',
            ],
          },
          {
            id: 'lg-visual',
            title: 'Presencia visual y UGC',
            icon: 'broadcast',
            badge: 'Web · redes · tienda',
            summary:
              'Misma línea gráfica en programas, web y recortes. Opción de piezas UGC-style (estudio TNO + producto LG) para redes del patrocinador — una producción mensual corta incluida en plan acordado.',
            topics: [
              'Lower thirds y banners en programas del plan + portada tnonetwork.com.',
              '1 pieza UGC/mes (30–45 s) para Instagram/Reels del patrocinador, según calendario.',
              'Manual de marca LG aplicado por diseño TNO.',
            ],
          },
        ],
      },
      products: [],
      valueAdd: {
        layout: 'features',
        overline: 'Por qué encaja',
        title: 'Inbound + grilla, no solo pauta',
        items: [
          {
            title: 'Embudo completo',
            description:
              'Expedientes del Delito aporta credibilidad y tecnología; Vivir en Equilibrio inspira el hogar; TNO Sports emociona; Comercio Corporativo conecta con gestión y mercado; Digest y hub capturan la búsqueda en Google.',
            icon: 'chart',
          },
          {
            title: 'Demanda que se busca sola',
            description:
              'Los artículos y el asistente del hub trabajan 24/7: quien investiga antes de comprar encuentra a LG con contenido útil, no solo un anuncio.',
            icon: 'network',
          },
          {
            title: 'Cercanía con la compra',
            description:
              'CTAs a tienda autorizada y WhatsApp comercial en hub, Digest y clips — la audiencia de TNO no se queda solo en “me gustó el programa”.',
            icon: 'handshake',
          },
        ],
      },
      extraSections: [
        {
          id: 'inbound',
          overline: 'Inbound marketing',
          title: 'Qué hace crecer demanda sin gritar',
          description:
            'La propuesta incluye captación, nutrición y conversión ligera — alineado a cómo se compra electrodoméstico hoy: investigar, comparar, ir a tienda.',
          groups: [
            {
              label: 'Atracción (top of funnel)',
              bullets: [
                'SEO en TNO Digest: 4 artículos/mes orientados a intención de compra y comparación.',
                'Clips de programas y UGC en redes TNO + entrega al patrocinador para sus canales.',
                'Micros en rotativa con QR a hub en campañas puntuales.',
              ],
            },
            {
              label: 'Consideración (hub Life\'s Good)',
              bullets: [
                'Landing tnonetwork.com/lg-venezuela: quiz de recomendación, guías y localizador de tiendas.',
                'Lead magnets editoriales (checklists, “5 errores al elegir TV”) enlazados desde Digest.',
                'Remarketing orgánico: enlaces cruzados entre artículos y programas en antena.',
              ],
            },
            {
              label: 'Conversión',
              bullets: [
                'CTA persistente: WhatsApp comercial / tienda autorizada según ciudad.',
                'Promoción del mes en banner web y hub (validación LG).',
                'Métricas básicas de hub acordadas en kickoff (visitas, clics CTA).',
              ],
            },
          ],
        },
        {
          id: 'programas',
          overline: 'Grilla recomendada',
          title: 'Cuatro programas + noticiero',
          description:
            'Prioridad editorial sugerida para LG Venezuela según categoría de producto y audiencia.',
          groups: [
            {
              label: 'Expedientes del Delito — tecnología y credibilidad',
              mediaKitSlug: 'expedientesdeldelito',
              mediaKitLinkLabel: 'Ir al media kit de Expedientes del Delito',
              bullets: [
                'Bloque “Tecnología en la pista” + cintillos cuando el caso lo amerite.',
                'Santiago Gutiérrez y Angélica Lugo; audiencia exigente e institucional.',
                'Instagram de Santiago Gutiérrez: más de 800 mil seguidores — refuerzo orgánico para clips y mensajes LG.',
              ],
            },
            {
              label: 'Vivir en Equilibrio — bienestar en casa',
              mediaKitSlug: 'vivirenequilibrio',
              mediaKitLinkLabel: 'Ir al media kit de Vivir en Equilibrio',
              bullets: [
                'Bloque mensual “Hogar en equilibrio” + cintillos; misma estructura de presencia que Expedientes.',
                'Climatización, eficiencia y confort con Liliana Fernandes; enlace a guías del hub.',
              ],
            },
            {
              label: 'TNO Sports — entretenimiento',
              mediaKitSlug: 'tnosports',
              mediaKitLinkLabel: 'Ir al media kit de TNO Sports',
              bullets: [
                'Bloque mensual “Partido en casa” + cintillos; misma estructura de presencia que Expedientes.',
                'Yeraldin Marrero: más de 100 mil seguidores en sus redes — refuerzo orgánico para clips y campañas LG.',
              ],
            },
            {
              label: 'Comercio Corporativo — gestión y mercado',
              mediaKitSlug: 'comerciocorporativo',
              mediaKitLinkLabel: 'Ir al media kit de Comercio Corporativo',
              bullets: [
                'Micro “Electro en la operación” + vocero LG con Darío Ascanio.',
                'Audiencia ejecutiva y tomadores de decisión.',
              ],
            },
            {
              label: 'Noticias TNO — coyuntura',
              mediaKitSlug: 'noticias',
              mediaKitLinkLabel: 'Ir al media kit de Noticias TNO',
              bullets: [
                'Cintillos de tecnología y consumo con Sol Vargas según noticia del día.',
              ],
            },
          ],
        },
        {
          id: 'rotativa-digest',
          overline: 'Siempre encendido',
          title: 'Rotativa, Digest y hub',
          description: 'Recordación diaria + biblioteca SEO + destino de conversión.',
          groups: [
            {
              label: 'Rotativa 24/7',
              bullets: [
                'TV / deportes · cocina & lavado · AC & eficiencia — 12 micros/día.',
              ],
            },
            {
              label: 'TNO Digest (4/mes)',
              bullets: [
                'Semana a semana: TV, clima, línea blanca, guía de compra — títulos en kickoff.',
              ],
            },
            {
              label: 'Hub inbound',
              bullets: [
                'Quiz + guías + tiendas; desarrollo e integración TNO en URL acordada.',
              ],
            },
          ],
        },
      ],
      investmentSummary: {
        items: [
          {
            product: 'Expedientes del Delito',
            detail:
              'Bloque “Tecnología en la pista” mensual (3–5 min) + cintillos; guion con Santiago y Angélica; alcance en Instagram de Santiago (+800K seguidores).',
            investment: '€100',
          },
          {
            product: 'Vivir en Equilibrio',
            detail:
              'Bloque mensual “Hogar en equilibrio” (3–5 min) + cintillos; guion con Liliana Fernandes; misma línea de inversión que Expedientes.',
            investment: '€100',
          },
          {
            product: 'TNO Sports',
            detail:
              'Bloque mensual “Partido en casa” (3–5 min) + cintillos; guion con Yeraldin Marrero; alcance en sus redes (+100K seguidores); misma línea que Expedientes.',
            investment: '€100',
          },
          {
            product: 'Comercio Corporativo',
            detail:
              'Micro “Electro en la operación” mensual + vocero o entrevista con Darío Ascanio; menciones y cintillo.',
            investment: '€80',
          },
          {
            product: 'Noticias TNO',
            detail: 'Cintillos de tecnología y consumo con Sol Vargas según coyuntura.',
            investment: '€55',
          },
          {
            product: 'Hub inbound Life\'s Good',
            detail: 'Quiz, guías, localizador y CTAs — desarrollo en tnonetwork.com/lg-venezuela.',
            investment: '€90',
          },
          {
            product: 'Micros en rotativa 24/7',
            detail: '12 micros/día; tres líneas de mensaje (TV, línea blanca, AC).',
            investment: '€95',
          },
          {
            product: 'TNO Digest (4 artículos / mes)',
            detail: 'SEO inbound: TV, AC, cocina/lavado, guía de compra.',
            investment: '€65',
          },
          {
            product: 'Presencia visual + 1 UGC/mes',
            detail: 'Cintillos, banner web, 1 pieza corta UGC para redes del patrocinador.',
            investment: '€45',
          },
        ],
        totalLabel: 'TOTAL MENSUAL | Inbound + grilla multi-programa (EUR)',
        totalValue: '€730',
      },
      areYouReady: {
        title: 'Siguiente paso',
        description:
          'En kickoff definimos primeros temas de Digest, mensajes de rotativa, estructura del hub y calendario en Expedientes del Delito, Vivir en Equilibrio, TNO Sports y Comercio Corporativo según lanzamientos LG del trimestre.',
        ctaText: 'Hablar con comercial',
        whatsappNumber: '584123964576',
        contactEmail: 'mercadeoyventas@tnonetwork.com',
        footerText: '© TNO Network 2026 | LG Venezuela × TNO Network | The Content Evolution.',
      },
    },
    isActive: true,
    order: 14.25,
  },
  {
    type: 'client',
    slug: 'cafe-san-domingo',
    name: 'Café San Domingo × TNO Network',
    metaTitle: 'Propuesta Estratégica | Café San Domingo × TNO Network',
    metaDescription:
      'Café San Domingo en TNO Network: 3 de Copas, Vivir en Equilibrio, La Dosis Positiva, Entérate con Flavia (Chef Páez), Noticias TNO, rotativa 24/7 y TNO Digest (4 artículos/mes). Plan €495/mes.',
    ogImage: '/images/logos/cafe-san-domingo-logo.svg',
    content: {
      ui: {
        enhanced: true,
        showProgramIndex: true,
        brand: {
          primary: '#5C3D2E',
          accent: '#D4A574',
          ctaSecondary: '#D4A574',
          ctaSecondaryText: '#1a1a1a',
          heroGradient:
            'linear-gradient(145deg, #1a1410 0%, #3D2817 35%, #5C3D2E 68%, #2E1F18 100%)',
        },
      },
      hero: {
        overline: 'Propuesta estratégica · Tradición y alcance nacional',
        title: 'Café San Domingo × TNO Network',
        showTnoLogo: true,
        subtitle:
          'San Domingo cada día · 3 de Copas · Vivir en Equilibrio · La Dosis Positiva · Entérate con Flavia (Chef Páez) · Noticias TNO',
        analysisTitle: 'De qué va la propuesta',
        analysisText:
          'Café San Domingo lleva casi 70 años tostando café 100 % arábica venezolano y hoy llega a todo el país. La idea no es gritar en cada break: es estar en conversaciones reales — lifestyle, bienestar, inspiración diaria y gastronomía con Flavia Sánchez y el chef Páez — más la coyuntura en Noticias, la rotativa y el Digest en Google.',
      },
      planIntegralStrip: {
        title: 'Plan mensual',
        description:
          '€495 al mes (euros): cinco programas, micros en rotativa 24/7, cuatro artículos en TNO Digest y presencia visual en web. El desglose está en la tabla.',
      },
      tnoEcosystem: {
        title: 'El ecosistema TNO',
        description:
          'Somos un ecosistema de comunicación integral que no se detiene. Unimos la inmediatez con la profundidad a través de tres ejes:',
        highlights: [
          {
            value: '30.000+',
            label: 'Conexiones mensuales al streaming de TNO Network.',
            icon: 'streaming',
          },
          {
            value: '300.000+',
            label: 'Alcance promedio en Instagram (últimos 3 meses).',
            icon: 'reach',
          },
          {
            value: '52.700',
            label: 'Seguidores actuales en Instagram.',
            icon: 'followers',
          },
          {
            value: '430.000+',
            label: 'Alcance en Instagram durante mayo.',
            icon: 'visibility',
          },
        ],
      },
      productsExplained: {
        overline: 'Dónde aparece la marca',
        title: 'Programas y formatos',
        description:
          'Cada espacio tiene su tono: conversación, bienestar, inspiración o gastronomía. Lo que une el plan es que San Domingo entra con el lenguaje del programa, no como cuña genérica.',
        columnsPerRow: 2,
        items: [
          {
            id: 'csd-3decopas',
            title: '3 de Copas con Zidney',
            icon: 'broadcast',
            badge: 'Con Zidney Alzauro',
            mediaKitSlug: '3decopas',
            mediaKitLinkLabel: 'Ir al media kit de 3 de Copas',
            summary:
              'El café es ritual social: conversación, pausa y conexión. En 3 de Copas, San Domingo acompaña el relato de estilo de vida y transformación — un brindis con propósito, menciones naturales y el tono cercano de Zidney, sin convertir el programa en publicidad de supermercado.',
            topics: [
              'Bloque mensual “Tres copas, una tradición” (3–5 min): ritual del café en casa, oficina o encuentro; presentaciones de línea según temporada.',
              'Cintillos cuando el episodio toque hábitos, vínculos, celebraciones o momentos de pausa.',
              'Clips para redes del patrocinador; guion validado con San Domingo antes de salir al aire.',
            ],
          },
          {
            id: 'csd-vivir-equilibrio',
            title: 'Vivir en Equilibrio',
            icon: 'broadcast',
            badge: 'Con Liliana Fernandes',
            mediaKitSlug: 'vivirenequilibrio',
            mediaKitLinkLabel: 'Ir al media kit de Vivir en Equilibrio',
            summary:
              'La primera taza del día es bienestar: energía, foco y ritual consciente. Con Liliana Fernandes, San Domingo se vincula a hábitos saludables, pausa inteligente y “mañana que arranca bien” — aroma y tradición venezolana, no discurso de catálogo.',
            topics: [
              'Bloque mensual “La taza que equilibra” (3–5 min): café de especialidad, guayoyo o espresso según el tema de bienestar del episodio.',
              'Menciones en vivo + cintillo en programas de hábitos y autocuidado.',
              'Enlace editorial a recetas o ritual matutino en Digest cuando corresponda.',
            ],
          },
          {
            id: 'csd-dosis-positiva',
            title: 'La Dosis Positiva',
            icon: 'broadcast',
            badge: 'Con Yanira Bocaranda',
            mediaKitSlug: 'ladosispositiva',
            mediaKitLinkLabel: 'Ir al media kit de La Dosis Positiva',
            summary:
              'Inspiración y equilibrio van con la pausa del café: productividad consciente, hábitos y relaciones de calidad. Con Yanira Bocaranda, San Domingo acompaña el relato de bienestar real — la taza como ritual positivo antes de arrancar el día, sin tono de infomercial.',
            topics: [
              'Bloque mensual “Tu dosis de aroma” (3–5 min): café, energía y foco; presentaciones de línea según temporada.',
              'Cintillos cuando el episodio toque hábitos, autocuidado o rutinas matutinas.',
              'Clips para redes del patrocinador; guion validado con San Domingo.',
            ],
          },
          {
            id: 'csd-enterate-flavia',
            title: 'Entérate con Flavia',
            icon: 'broadcast',
            badge: 'Con Flavia Sánchez · Chef Páez',
            mediaKitSlug: 'enterateconflavia',
            mediaKitLinkLabel: 'Ir al media kit de Entérate con Flavia',
            summary:
              'Pasaporte Social: destinos, gastronomía y experiencias. En el programa participa el chef Páez — ideal para mostrar café en cocina, maridajes, recetas y cultura gastronómica venezolana con la credibilidad de Flavia Sánchez y el sello San Domingo.',
            topics: [
              'Bloque mensual “De la cocina a la taza” (3–5 min): chef Páez y café San Domingo en preparaciones, postres o experiencias food & beverage.',
              'Cintillos e inserts en rutas gastronómicas, turismo y lifestyle; menciones en vivo con Flavia.',
              'Clips para redes del patrocinador; coordinación de temas con producción y chef en kickoff.',
            ],
          },
          {
            id: 'csd-noticias',
            title: 'Noticias TNO',
            icon: 'broadcast',
            badge: 'Con Sol Vargas',
            mediaKitSlug: 'noticias',
            mediaKitLinkLabel: 'Ir al media kit de Noticias TNO',
            summary:
              'Cuando el noticiero toca economía, consumo o precios en góndola, un cintillo breve puede enlazar la noticia con la categoría café — disponibilidad nacional, promoción o consumo en hogar — sin desentonar con la línea informativa de Sol Vargas.',
            topics: [
              'Cintillo “Taza y economía” según coyuntura (inflación, consumo, agroindustria).',
              'Presencia en bloques de negocios cuando el día traiga retail o alimentos.',
              'Mensaje acordado mes a mes con equipo comercial San Domingo.',
            ],
          },
          {
            id: 'csd-rotativa',
            title: 'Micros en la rotativa',
            icon: 'build',
            badge: 'Radio Visual 24/7',
            summary:
              'Tres familias de mensaje en rotación: mañana que inspira, tradición venezolana y hogar que comparte. La rotativa mantiene “San Domingo cada día” entre programas, sin depender de un solo horario.',
            topics: [
              'Líneas referenciales: Mañana (ritual y energía), Tradición (70 años · 100 % arábica), Hogar (la taza que une).',
              '12 micros/día de referencia; copy renovado cada mes (campaña, presentación, temporada).',
              'Producción TNO con arte de la marca.',
            ],
          },
          {
            id: 'csd-digest',
            title: 'TNO Digest · 4 artículos al mes',
            icon: 'seo',
            badge: 'Un artículo por semana',
            summary:
              'Cuatro piezas al mes para quien busca en Google cómo elegir café, preparar guayoyo o entender origen y tostión. San Domingo aparece como referencia de calidad venezolana, no como anuncio intrusivo.',
            topics: [
              'Frentes: ritual matutino, tipos de preparación, café venezolano vs importado, consumo en familia.',
              'SEO TNO + validación de datos por la marca (presentaciones, disponibilidad).',
              'Artículos que siguen posicionando meses después; calendario en kickoff.',
            ],
          },
          {
            id: 'csd-visual',
            title: 'Presencia visual y redes',
            icon: 'broadcast',
            badge: 'Web y redes TNO',
            summary:
              'Misma línea gráfica en programas, web y recortes para que la marca se reconozca en antena y digital. Coherencia entre vivo, portal y clips.',
            topics: [
              'Lower thirds y cintillos en los cinco programas del plan; banner en portada tnonetwork.com.',
              'Derivados para redes TNO y entrega de clips al patrocinador cuando haya bloque en estudio.',
              'Arte según manual de marca San Domingo; variantes se definen en kickoff.',
            ],
          },
        ],
      },
      products: [],
      valueAdd: {
        layout: 'features',
        overline: 'Por qué encaja',
        title: 'Tres razones prácticas',
        items: [
          {
            title: 'Café es conversación y hábito',
            description:
              '3 de Copas y Vivir en Equilibrio tocan lifestyle y bienestar; La Dosis Positiva inspira el día; Entérate con Flavia y el chef Páez llevan la marca a gastronomía; Noticias enlaza coyuntura. La rotativa y el Digest mantienen presencia diaria.',
            icon: 'chart',
          },
          {
            title: 'El Digest trabaja en buscadores',
            description:
              'Cuatro artículos al mes sobre café y consumo siguen atrayendo tráfico orgánico mucho después de publicarse.',
            icon: 'network',
          },
          {
            title: 'Suena al programa',
            description:
              'En cada espacio San Domingo entra con el tono de ese espacio — tradición venezolana con credibilidad, no cuña repetida.',
            icon: 'handshake',
          },
        ],
      },
      extraSections: [
        {
          id: 'programas',
          overline: 'En antena',
          title: 'Los cinco programas',
          description:
            'Prioridad editorial para Café San Domingo: conversación, bienestar, inspiración, gastronomía y noticia del día.',
          groups: [
            {
              label: '3 de Copas — lifestyle y ritual',
              mediaKitSlug: '3decopas',
              mediaKitLinkLabel: 'Ir al media kit de 3 de Copas',
              bullets: [
                'Bloque “Tres copas, una tradición” + cintillos con Zidney Alzauro.',
                'Tono cercano; ideal para campañas emocionales y presentaciones de línea.',
              ],
            },
            {
              label: 'Vivir en Equilibrio — bienestar matutino',
              mediaKitSlug: 'vivirenequilibrio',
              mediaKitLinkLabel: 'Ir al media kit de Vivir en Equilibrio',
              bullets: [
                'Bloque “La taza que equilibra” con Liliana Fernandes.',
                'Conexión natural con hábitos, energía y autocuidado.',
              ],
            },
            {
              label: 'La Dosis Positiva — inspiración y hábitos',
              mediaKitSlug: 'ladosispositiva',
              mediaKitLinkLabel: 'Ir al media kit de La Dosis Positiva',
              bullets: [
                'Bloque “Tu dosis de aroma” con Yanira Bocaranda.',
                'Bienestar, productividad y ritual matutino con la marca.',
              ],
            },
            {
              label: 'Entérate con Flavia — gastronomía',
              mediaKitSlug: 'enterateconflavia',
              mediaKitLinkLabel: 'Ir al media kit de Entérate con Flavia',
              bullets: [
                'Bloque “De la cocina a la taza” con Flavia Sánchez y el chef Páez.',
                'Turismo, sabores y experiencias; ideal para recetas y maridaje con café.',
              ],
            },
            {
              label: 'Noticias TNO — coyuntura',
              mediaKitSlug: 'noticias',
              mediaKitLinkLabel: 'Ir al media kit de Noticias TNO',
              bullets: [
                'Cintillos “Taza y economía” con Sol Vargas según noticia del día.',
              ],
            },
          ],
        },
        {
          id: 'rotativa-digest',
          overline: 'Fuera del vivo',
          title: 'Rotativa y Digest',
          description: 'Recordación diaria en señal 24/7 y biblioteca SEO en el portal.',
          groups: [
            {
              label: 'Micros en rotativa',
              bullets: [
                'Mañana · Tradición · Hogar — 12 micros/día de referencia.',
                'Copy mensual alineado a campaña “San Domingo cada día”.',
              ],
            },
            {
              label: 'TNO Digest — 4 artículos al mes',
              bullets: [
                'Ritual, preparación, origen venezolano y consumo en familia; títulos en kickoff.',
                'Redacción TNO + validación de datos por la marca.',
              ],
            },
          ],
        },
      ],
      investmentSummary: {
        items: [
          {
            product: '3 de Copas con Zidney',
            detail:
              'Bloque “Tres copas, una tradición” mensual (3–5 min) + cintillos; guion acordado con San Domingo.',
            investment: '€80',
          },
          {
            product: 'Vivir en Equilibrio',
            detail:
              'Bloque “La taza que equilibra” mensual (3–5 min) + menciones y cintillo con Liliana Fernandes.',
            investment: '€75',
          },
          {
            product: 'La Dosis Positiva',
            detail:
              'Bloque “Tu dosis de aroma” mensual (3–5 min) + cintillos con Yanira Bocaranda.',
            investment: '€75',
          },
          {
            product: 'Entérate con Flavia',
            detail:
              'Bloque “De la cocina a la taza” con Flavia Sánchez y chef Páez (3–5 min) + cintillos gastronómicos.',
            investment: '€80',
          },
          {
            product: 'Noticias TNO',
            detail: 'Cintillos “Taza y economía” con Sol Vargas según coyuntura de consumo.',
            investment: '€45',
          },
          {
            product: 'Micros en rotativa 24/7',
            detail:
              '12 micros/día; tres líneas (Mañana, Tradición, Hogar) con renovación mensual de copy.',
            investment: '€70',
          },
          {
            product: 'TNO Digest (4 artículos / mes)',
            detail:
              'Cuatro piezas SEO semanales: ritual, preparación, origen y consumo — títulos en kickoff.',
            investment: '€45',
          },
          {
            product: 'Presencia visual (inserts + banners)',
            detail:
              'Lower thirds en programas, banner web, derivados para redes; arte según manual San Domingo.',
            investment: '€25',
          },
        ],
        totalLabel: 'TOTAL MENSUAL | Tradición cafetalera multi-programa (EUR)',
        totalValue: '€495',
      },
      areYouReady: {
        title: 'Siguiente paso',
        description:
          'En kickoff definimos mensajes de rotativa, temas del Digest y calendario en 3 de Copas, Vivir en Equilibrio, La Dosis Positiva y Entérate con Flavia (con chef Páez) según la campaña nacional de San Domingo.',
        ctaText: 'Hablar con comercial',
        whatsappNumber: '584123964576',
        contactEmail: 'mercadeoyventas@tnonetwork.com',
        footerText: '© TNO Network 2026 | Café San Domingo × TNO Network | The Content Evolution.',
      },
    },
    isActive: true,
    order: 14.3,
  },
  {
    type: 'client',
    slug: 'alter-gold-programa',
    name: 'Alter Gold Servicios de Salud',
    metaTitle: 'Producción TNO Media Lab | Alter Gold Servicios de Salud',
    metaDescription:
      'Producción en TNO Media Lab para Alter Gold Servicios de Salud: programa propio (Tagyp, T.A.G., Club AlterGold), micros en rotativa, clips para redes, 4 promocionales YouTube por episodio y tráfico a TNO Network. Bonificación: 4 artículos en TNO Digest. $230 USD/mes.',
    content: {
      hero: {
        showTnoLogo: true,
        skipWhoSection: true,
        overline: 'TNO Media Lab · Propuesta por bloques',
        title: 'Alter Gold Servicios de Salud',
        subtitle:
          'Arranque con identidad gráfica y operación mensual en TNO: programa, rotativa, clips para redes, 4 clips promocionales en YouTube por episodio y audiencia dirigida a TNO Network. Bonificación: 4 artículos en TNO Digest.',
      },
      tnoEcosystem: {
        title: 'Cuatro bloques, una operación mensual',
        subtitle: 'Producido en TNO Network',
        description:
          'Cada bloque responde a una etapa del recorrido de audiencia: profundidad editorial, recordación en señal, consumo en redes y conversión hacia TNO Network.',
        axes: [
          {
            title: 'Bloque 1 · Programa',
            text: 'Producción del espacio principal en TNO Media Lab: preproducción, grabación y postproducción.',
          },
          {
            title: 'Bloque 2 · Rotativa',
            text: 'Micros en la rotativa 24/7 de TNO Network para presencia constante de marca.',
          },
          {
            title: 'Bloque 3 · Redes',
            text: '12 clips por episodio para Instagram, LinkedIn, TikTok y X — incluidos en el paquete de clips ($50 USD/mes).',
          },
          {
            title: 'Bloque 4 · YouTube promocional',
            text: '4 clips promocionales por episodio (Shorts y avances) — mismo paquete de $50 USD/mes, con CTAs a TNO Network.',
          },
        ],
        highlights: [
          { value: '4', label: 'Bloques integrados en un solo plan mensual.' },
          { value: '24/7', label: 'Rotativa TNO Network para los micros de Alter Gold.' },
          { value: '$230', label: 'USD mensuales — producción completa en TNO Media Lab.' },
          {
            value: '→',
            label: 'TNO Network',
            subtitle: 'Plataforma destino: streaming, on-demand y tnonetwork.com.',
          },
        ],
      },
      productsExplained: {
        overline: 'Alcance',
        title: 'Fase inicial y plan mensual',
        description:
          'Primero definimos la identidad visual del proyecto; luego opera el plan mensual de cuatro bloques, producido en TNO Media Lab y publicado en el ecosistema TNO Network.',
        items: [
          {
            id: 'agp-bloque-0-grafico',
            title: 'Fase inicial · Tapas, logo y paquete gráfico',
            icon: 'web',
            badge: 'Pago único',
            investment: '$120 USD',
            summary:
              'Identidad visual de arranque para el programa y la marca Alter Gold Servicios de Salud: tapas, logo y materiales gráficos reutilizables en todos los bloques.',
            topics: [
              'Diseño de tapas / covers para podcast, YouTube y plataformas digitales.',
              'Logo y variantes para uso en video, rotativa y redes.',
              'Paquete gráfico: plantillas, lower thirds y piezas para promoción.',
              'Manual breve de uso de marca para el equipo de Alter Gold.',
            ],
          },
          {
            id: 'agp-bloque-1-programa',
            title: 'Bloque 1 · Producción de programa',
            icon: 'mic',
            badge: 'TNO Media Lab',
            investment: '$100 USD/mes',
            summary:
              'Producción del programa de Alter Gold Servicios de Salud en estudios TNO: formato acordado, grabación profesional y episodio editado listo para publicar.',
            topics: [
              'Preproducción: temario, guion u orden del día y coordinación de voceros.',
              'Producción en estudio TNO Media Lab (audio/video según formato).',
              'Postproducción: edición, mezcla y master para entrega.',
              'Archivo y nomenclatura listos para publicación en TNO Network (streaming, on-demand y canales digitales de la red).',
            ],
          },
          {
            id: 'agp-bloque-2-rotativa',
            title: 'Bloque 2 · Micros en rotativa TNO',
            icon: 'broadcast',
            badge: 'Rotativa 24/7',
            investment: '$80 USD/mes',
            summary:
              'Micros de Alter Gold integrados en la rotativa de TNO Network: piezas cortas que circulan en la grilla regular de la señal multiplataforma.',
            topics: [
              'Producción de micros con mensajes de prevención, Tagyp, T.A.G. y Club AlterGold.',
              'Inserción en rotativa 24/7 del ecosistema TNO Network.',
              'Recordación constante frente a audiencias de salud, familias y cuidado preventivo.',
              'Sincronización con temas del programa y campañas en TNO Network.',
            ],
          },
          {
            id: 'agp-bloque-3-clips',
            title: 'Bloque 3 · Clips para redes sociales',
            icon: 'ugc',
            badge: '12 por episodio',
            investment: '$50 USD/mes (paquete clips)',
            summary:
              'Por cada episodio: doce clips para redes y cuatro clips promocionales para YouTube, todo bajo un solo paquete de $50 USD/mes — sin costo adicional por clip.',
            topics: [
              '12 clips por episodio: vertical y cuadrado para Instagram, LinkedIn, TikTok y X.',
              'Subtítulos, branding y ganchos listos para publicar.',
              'Calendario sugerido de publicación en redes con enlaces a TNO Network.',
              'Extraídos del programa y de los micros de rotativa.',
            ],
          },
          {
            id: 'agp-bloque-4-youtube',
            title: 'Bloque 4 · Clips promocionales en YouTube',
            icon: 'broadcast',
            badge: '4 promocionales · por episodio',
            investment: 'Incl. en paquete clips',
            summary:
              'Cuatro clips promocionales por episodio (Shorts y avances del programa) para impulsar audiencia hacia TNO Network — incluidos en el paquete de $50 USD/mes.',
            topics: [
              '4 clips promocionales por episodio: ganchos, avances y llamados a ver el programa en TNO.',
              'Títulos, descripciones y cards con enlace a tnonetwork.com y la grilla / on-demand de TNO Network.',
              'Coordinación con el estreno del episodio en la plataforma y la campaña en redes.',
              'Impulso cruzado: redes → YouTube promocional → TNO Network.',
            ],
          },
        ],
      },
      products: [
        {
          id: 'agp-p0-grafico',
          title: 'Fase inicial · Tapas, logo y paquete gráfico',
          product: 'Covers, logo, plantillas y elementos gráficos de marca.',
          investment: '$120 USD',
          impacts: [
            'Identidad coherente antes del primer episodio.',
            'Materiales listos para rotativa, redes y YouTube.',
          ],
        },
        {
          id: 'agp-p1-programa',
          title: 'Bloque 1 · Producción de programa',
          product: 'Programa producido en TNO Media Lab (pre + producción + post).',
          investment: '$100/mes',
          impacts: [
            'Contenido de autoridad con estándar broadcast TNO.',
            'Episodio listo para publicar en TNO Network.',
          ],
        },
        {
          id: 'agp-p2-rotativa',
          title: 'Bloque 2 · Micros en rotativa',
          product: 'Micros en rotativa 24/7 de TNO Network.',
          investment: '$80/mes',
          impacts: [
            'Presencia en señal sin depender de un solo horario.',
            'Top of mind en audiencia de salud, familias y cuidado preventivo.',
          ],
        },
        {
          id: 'agp-p3-clips',
          title: 'Bloques 3 y 4 · Clips redes y YouTube',
          product: '12 clips redes + 4 clips promocionales YouTube por episodio — $50 USD/mes el paquete completo.',
          investment: '$50/mes',
          impacts: [
            '16 piezas por episodio: alcance social y promoción del programa en YouTube hacia TNO Network.',
            'Paquete fijo mensual sin costo por clip individual.',
          ],
        },
      ],
      metricsSection: {
        overline: 'Plan mensual',
        title: 'En números',
        description: 'Operación mensual producida en TNO Media Lab por $230 USD.',
      },
      metrics: [
        {
          value: '4',
          label: 'Bloques de producción',
          subtitle: 'Programa, rotativa, redes y YouTube promocional → TNO Network.',
        },
        {
          value: '$100',
          label: 'Programa en estudio',
          subtitle: 'Producción mensual en TNO Media Lab.',
        },
        {
          value: '12+4',
          label: 'Clips por episodio',
          subtitle: '12 para redes y 4 promocionales YouTube — $50 USD/mes el paquete completo.',
        },
        {
          value: '$230',
          label: 'Inversión mensual',
          subtitle: 'USD — programa $100 + micros $80 + paquete clips $50.',
        },
        {
          value: '1',
          label: 'Plataforma destino',
          subtitle: 'TNO Network — CTAs desde redes y clips promocionales de YouTube.',
        },
        {
          value: '4',
          label: 'Artículos TNO Digest',
          subtitle: 'Bonificación mensual — autoridad SEO y permanencia digital.',
        },
      ],
      bonificacion: {
        overline: 'Bonificación incluida',
        title: '4 artículos mensuales en TNO Digest',
        badge: 'Sin costo adicional',
        highlight: 'Autoridad editorial permanente en tnonetwork.com',
        description:
          'Como bonificación del plan mensual, Alter Gold Servicios de Salud recibe cuatro artículos al mes en TNO Digest: contenido optimizado para buscadores que amplifica el mensaje de Alter Gold más allá del video y la rotativa.',
        items: [
          '4 publicaciones mensuales en TNO Digest (uno por semana, según calendario editorial).',
          'Redacción y edición SEO a partir de temas, voceros o material de Alter Gold.',
          'Presencia en TNO Digest (tnonetwork.com) con enlaces al programa en TNO Network.',
          'Contenido evergreen: sigue posicionando en Google semanas después de publicarse.',
        ],
        investment: 'Incluido en el plan',
      },
      comercializacion: {
        overline: 'Comercialización del programa',
        title: 'Reparto de ingresos por patrocinio',
        badge: 'Opcional · por cliente cerrado',
        highlight: '30% TNO si trae el cliente Alter Gold · 50/50 si lo trae la radio',
        description:
          'Si comercializan el programa (venta de espacios o patrocinios sobre el contenido), el reparto de cada ingreso depende de quién originó al cliente:',
        items: [
          'Cliente traído por Alter Gold Servicios de Salud: Alter Gold paga a TNO Network (la radio) el 30% por cada cliente que cierre.',
          'Cliente traído por TNO Network (la radio): reparto 50% y 50% entre Alter Gold y TNO por cada cliente que cierre.',
          'Aplica por patrocinador o anunciante cerrado, sobre el monto neto acordado en el contrato de comercialización.',
          'La producción mensual ($230 USD) es independiente; la comercialización es ingreso adicional opcional.',
        ],
      },
      investmentSummary: {
        items: [
          {
            product: 'Fase inicial · Identidad gráfica',
            detail: 'Tapas, logo y paquete gráfico (pago único)',
            investment: '$120',
          },
          {
            product: 'Bloque 1 · Producción de programa',
            detail: 'Preproducción, grabación y post en TNO Media Lab',
            investment: '$100',
          },
          {
            product: 'Bloque 2 · Micros en rotativa',
            detail: 'Piezas en rotativa 24/7 de TNO Network',
            investment: '$80',
          },
          {
            product: 'Bloques 3 y 4 · Clips redes y YouTube promocional',
            detail: '12 clips redes + 4 clips promocionales YouTube por episodio — paquete mensual',
            investment: '$50',
          },
          {
            product: 'Bonificación · TNO Digest',
            detail: '4 artículos mensuales optimizados SEO en TNO Digest',
            investment: 'Incluido',
          },
          {
            product: 'Comercialización del programa',
            detail: 'Cliente de Alter Gold: 30% TNO · Cliente de la radio: 50/50',
            investment: 'Por cliente',
          },
        ],
        monthlyTotalLabel: 'Total mensual',
        monthlyTotalValue: '$230 USD',
        totalLabel: 'Fase inicial (única vez)',
        totalValue: '$120 USD',
        totalNote:
          'Arranque: tapas, logo y paquete gráfico por $120 USD (pago único). Mensual: programa ($100) + micros ($80) + paquete clips redes y YouTube ($50) = $230 USD/mes por episodio producido. Bonificación: 4 artículos en TNO Digest incluidos. Comercialización opcional: si Alter Gold trae al patrocinador, paga 30% a TNO por cliente; si lo trae la radio, 50% y 50%.',
      },
      areYouReady: {
        title: 'Activemos el plan para Alter Gold Servicios de Salud',
        description:
          'Coordinemos el paquete gráfico de arranque y el primer mes: programa, micros a rotativa, clips para redes y promoción en YouTube hacia TNO Network.',
        ctaText: 'Quiero activar este plan',
        whatsappNumber: '04123964576',
        footerText:
          '© TNO Network 2026 | Alter Gold Servicios de Salud × TNO Media Lab | The Content Evolution.',
      },
    },
    isActive: true,
    order: 14.35,
  },
  {
    type: 'client',
    slug: 'alter-gold-publicidad',
    name: 'Alter Gold × TNO Network · Publicidad',
    metaTitle: 'Propuesta Estratégica | Alter Gold Servicios de Salud × TNO Network',
    metaDescription:
      'Alter Gold en TNO Network: En Sintonía con tu Salud, Vivir en Equilibrio, Noticias TNO, rotativa 24/7 y TNO Digest (4 artículos/mes). Plan €285/mes.',
    ogImage: '/images/logos/alter-gold-logo.svg',
    content: {
      ui: {
        enhanced: true,
        showProgramIndex: true,
        brand: {
          primary: '#1B5E4B',
          accent: '#D4AF37',
          ctaSecondary: '#D4AF37',
          ctaSecondaryText: '#1a1a1a',
          heroGradient:
            'linear-gradient(145deg, #0d1a16 0%, #1B5E4B 40%, #2E7D5A 70%, #0f2922 100%)',
        },
      },
      hero: {
        overline: 'Propuesta estratégica · Publicidad en grilla TNO',
        title: 'Alter Gold Servicios de Salud × TNO Network',
        showTnoLogo: true,
        subtitle:
          'Tagyp · T.A.G. · Club AlterGold · En Sintonía con tu Salud · Vivir en Equilibrio · Noticias TNO',
        analysisTitle: 'De qué va la propuesta',
        analysisText:
          'Alter Gold conecta monitoreo Tagyp, asistente T.A.G. con QR y Club AlterGold. La publicidad en TNO entra en salud y bienestar con bloques creíbles en dos programas clave, rotativa diaria y Digest SEO sobre prevención y signos vitales — plan accesible en €285/mes.',
      },
      planIntegralStrip: {
        title: 'Plan mensual',
        description:
          '€285 al mes: dos programas de grilla + Noticias, rotativa 24/7 y cuatro artículos en TNO Digest. El desglose está en la tabla.',
      },
      tnoEcosystem: {
        title: 'El ecosistema TNO',
        description:
          'Somos un ecosistema de comunicación integral que no se detiene. Unimos la inmediatez con la profundidad a través de tres ejes:',
        highlights: [
          { value: '30.000+', label: 'Conexiones mensuales al streaming de TNO Network.', icon: 'streaming' },
          { value: '300.000+', label: 'Alcance promedio en Instagram (últimos 3 meses).', icon: 'reach' },
          { value: '52.700', label: 'Seguidores actuales en Instagram.', icon: 'followers' },
          { value: '430.000+', label: 'Alcance en Instagram durante mayo.', icon: 'visibility' },
        ],
      },
      productsExplained: {
        overline: 'Grilla sugerida',
        title: 'Dónde vive Alter Gold',
        description: 'Salud clínica, hábitos y coyuntura — más rotativa y Digest.',
        columnsPerRow: 2,
        items: [
          {
            id: 'ag-sintonia',
            title: 'En Sintonía con tu Salud',
            icon: 'broadcast',
            badge: 'Con la Dra. Grecia Andreina',
            mediaKitSlug: 'ensintoniacontusalud',
            mediaKitLinkLabel: 'Ir al media kit de En Sintonía con tu Salud',
            summary:
              'Prevención y bienestar integral con rigor médico. Alter Gold presenta Tagyp, alertas 24/7 y T.A.G. con la credibilidad de la Dra. Grecia Andreina.',
            topics: [
              'Bloque mensual “Vigilancia que cuida” (3–5 min): monitoreo y acompañamiento familiar.',
              'Cintillos en episodios de prevención y crónicos.',
              'Guion validado con Alter Gold; clips para redes.',
            ],
          },
          {
            id: 'ag-vivir',
            title: 'Vivir en Equilibrio',
            icon: 'broadcast',
            badge: 'Con Liliana Fernandes',
            mediaKitSlug: 'vivirenequilibrio',
            mediaKitLinkLabel: 'Ir al media kit de Vivir en Equilibrio',
            summary:
              'Wellness práctico: rutinas, energía y prevención en el hogar con Liliana Fernandes.',
            topics: [
              'Bloque “Equilibrio con datos” (3–5 min).',
              'Menciones + cintillo en temas de autocuidado.',
            ],
          },
          {
            id: 'ag-noticias',
            title: 'Noticias TNO',
            icon: 'broadcast',
            badge: 'Con Sol Vargas',
            mediaKitSlug: 'noticias',
            mediaKitLinkLabel: 'Ir al media kit de Noticias TNO',
            summary:
              'Cintillos “Salud en la noticia” cuando la agenda traiga salud pública o sistema sanitario — con Sol Vargas.',
            topics: [
              'Mensaje mensual acordado con Alter Gold.',
              'Tono informativo, sin desentonar con el noticiero.',
            ],
          },
          {
            id: 'ag-rotativa',
            title: 'Micros en la rotativa',
            icon: 'build',
            badge: 'Radio Visual 24/7',
            summary: 'Tagyp · T.A.G. · Club AlterGold — 12 micros/día de referencia.',
            topics: ['Copy renovado cada mes.', 'Producción TNO con arte Alter Gold.'],
          },
          {
            id: 'ag-digest',
            title: 'TNO Digest · 4 artículos al mes',
            icon: 'seo',
            badge: 'Motor SEO salud',
            summary: 'Monitoreo en casa, hipertensión, diabetes y T.A.G. — autoridad en Google.',
            topics: ['Cuatro piezas/mes; validación Alter Gold.', 'Enlaces a contacto comercial.'],
          },
        ],
      },
      products: [],
      valueAdd: {
        layout: 'features',
        overline: 'Por qué encaja',
        title: 'Salud con credibilidad',
        items: [
          {
            title: 'Audiencias de salud',
            description:
              'En Sintonía con tu Salud y Vivir en Equilibrio cubren prevención y hábitos; Noticias aporta coyuntura. Rotativa y Digest sostienen el mensaje.',
            icon: 'chart',
          },
          {
            title: 'Búsqueda orgánica',
            description: 'Cuatro artículos/mes para quien investiga antes de contratar monitoreo.',
            icon: 'network',
          },
          {
            title: 'Tono del programa',
            description: 'Alter Gold como cuidado, no cuña agresiva — coherente con Tagyp y T.A.G.',
            icon: 'handshake',
          },
        ],
      },
      extraSections: [
        {
          id: 'programas',
          overline: 'Grilla recomendada',
          title: 'Dos programas + noticiero',
          description: 'Espacios de salud y bienestar en TNO Network.',
          groups: [
            {
              label: 'En Sintonía con tu Salud',
              mediaKitSlug: 'ensintoniacontusalud',
              mediaKitLinkLabel: 'Ir al media kit de En Sintonía con tu Salud',
              bullets: ['Bloque “Vigilancia que cuida” + cintillos.', 'Dra. Grecia Andreina.'],
            },
            {
              label: 'Vivir en Equilibrio',
              mediaKitSlug: 'vivirenequilibrio',
              mediaKitLinkLabel: 'Ir al media kit de Vivir en Equilibrio',
              bullets: ['Bloque “Equilibrio con datos”.', 'Liliana Fernandes.'],
            },
            {
              label: 'Noticias TNO',
              mediaKitSlug: 'noticias',
              mediaKitLinkLabel: 'Ir al media kit de Noticias TNO',
              bullets: ['Cintillos con Sol Vargas.'],
            },
          ],
        },
        {
          id: 'rotativa-digest',
          overline: 'Siempre encendido',
          title: 'Rotativa y Digest',
          groups: [
            { label: 'Rotativa 24/7', bullets: ['Tagyp · T.A.G. · Club AlterGold.', '12 micros/día.'] },
            { label: 'TNO Digest', bullets: ['4 artículos SEO/mes.', 'Redacción TNO + validación Alter Gold.'] },
          ],
        },
      ],
      investmentSummary: {
        items: [
          { product: 'En Sintonía con tu Salud', detail: 'Bloque “Vigilancia que cuida” + cintillos; Dra. Grecia Andreina.', investment: '€50' },
          { product: 'Vivir en Equilibrio', detail: 'Bloque “Equilibrio con datos” + menciones; Liliana Fernandes.', investment: '€50' },
          { product: 'Noticias TNO', detail: 'Cintillos “Salud en la noticia”; Sol Vargas.', investment: '€50' },
          { product: 'Micros en rotativa 24/7', detail: '12 micros/día; Tagyp, T.A.G., Club AlterGold.', investment: '€90' },
          { product: 'TNO Digest (4 artículos / mes)', detail: 'SEO: monitoreo, crónicos, T.A.G., prevención.', investment: '€45' },
        ],
        totalLabel: 'TOTAL MENSUAL | Publicidad salud multi-programa (EUR)',
        totalValue: '€285',
      },
      areYouReady: {
        title: 'Siguiente paso',
        description:
          'En kickoff definimos rotativa, Digest y calendario en Sintonía con tu Salud y Vivir en Equilibrio según prioridad Alter Gold.',
        ctaText: 'Hablar con comercial',
        whatsappNumber: '584123964576',
        contactEmail: 'mercadeoyventas@tnonetwork.com',
        footerText: '© TNO Network 2026 | Alter Gold Servicios de Salud × TNO Network | The Content Evolution.',
      },
    },
    isActive: true,
    order: 14.4,
  },
  {
    type: 'client',
    slug: 'grupo-express',
    name: 'Grupo Express × TNO Network · Intercambio',
    metaTitle: 'Propuesta por Intercambio | Grupo Express × TNO Network',
    metaDescription:
      'Grupo Express (@grupoexpress) × TNO Network: intercambio $250 USD/mes — pack radio (micros, banner player, insert gráfico, web) + pack catering TNO. Sin efectivo.',
    ogImage: '/images/logos/grupo-express-logo.svg',
    content: {
      ui: {
        enhanced: true,
        showProgramIndex: false,
        brand: {
          primary: '#2C1810',
          accent: '#D4AF37',
          ctaSecondary: '#D4AF37',
          ctaSecondaryText: '#1a1a1a',
          heroGradient:
            'linear-gradient(145deg, #1a1008 0%, #2C1810 45%, #4a3020 75%, #1a1008 100%)',
        },
      },
      hero: {
        overline: 'Propuesta por intercambio · Sin efectivo',
        title: 'Grupo Express × TNO Network',
        showTnoLogo: true,
        subtitle:
          '@grupoexpress · Pack radio 24/7 + pack catering · 7 productos',
        analysisTitle: 'De qué va la alianza',
        analysisText:
          'Paquete bilateral sin efectivo: cuatro productos de antena y entorno digital TNO (micros, banner player, insert en señal, vitrina web) y tres de catering Grupo Express para Media Lab y eventos. Sin programas de grilla ni redes TNO. $250 USD mensuales por parte en intercambio.',
      },
      planIntegralStrip: {
        title: 'Plan mensual',
        description:
          '$250 USD/mes por parte: 4 productos radio/web (TNO) + 3 productos catering (Grupo Express). Desglose en la tabla.',
      },
      tnoEcosystem: {
        title: 'El ecosistema TNO',
        description:
          'Radio Visual 24/7, player web/app y portal TNO concentran la visibilidad de Grupo Express sin depender de la grilla de programas.',
        highlights: [
          { value: '30.000+', label: 'Conexiones mensuales al streaming de TNO Network.', icon: 'streaming' },
          { value: '300.000+', label: 'Alcance promedio en Instagram (últimos 3 meses).', icon: 'reach' },
          { value: '@grupoexpress', label: 'Marca aliada en catering y organización de eventos.', icon: 'followers' },
          { value: '24/7', label: 'Rotativa Radio Visual para micros de Grupo Express.', icon: 'visibility' },
        ],
      },
      productsExplained: {
        overline: 'Inventario del intercambio',
        title: 'Siete productos · radio + catering',
        description:
          'Cuatro líneas en la señal TNO (sin programas de grilla) y tres líneas de servicio Grupo Express para estudios y eventos.',
        columnsPerRow: 2,
        items: [
          {
            id: 'ge-micros',
            title: 'Micros video en rotativa',
            icon: 'build',
            badge: '12/día · 24/7',
            summary: 'Piezas 15–30 s «Tu evento, nuestra pasión» en Radio Visual y streaming continuo.',
            topics: [
              '≈360 impactos/mes en señal multiplataforma.',
              'Arte y copy validados con Grupo Express; rotación mensual.',
            ],
          },
          {
            id: 'ge-player',
            title: 'Banner en player web y app',
            icon: 'seo',
            badge: 'Streaming',
            summary: 'Presencia gráfica junto al reproductor de TNO Network mientras la audiencia consume la señal.',
            topics: [
              'Banner con logo Grupo Express y CTA (WhatsApp / web).',
              'Medida estándar Glacier; enlace rastreable acordado con comercial.',
            ],
          },
          {
            id: 'ge-insert',
            title: 'Insert gráfico en señal',
            icon: 'layers',
            badge: 'Lower third rotativo',
            summary: 'Cintillo o lower third con marca en la señal de video 24/7, sin entrar a programas en vivo.',
            topics: [
              'Aparición programada en rotativa visual (frecuencia acordada en kickoff).',
              'Coherente con manual de marca Grupo Express.',
            ],
          },
          {
            id: 'ge-web',
            title: 'Vitrina «Aliados» en tnonetwork.com',
            icon: 'network',
            badge: 'Web',
            summary: 'Ficha breve de Grupo Express en sección de aliados o patrocinadores del portal TNO.',
            topics: [
              'Logo, texto corto y enlace a @grupoexpress / contacto.',
              'Actualización trimestral de copy o foto destacada.',
            ],
          },
          {
            id: 'ge-lunch',
            title: 'Lunch box producción',
            icon: 'handshake',
            badge: 'Media Lab',
            summary: 'Almuerzo individual o box lunch para grabaciones extendidas en TNO Media Lab.',
            topics: [
              '2 lunch boxes/mes (menú ejecutivo o buffet en box).',
              'Opción vegetariana o corporativa según preferencia TNO.',
            ],
          },
          {
            id: 'ge-buffet',
            title: 'Buffet evento interno TNO',
            icon: 'handshake',
            badge: 'Hasta 25 pax',
            summary: 'Servicio buffet o estación caliente para reunión, lanzamiento o after office de TNO Network.',
            topics: [
              '1 evento/mes alternando con otras líneas si el calendario lo requiere.',
              'Menú y logística acordados por escrito con operaciones TNO.',
            ],
          },
          {
            id: 'ge-finger',
            title: 'Finger food y coctelería ligera',
            icon: 'handshake',
            badge: 'Eventos',
            summary: 'Canapés, pasapalos y estación de bebidas para invitados a eventos en sede o exterior TNO.',
            topics: [
              'Incluido dentro del cupo mensual de catering (valor equivalente).',
              'Ideal para networking post-grabación o activaciones de marca TNO.',
            ],
          },
        ],
      },
      products: [],
      valueAdd: {
        layout: 'features',
        overline: 'Win-win',
        title: 'Por qué encaja',
        items: [
          {
            title: 'Pack radio completo',
            description:
              'Micros, banner en player, insert en señal y vitrina web cubren recordación sin programas de grilla.',
            icon: 'chart',
          },
          {
            title: 'Pack catering operativo',
            description:
              'Lunch boxes, buffet y finger food para estudios y eventos TNO.',
            icon: 'handshake',
          },
          {
            title: 'Sin efectivo',
            description:
              '$250 USD/mes por parte en valor equivalente: fácil de renovar y auditar por línea de producto.',
            icon: 'network',
          },
        ],
      },
      extraSections: [
        {
          id: 'intercambio',
          overline: 'Modalidad',
          title: '100% intercambio — $250 USD/mes por parte',
          description: 'Sin pago en efectivo. El valor se reparte en las siete líneas del inventario.',
          groups: [
            {
              label: 'TNO · Pack radio y web (4 productos)',
              bullets: [
                'Micros video 12/día en rotativa 24/7.',
                'Banner player streaming · insert gráfico en señal.',
                'Ficha en vitrina Aliados tnonetwork.com.',
              ],
            },
            {
              label: 'Grupo Express · Pack catering (3 productos)',
              bullets: [
                '2 lunch boxes/mes (Media Lab).',
                '1 buffet interno o finger food evento TNO.',
                'La Florida · @grupoexpress · coordinación 48 h.',
              ],
            },
          ],
        },
        {
          id: 'radio-detalle',
          overline: 'Solo antena',
          title: 'Sin programas de grilla',
          description: 'Todo el aporte TNO ocurre en rotativa 24/7, player y portal — no hay cupos en shows en vivo.',
          groups: [
            { label: 'Incluido', bullets: ['Radio Visual 24/7', 'Web/app player', 'tnonetwork.com'] },
            {
              label: 'No incluido',
              bullets: ['Menciones en programas', 'Historias @tnonetwork', 'TNO Digest'],
            },
          ],
        },
      ],
      investmentSummary: {
        items: [
          {
            product: 'Micros video rotativa 24/7',
            detail: '12 piezas/día (15–30 s) · Radio Visual y streaming',
            investment: '$140 USD',
          },
          {
            product: 'Banner player web y app',
            detail: 'Presencia gráfica junto al reproductor TNO Network',
            investment: '$40 USD',
          },
          {
            product: 'Insert gráfico en señal',
            detail: 'Lower third / cintillo en rotativa visual 24/7',
            investment: '$35 USD',
          },
          {
            product: 'Vitrina Aliados · tnonetwork.com',
            detail: 'Logo, texto y enlace a contacto Grupo Express',
            investment: '$35 USD',
          },
          {
            product: 'Lunch box producción (×2)',
            detail: 'Almuerzo/box para equipo en Media Lab',
            investment: '$110 USD',
          },
          {
            product: 'Buffet o finger food evento TNO',
            detail: '1 servicio/mes · hasta 25 pax o equivalente pasapalos',
            investment: '$140 USD',
          },
          {
            product: 'Modalidad de pago',
            detail: '100% intercambio (trade) — sin efectivo · $250 USD aporte por parte',
            investment: '$0 cash',
          },
        ],
        totalLabel: 'APORTE MENSUAL POR PARTE | Intercambio bilateral (USD)',
        totalValue: '$250 USD',
      },
      areYouReady: {
        title: 'Activemos el intercambio',
        description:
          'En kickoff priorizamos las siete líneas del pack: calendario catering, arte de micros, banner player e insert en señal.',
        ctaText: 'Coordinar con comercial',
        whatsappNumber: '584123964576',
        contactEmail: 'mercadeoyventas@tnonetwork.com',
        footerText:
          '© TNO Network 2026 | Grupo Express × TNO Network | The Content Evolution.',
      },
    },
    isActive: true,
    order: 14.45,
  },

  {
    type: 'client',
    slug: 'patrocinio-tno-business',
    name: 'Patrocinio TNO Business',
    metaTitle: 'Propuesta Genérica | Patrocinio en TNO Business | TNO Network',
    metaDescription:
      'Plan mensual genérico para marcas en TNO Business: presencia en el programa (segmento y menciones en vivo), banners en tnonetwork.com y artículos SEO en TNO Digest. Inversión referencial $300/mes.',
    ogImage: '/images/logos/logotnobusiness.png',
    content: {
      hero: {
        overline: 'Propuesta genérica · Plan mensual',
        title: 'Tu marca en TNO Business',
        subtitle: 'Programa + Banners Web + Artículos en TNO Digest',
        image: '/images/logos/logotnobusiness.png',
        imageAlt: 'TNO Business',
        analysisTitle: 'Resumen ejecutivo',
        analysisText:
          'Esta es la propuesta base para cualquier marca que quiera posicionarse en el ecosistema de negocios de TNO Network sin depender de un formato publicitario intrusivo. Integra tres pilares: credibilidad en antena dentro de TNO Business (el programa de referencia para directivos y emprendedores), visibilidad permanente en la web con banners estratégicos, y autoridad digital con artículos optimizados en TNO Digest que siguen trabajando en buscadores semanas después de publicarse. Los montos y frecuencias pueden ajustarse según sector, temporada o campaña; la estructura se mantiene.',
      },
      tnoEcosystem: {
        title: 'Por qué TNO Business',
        description:
          'TNO Business es la vitrina corporativa de TNO Network: entrevistas a líderes, análisis de coyuntura y casos de éxito con producción de TNO Media Lab. Quien patrocina aquí no compra un corte publicitario aislado: se asocia con un espacio donde la audiencia busca ideas, tendencias y decisiones de inversión.',
      },
      productsExplained: {
        overline: 'Inventario mensual',
        title: 'Tres pilares de la propuesta',
        description:
          'Cada línea cumple una función distinta: confianza en vivo (programa), recordación digital (banners) y captación orgánica (Digest). Juntas cubren el embudo de una marca B2B o de servicios profesionales.',
        itemsLayout: 'fullRow',
        items: [
          {
            id: 'programa-tno-business',
            icon: 'mic',
            badge: 'Antena · credibilidad',
            title: 'Pauta en TNO Business',
            summary:
              'Presencia integrada en el programa de negocios de TNO: la marca se presenta con el tono editorial del espacio, no como un spot ajeno al contenido.',
            topics: [
              '1 segmento patrocinado mensual (3 a 5 minutos): entrevista, caso de éxito o bloque de marca acordado con producción.',
              'Menciones integradas en vivo durante las emisiones del mes («Presentado por» / referencia orgánica al patrocinador).',
              'Cintillo gráfico en pantalla (lower third) en las transmisiones de TNO Business del periodo contratado.',
              'Difusión en señal multiplataforma TNO Network y archivo On-Demand del ecosistema.',
              'Clips recortados para redes del patrocinador (según calendario de postproducción).',
            ],
          },
          {
            id: 'banners-web',
            icon: 'web',
            badge: 'Web · 24/7',
            title: 'Banners en tnonetwork.com',
            summary:
              'Visibilidad fija y actualizable en el portal oficial de TNO Network, donde conviven streaming, programas y el hub editorial.',
            topics: [
              'Banner horizontal en portada web (zona de alta visibilidad).',
              'Presencia en bloque de aliados comerciales / patrocinadores del portal.',
              'Enlace directo al sitio o landing de la marca (CTA configurable por campaña).',
              'Arte adaptable por temporada: logo, promoción activa o mensaje institucional.',
              'Complementa la antena: quien llega por Digest o redes también encuentra la marca en web.',
            ],
          },
          {
            id: 'tno-digest',
            icon: 'seo',
            badge: 'SEO · permanencia',
            title: 'Artículos en TNO Digest',
            summary:
              'Piezas editoriales optimizadas para buscadores en TNO Digest: la inversión en antena se amplía con activos digitales que generan tráfico orgánico de forma sostenida.',
            topics: [
              '2 artículos mensuales en TNO Digest (nota de prensa ampliada, entrevista escrita, guía o tendencia de sector).',
              'Keywords y estructura SEO acordadas con la marca (negocios, emprendimiento, finanzas, industria, etc.).',
              'Imágenes, enlaces y CTA hacia web, WhatsApp o formulario de contacto.',
              'Publicación en tnonetwork.com con difusión cruzada en redes del ecosistema TNO.',
              'Contenido evergreen posible: las piezas siguen posicionando meses después de salir.',
            ],
          },
        ],
      },
      products: [
        {
          id: 'tb-programa',
          title: 'Pauta en TNO Business',
          product:
            '1 segmento patrocinado mensual + menciones integradas en vivo + cintillo en pantalla en emisiones del mes.',
          investment: '$140',
          impacts: [
            'Bloque dedicado de 3 a 5 minutos con narrativa de marca o vocero.',
            'Asociación editorial con el programa líder en negocios del ecosistema TNO.',
            'Presencia en streaming en vivo y biblioteca On-Demand.',
            'Refuerzo de autoridad frente a tomadores de decisión y emprendedores.',
          ],
        },
        {
          id: 'tb-banners',
          title: 'Banners Web',
          product: 'Banner en portada + espacio en zona de aliados en tnonetwork.com (actualizable por campaña).',
          investment: '$50',
          impacts: [
            'Visibilidad 24/7 independiente del horario de emisión.',
            'Tráfico directo al activo digital de la marca.',
            'Refuerzo de credibilidad por proximidad al ecosistema TNO.',
          ],
        },
        {
          id: 'tb-digest',
          title: 'TNO Digest (artículos SEO)',
          product: '2 artículos mensuales optimizados para buscadores, con CTA y enlaces de marca.',
          investment: '$110',
          impacts: [
            'Posicionamiento orgánico en Google para términos del sector.',
            'Activo digital permanente más allá de la emisión en vivo.',
            'Amplificación del mensaje del segmento en antena.',
          ],
        },
      ],
      metrics: [
        { value: '30.000+', label: 'Conexiones mensuales al streaming TNO Network.', icon: 'live' },
        { value: '90.000+', label: 'Seguidores orgánicos en el ecosistema social TNO.', icon: 'instagram' },
        { value: '25+', label: 'Programas y formatos en la parrilla semanal.', icon: 'audience' },
        { value: '2', label: 'Artículos Digest incluidos en este plan base (ampliables).', icon: 'digest' },
      ],
      valueAdd: {
        layout: 'features',
        overline: 'Ventaja',
        title: 'Por qué este paquete funciona',
        items: [
          {
            title: 'Antena con credibilidad',
            description:
              'TNO Business no es rotativa genérica: es conversación con líderes. La marca entra con legitimidad, no con un corte que interrumpe.',
            icon: 'handshake',
          },
          {
            title: 'Web que acompaña la campaña',
            description:
              'Los banners mantienen la marca visible cuando no hay programa en vivo: puente entre contenido editorial y conversión.',
            icon: 'chart',
          },
          {
            title: 'Digest que vende en silencio',
            description:
              'Cada artículo es un vendedor en Google: educa, posiciona y lleva tráfico cualificado sin depender solo de la pantalla.',
            icon: 'network',
          },
        ],
      },
      investmentSummary: {
        items: [
          {
            product: 'Pauta en TNO Business',
            detail: '1 segmento mensual + menciones en vivo + cintillo en emisiones del mes',
            investment: '$140',
          },
          {
            product: 'Banners Web',
            detail: 'Banner portada + espacio aliados en tnonetwork.com',
            investment: '$50',
          },
          {
            product: 'TNO Digest',
            detail: '2 artículos SEO mensuales con CTA de marca',
            investment: '$110',
          },
        ],
        totalLabel: 'TOTAL MENSUAL | Plan genérico TNO Business (referencial)',
        totalValue: '$300',
        footnote:
          'Precio base para una marca estándar. Rotativa 24/7, spots adicionales, más artículos Digest o segmentos extra en TNO Business se cotizan como add-ons.',
      },
      areYouReady: {
        title: 'Personalicemos tu plan en TNO Business',
        description:
          'Esta propuesta es la plantilla comercial para patrocinar en TNO Business. Indícanos tu sector y objetivo (lanzamiento, captación B2B, posicionamiento de autoridad) y ajustamos frecuencias, artículos Digest y creatividades.',
        ctaText: 'Quiero patrocinar en TNO Business',
        whatsappNumber: '584123964576',
        contactEmail: 'mercadeoyventas@tnonetwork.com',
        footerText: '© TNO Network 2026 | Patrocinio TNO Business · Propuesta genérica | The Content Evolution.',
      },
    },
    isActive: true,
    order: 14.5,
  },
];

const _staticProposalSlugs = new Set(STATIC_PROPOSALS_RAW.map((p) => p.slug));
export const STATIC_PROPOSALS = [
  ...STATIC_PROPOSALS_RAW,
  ...PROGRAM_GRID_MEDIA_KIT_PROPOSALS.filter((p) => !_staticProposalSlugs.has(p.slug)),
];

/** Slug de propuestas que están definidas aquí (estáticas). El resto se pide al API. */
export const getStaticProposalBySlug = (slug) =>
  STATIC_PROPOSALS.find((p) => p.slug === slug) || null;
